import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type SetStateAction,
} from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AssetEditDialog,
  RemoveAllAssetsDialog,
  removeAllListOf,
  removeAllPlural,
  removeAllReturnFocus,
  SecondaryButton,
  convertPlanToTwin,
  prefersReducedMotion,
  useOpenTwin,
  usePresence,
  visuallyHidden,
} from '@repo/ui';
import type { AssetFormPatch, RemoveAllChoice, RemoveAllCounts } from '@repo/ui';
import { Banner, Button, Icon, Scrim, Search, ToastProvider } from '@repo/design-system';
import { glass, motion, radius, semantic, shadow, typeRole, zIndex } from '@repo/design-tokens';
import {
  BAR_BOTTOM,
  GLASS_EDGE,
  GLASS_START_VAR,
  NAV_WIDTH_COLLAPSED,
  NAV_WIDTH_EXPANDED,
  PANEL_TOP,
  glassStartFor,
} from './glassLayout';
import {
  flyToOnPlan,
  useEditorStore,
  useEquipmentCatalogStore,
  useFloorplanStore,
  useTwinContext,
  useUserStore,
  registerReadState,
  resolveRegisterScope,
  useAddMissingRegisterRows,
} from '@repo/front-end-state';
import {
  asArray,
  assetPlanStateFor,
  buildAssetCoverageIndex,
  buildIdentifyModel,
  buildPlanSearchIndex,
  matchPlanEntities,
  placementIndexFor,
  computeActivationDelta,
  effectiveCodeChanges,
  liveEntityBidsOf,
  renamesOf,
  withoutRenumbered,
  type IdentifiedNode,
  computeModelDiff,
  observerTargetNamesByRefid,
  withFloorTargetBids,
  findDuplicateEntityCodes,
  isLive,
  joinRegisterOntoPlan,
  mirrorRegisterThroughJoin,
  clearPlanModelThroughJoin,
  registerRowsForMirror,
  layoutCurrentDesigns,
  layoutHasGeometry,
  diagnosePlanRegisterDrift,
  reservedRegisterIdentities,
  resolveOnboardingMode,
  resolveOnboardingRoute,
  isExistingTwinRoute,
  stampEntityCodes,
  awaitingCustomerRegisterImport,
  preImportWarningText,
  describeFailedSave,
  trackedPlanEntities,
  removePlacementsFromLayout,
  reconcileGeneratedComposites,
  newCompositeRefid,
  encodeRef,
  registerCapacityLookup,
  nextSort,
  placementWhereForRefid,
} from '@repo/front-end-utils';
import { referenceIdsByAssetUid } from './referenceIdsByAssetUid';
import { useLaunchBiCheck } from './useLaunchBiCheck';
import {
  createTwinAsset,
  discardTwinAssets,
  DISCARD_MAX_IDS,
  getModelFiles,
  getTokenBounded,
  isWriteOutcomeUnknown,
  updateTwinAsset,
  isAlreadyIdentifiedError,
  dgqFetchTwinOutcome,
  activatePlan,
  getTwinById,
  getTwinSenseConfigsOutcome,
  getConfigurationStatementOutcome,
  fileConfigurationStatementOutcome,
  persistTwinLifecycleStage,
  keepAbsentImportRows,
} from '@repo/front-end-services';
import type {
  BiImportFailure,
  BulkUploadTwinAssetsResult,
  TwinAssetResponse,
} from '@repo/front-end-services';
import type {
  ActivationDelta,
  DriftEntity,
  ModelDiff,
  OnboardingMode,
} from '@repo/front-end-utils';
import type { TwinEntity } from '@repo/backend-types';
import type {
  AdminTwin,
  ConfigurationStatement,
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  EntityCodeChange,
  FloorPlannerLayout,
  Entity,
  NolaConfig,
  SetBiOutcomeInput,
  TwinLaunchHistoryResponse,
  TwinLaunchSummary,
} from '@repo/frontend-types';
import { TwinLifecycleStageValues } from '@repo/frontend-types';
import { AddSensorInput, MetricTypeRef, MetricDataType } from '@repo/backend-types';

import Config from '../../common/Config';
import { PLAN_NAME } from './constants';
import OnboardingHeader, { routeChipLabel } from './OnboardingHeader';
import { useTwinOrganisationName } from './useTwinOrganisationName';
import { useOrganisationTwins } from './useOrganisationTwins';
import OnboardingNav from './OnboardingNav';
import OnboardingCreateMode from './OnboardingCreateMode';
import type { OnboardingNavItem } from './OnboardingNav';
import OnboardingLaunch from './OnboardingLaunch';
import { putBackFloor } from './putBackFloor';
import { renumberedRowsFor } from './LaunchRenumbered';
import OnboardingTwinDetails from './OnboardingTwinDetails';
import OnboardingPositionOnMap from './OnboardingPositionOnMap';
import type { TwinDetailsState } from './OnboardingTwinDetails';
import {
  TWIN_REQUEST_TIMEOUT_MS,
  carriesAddress,
  formFromTwin,
  isLocated,
  looksLikePlaceholderOrigin,
  recordAfterSave,
  summaryAddress,
  type TwinDetailsPatch,
} from './twinDetailsForm';
import AssetCsvUploader, { ASSET_IMPORT_BODY_ID } from './AssetCsvUploader';
import OnboardingPlanner from './OnboardingPlanner';
import OnboardingCameras from './OnboardingCameras';
import OnboardingObservers from './OnboardingObservers';
import { PlanEntitySearch } from './PlanEntitySearch';
import type {
  LaunchRecordRead,
  PlanChecks,
  PlanSearchEntry,
  RegisterNameRow,
} from '@repo/front-end-utils';
import { nextPlanScore, planScoreShown, syncPlanScore, type PlanScore } from './planScore';
import { usePlanSearchSources } from './usePlanSearchSources';
import OnboardingCompositeMeasures from './OnboardingCompositeMeasures';
import { launchNote } from './launchNote';
import { stepCaption } from './stepCaption';
import { useOnboardingSessionStore } from './onboardingSession';
import {
  buildConfigurationStatement,
  renumberingsTheConfigurationHolds,
  statementObserversAsRunning,
} from './buildConfigurationStatement';
import { toImportAttempt } from './importAttempt';
import type { PageImportAttempt } from './importAttempt';
import { sendFrameSplitConfig } from './sendFrameSplitConfig';
import type { SendFrameSplitConfigResult } from './sendFrameSplitConfig';
import {
  buildDeploymentSummary,
  type BiOutcomeSave,
  type BiSummary,
  type BuildDeploymentSummaryParams,
} from './LaunchSuccessSummary';
import {
  skipsManufacturer,
  toBiOutcome,
  toBiSummary,
  toFailedBiOutcome,
  toNotUpdatedBiOutcome,
  useLaunchBiPush,
} from './useLaunchBiPush';
import { biFailureReason } from './biPushCopy';
import { useOnboardingModal, type ActivationPhase } from './useOnboardingModal';
import {
  getOnboardingModal,
  setModalComparison,
  updateSummary,
  type Outcome,
} from './onboardingModalStore';
import { buildLaunchChangesMessage } from './reconciliation';
import {
  computeCompositeLoss,
  renumberStatement,
  NO_COMPOSITE_LOSS,
  type CompositeLoss,
  type CompositeLossEntry,
} from './compositeLoss';
import {
  POSTGREST_MAX_ROWS,
  baseEntityBid,
  computeObserverLoss,
  selectFileableObservers,
} from './observerConfigDiff';
import type { RunningObserver } from './observerConfigDiff';
import {
  equipmentStepComplete,
  registerHasRows,
  registerKnownEmpty,
  launchAssetLists,
} from './registerCompletion';
import {
  entityItem,
  goLiveCompositeRefusal,
  launchCompositeGroups,
  refusedTargetNames,
  launchPlacementState,
  observersReadState,
  biGapGroups,
  biInputsOf,
} from './launchSummaryDetail';
import { activationOrgErrorMessage, resolveActivationOrg } from './resolveActivationOrg';
import { useLiveTwinConfiguration } from './useLiveTwinConfiguration';
import { feedsComplete } from './feedsCompletion';
import {
  coverageBlock,
  liveConfigurationPlaced,
  liveReadState,
  observerCountsFromLayout,
  planMeasures,
  summarisePlanCoverage,
} from './entityCoverage';
import { useLeaveGuard } from './useLeaveGuard';
import { useLaunchLock } from './useLaunchLock';
import { openingHoursReady, useLaunchHours } from './useLaunchHours';
import { useOpeningHoursEditor } from './openingHours/useOpeningHoursEditor';
import { useTwinDetailsSave } from './useTwinDetailsSave';
import { useAddressFlight } from './useAddressFlight';
import OnboardingOpeningHours from './openingHours/OnboardingOpeningHours';
import { startPanelShows } from './openingHours/hoursModel';
import { useLaunchRecord, summaryLaunchRecord } from './useLaunchRecord';
import DraftStatusTag from './DraftStatusTag';
import EquipmentStage, { type EquipmentPanel } from './EquipmentStage';
import { EquipmentImportUndoButton, EquipmentImportUndoHost } from './EquipmentImportUndoHost';
import { nextUndoHolder } from './useEquipmentImportUndo';
import {
  equipmentAnnouncement,
  equipmentChipCaption,
  equipmentChipGroups,
  equipmentChipCount,
  equipmentChipLabel,
  equipmentCountText,
  equipmentLanding,
  equipmentRowFilter,
  FROM_IMPORT_TITLE,
  fromImportAnnouncement,
  fromImportCountText,
  isChipOffered,
  type EquipmentChip,
  type EquipmentChipGroups,
} from './equipmentFilter';
import EquipmentGroupsView from './EquipmentGroupsView';
import EquipmentRowsView, { type EquipmentSort } from './EquipmentRowsView';
import EquipmentSelectionBar from './EquipmentSelectionBar';
import EquipmentRemoveSelected from './EquipmentRemoveSelected';
import EquipmentBulkActions from './EquipmentBulkActions';
import {
  equipmentShownOrder,
  fromImportOf,
  fromImportOrder,
  selectionInShownOrder,
  type FromImport,
} from './equipmentShownOrder';
import type { EquipmentWriteDeps } from './useEquipmentRowWrites';
import { EquipmentAssetCardHost } from './EquipmentAssetCardHost';
import type { AssetCardFolds, EquipmentAssetCardHandle } from './EquipmentAssetCard';
import type { AssetRemoval } from './EquipmentAssetRemove';
import { useEquipmentSelection } from './useEquipmentSelection';
import EquipmentIdentify from './EquipmentIdentify';
import EquipmentLandedSummary, { lateRefusalLine } from './EquipmentLandedSummary';
import { useEquipmentImportSummary } from './useEquipmentImportSummary';
import { useEquipmentBrandMix } from './useEquipmentBrandMix';
import { useRegisterSaveState, type RegisterWriteOutcome } from './useRegisterSaveState';
import { activationOriginErrorMessage, resolveActivationOrigin } from './resolveActivationOrigin';
import { liveEntityRefids } from './sessionRecords';
import { referencesUnsaved } from './observerStamps';
import { decisionsUnsaved } from './observerWorkspace/suggestionDecisions';
import { hasUnlaunchedEdits } from './draftChoice';
import { usePlanLoad, usePlanSave, useRekeyedPlanSave } from './planSync';
import { DraftReloadModal, DraftRestoringModal } from './DraftChoiceModal';
import DraftChoiceNotices from './DraftChoiceNotices';
import VersionsModal, { type ChangesState, type VersionsFocus } from './VersionsModal';
import DiscardDraftModal from './DiscardDraftModal';
import {
  dialogsWantChanges,
  draftChangesWanted,
  draftHistoryState,
  useDraftChanges,
} from './useDraftChanges';
import { useLaunchPlans } from './useLaunchPlans';
import { useLoadComparison } from './useLoadComparison';
import {
  canDiscardDraft,
  draftSubline,
  draftOrigin,
  draftTitle,
  launchVersion,
  versionText,
} from './versionsText';
import LoadLaunchModal from './LoadLaunchModal';
import { useLaunchHistory } from './useLaunchHistory';
import {
  STEPS,
  STEP_ICONS,
  STEP_SCENES,
  handsOver,
  stepJoinFades,
  type StepId,
} from './onboardingSteps';
import StepFade from './StepFade';
import {
  PlanRendererProviders,
  PlanStageBoundary,
  preloadPlanSceneProviders,
} from '../../components/3D/planView/PlanRendererProviders';
import { NO_SCENE, createStageTunnelStore } from '../../components/3D/planView/PlanStageTunnel';
import type { PlanDrawState } from '../../components/3D/planView/planDrawState';
import { hasRealPosition, washedPlanAvailable } from '../../components/3D/planView/washedPlan';
import OnboardingWashedPlan from './OnboardingWashedPlan';
import { handBack } from '../../components/3D/planView/planPoseHandback';
import { requestArrivalFromStage } from '../../components/3D/planView/liveArrival';
import { cancelPageClear, schedulePageClear } from './onboardingPageClear';
import {
  getHandover,
  heldEditFor,
  holdScene,
  releaseHold,
  subscribeHandover,
} from '../../components/3D/planView/planHandover';

/** The areas a delta removes: the only removals Launch asks about (`launch-single-confirmation`). */
const areasRemovedBy = (d: ActivationDelta) => d.removed.filter(e => e.kind === 'area');

/**
 * Same set of AREAS about to be destroyed, ignoring order: what the Launch dialog showed. A machine,
 * wall or floor removed meanwhile was never asked about (R2), so it does not abort the launch (R6);
 * a machine that had a camera is caught by the observer comparison instead.
 */
const sameRemovals = (a: ActivationDelta, b: ActivationDelta): boolean => {
  const key = (d: ActivationDelta) =>
    areasRemovedBy(d)
      .map(e => e.bID)
      .sort()
      .join('\0');
  return key(a) === key(b);
};

export type { StepId } from './onboardingSteps';

type PreLaunchStepId = (typeof STEPS)[number]['id'];

/** A pre-Launch step's title. `launchNote.ts` keys on it (type-only), so a rename breaks the build. */
export type SectionTitle = (typeof STEPS)[number]['title'];

/** A pre-Launch step's title, or `null` for Launch. */
const preLaunchTitle = (id: StepId): SectionTitle | null =>
  STEPS.find(step => step.id === id)?.title ?? null;

/** The step after `id` in `STEPS`; Launch after the last. */
const nextStep = (id: StepId): StepId => {
  const at = STEPS.findIndex(step => step.id === id);
  return at >= 0 && at + 1 < STEPS.length ? STEPS[at + 1].id : 'launch';
};

/** The Floor plan search's index on every step that does not show it — one stable empty list. */
const NO_PLAN_SEARCH_ENTRIES: PlanSearchEntry[] = [];

/**
 * Steps BY NAME. The page compares `section` against these, never against a number, so adding a
 * step to `STEPS` moves nothing here.
 */
const SECTION = {
  twinDetails: 'twinDetails',
  openingHours: 'openingHours',
  assets: 'assets',
  plan: 'plan',
  cameras: 'cameras',
  observers: 'observers',
  /**
   * Complex measures (`complex-measures-in-configuration-composites-stage`, ledger rows 13–15).
   *
   * It used to be the one stage AFTER Launch, because the wizard had no configuration to edit
   * before Go live. It edits the PLAN now, which exists as soon as the operator has drawn one — so
   * it sits in `STEPS` like every other pre-Launch stage, and its nav item is gated on the plan
   * having entities rather than on the twin being live.
   */
  compositeMeasures: 'compositeMeasures',
  positionOnMap: 'positionOnMap',
  launch: 'launch',
} as const satisfies Record<string, StepId>;

const LAUNCH_SECTION = SECTION.launch;

/** Launch's composite groups while Launch is not showing — nothing computed, nothing to say. */
const NO_LAUNCH_COMPOSITES: ReturnType<typeof launchCompositeGroups> = {
  refused: [],
  refusalCheck: 'ok',
  empty: [],
  unfinished: [],
  nothingMeasured: [],
  review: [],
};

/**
 * The steps whose plan edits save themselves as a draft (`observers-page-draft`, FR2). Each one
 * shows the *Draft saved* tag where Save was. Autosave is rolling out to every step, one refactor at
 * a time: a step joins by being added here once its edits reach the plan store as they are made.
 * Composite measures joined second (`composites-page-draft`): its edits always went straight into
 * the plan store, so joining took only asking the saver for a save after each one. Camera Feeds
 * joined third (`camera-feeds-page-draft`): its stage already reported every write it made, and the
 * plan's camera gestures (move, turn, the camera tool, delete) report through `TwinPlanner`'s
 * `onCameraEdited`. Floor Plan joined fourth and last (`floor-plan-page-draft`): the planner writes
 * to the plan from some twenty-five places, so rather than a signal from each, the page watches the
 * plan store while the step is on screen and asks after any change that leaves the plan unsaved
 * (the Floor Plan watcher, below `handleCameraEdit`). With it, no step has a Save button.
 */
const DRAFT_SAVE_SECTIONS: ReadonlySet<StepId> = new Set([
  SECTION.plan,
  SECTION.cameras,
  SECTION.observers,
  SECTION.compositeMeasures,
]);

/**
 * The steps whose step list can shrink to its numbers, by the toggle at the list's foot (Observers
 * page v2, `observer-page-v2-ux`), and which may ask for that themselves (`observers-page-stage`, FR4).
 * These steps have no title-bar toggle: there it would only ever go full screen. The Observers step
 * asks whenever cameras are on its stage, so the picture gets the width. Rolling out page by page,
 * like the draft: a step joins by being added here — nothing in the list or the toggle names a step.
 */
const NAV_COLLAPSE_SECTIONS: ReadonlySet<StepId> = new Set([
  // Opening hours (`twin-details-page-hours-week`): frames 05–07 draw the list shrunk, and the week
  // editor's hour track wants the width.
  SECTION.openingHours,
  // Equipment/Assets joined with its frame (`equipment-page-stage`, R14): frame 04 draws the list
  // shrunk, and its left panel and table want the width.
  SECTION.assets,
  SECTION.plan,
  SECTION.cameras,
  SECTION.observers,
  SECTION.compositeMeasures,
  // Position twin (`onboarding-persistent-scene-position-twin`, R2.3): the map wants the width.
  SECTION.positionOnMap,
]);

/**
 * The steps that ARRIVE with the step list collapsed — a subset of `NAV_COLLAPSE_SECTIONS`.
 * Composite measures' list is drawn for the 64 px step list (board v2: the list starts at 76 px), so
 * it opens that way; the toggle still widens it (`composites-page-list`, spec R1). Camera Feeds
 * likewise: every frame of its design draws the list shrunk (`camera-feeds-page-stage`, ruled by
 * maria@twinlabs.ai 2026-10-10). Floor Plan too: frame 01 of Rob's 10 Oct design draws the list
 * shrunk (`floor-plan-page-draft`). The Observers step opens with the list whole and asks for it to
 * collapse itself when cameras go on its stage.
 */
const NAV_COLLAPSED_ON_ARRIVAL: ReadonlySet<StepId> = new Set([
  // Opening hours: frames 05, 06 and 07 all draw the list shrunk (`twin-details-page-hours-week`).
  SECTION.openingHours,
  // Equipment/Assets: every frame of Rob's 12 Oct design draws the list shrunk (`equipment-page-stage`).
  SECTION.assets,
  SECTION.plan,
  SECTION.cameras,
  SECTION.compositeMeasures,
  // Position twin: frame 11 of the Twin details board draws the list shrunk (R2.3).
  SECTION.positionOnMap,
]);

/** The step list's collapsed state on `section`: what was set there, else that step's arrival value. */
const navCollapsedOn = (state: { section: StepId; collapsed: boolean }, section: StepId) =>
  state.section === section ? state.collapsed : NAV_COLLAPSED_ON_ARRIVAL.has(section);

/**
 * The steps whose map runs the whole page under the glass (`onboarding-persistent-scene-camera-feeds-glass`,
 * R1): the step's content fills the window, and the nav bar, the step list and the step's own panels
 * float over it at the canvas's insets (`glassLayout.ts`). Floor Plan joins once its planner shows
 * (`onboarding-persistent-scene-floor-plan-glass`, R1); Observers joined in
 * `onboarding-persistent-scene-observers-glass`, and Position twin in
 * `onboarding-persistent-scene-position-twin`. Every other step keeps the list and content as one
 * flex row below the bar.
 */
const GLASS_SECTIONS: ReadonlySet<StepId> = new Set([
  SECTION.cameras,
  SECTION.plan,
  SECTION.observers,
  // Position twin (`onboarding-persistent-scene-position-twin`, R2.2): the stage's map fills the
  // window on the page backdrop, and the step's panel and map controls float over it.
  SECTION.positionOnMap,
]);

// One PeopleCountLive sensor per entity is created at activation (mirrors the
// admin plan-activation flow / the FML import's AddFloorplannerFile).
const PEOPLE_COUNT_METRIC: MetricTypeRef = {
  name: 'count',
  description: 'Quantities of things',
  metrics: [
    {
      name: 'countEntity',
      description: 'Number of objects counted in the entity',
      unit: 'count',
      dataType: MetricDataType.Int,
    },
    {
      name: 'capacity',
      description: 'Entity capacity',
      unit: 'count',
      dataType: MetricDataType.Int,
    },
    {
      name: 'usage',
      description: 'Count/Capacity expressed as percentage',
      unit: '%',
      dataType: MetricDataType.Float,
    },
  ],
};

interface Props {
  className?: string;
}

export type { PlanSaveOutcome } from './planSync';

/*
 * `register-remove-all`'s helpers, at module level because they read nothing from render.
 */

/** A Remove all failure, titled by what is actually known to have happened to the rows. */
type RemoveAllFailure = { title: string; message: string };
const NOTHING_REMOVED = 'Nothing was removed.';

/*
 * What the Rows view says when the register read fails. The advice to reload is gone; the warning
 * against re-uploading is kept, because it prevents duplicate rows that have no Asset Identifier
 * (`register-write-ux-hardening`, epic ledger row 67).
 */
const REGISTER_READ_ERROR =
  'The register could not be read. Do not re-upload — that would duplicate rows that have no ' +
  'Asset Identifier.';

/** The Rows view's sort, as the count line names it (`equipment-page-table`, R5). */
/** The Rows view's body rows: the table's head row carries a row index too. */
const ASSET_BODY_ROWS = '.rows-table [role="row"][aria-rowindex]:not([aria-rowindex="1"])';
const EQUIPMENT_SORT_CAPTIONS: Record<string, string> = {
  name: 'name',
  identifier: 'asset identifier',
  product: 'product',
  plan: 'plan',
  bid: 'BID',
};
const SOME_REMOVED = 'Not everything was removed.';
const REMOVAL_UNCONFIRMED = 'We could not confirm the removal.';

type DiscardOutcome = { deleted: number[]; alreadyGone: number[]; failure: unknown };

/**
 * Delete `ids` in batches the discard route accepts, one after another, stopping at the first
 * failure. Each batch is one all-or-nothing statement server-side. Never retried here: whether to
 * press again is the operator's call, after the re-read — and a re-send is safe, because ids
 * already gone come back in `alreadyGone`.
 */
const discardInBatches = async (
  token: string,
  forTwinId: number,
  ids: readonly number[],
): Promise<DiscardOutcome> => {
  const deleted: number[] = [];
  const alreadyGone: number[] = [];
  for (let start = 0; start < ids.length; start += DISCARD_MAX_IDS) {
    try {
      const result = await discardTwinAssets(Config.middlewareApi, token, {
        twinId: forTwinId,
        assetIds: ids.slice(start, start + DISCARD_MAX_IDS),
      });
      deleted.push(...result.deleted);
      alreadyGone.push(...result.alreadyGone);
    } catch (failure) {
      return { deleted, alreadyGone, failure };
    }
  }
  return { deleted, alreadyGone, failure: undefined };
};

/**
 * The dialog's account of a discard that stopped: what went, why the rest did not, and what is safe
 * next.
 *
 * The server's refusal ends "Nothing was deleted." — true of the ONE batch it refused, so when
 * earlier batches went, that is said first and the server's sentence is framed as the next batch's.
 * A 409 after the plan was saved means another SAVED copy of the plan still places a row (another
 * tab, say): pressing *Remove the unplaced assets* would meet the same refusal, so that advice is
 * given only where it can work.
 */
const discardFailure = (outcome: DiscardOutcome, planSaved: boolean): RemoveAllFailure => {
  const done = outcome.deleted.length;
  const unknown = isWriteOutcomeUnknown(outcome.failure);
  const placementRefusal = (outcome.failure as { status?: unknown } | null)?.status === 409;
  const server =
    outcome.failure instanceof Error && outcome.failure.message
      ? outcome.failure.message
      : 'The rows could not be removed.';
  const lead =
    done > 0
      ? `${removeAllPlural(done, 'asset was', 'assets were')} removed before a batch stopped. `
      : '';
  const cause = unknown
    ? 'We could not confirm whether the last batch was removed. A fresh read of the register has ' +
      'started; pressing again is safe, because rows already removed are skipped.'
    : done > 0
      ? `The next batch was refused: ${server}`
      : server;
  const next = !planSaved
    ? ''
    : placementRefusal
      ? ' Another saved copy of the plan still places them — reload the page to see it before ' +
        'trying again.'
      : ' The plan is already saved without that equipment, so the rows still listed are ' +
        'unplaced: Remove the unplaced assets finishes the job.';
  return {
    title: unknown ? REMOVAL_UNCONFIRMED : done > 0 ? SOME_REMOVED : NOTHING_REMOVED,
    message: `${lead}${cause}${next}`,
  };
};

/**
 * Removal is by refid, which is safe only while every refid it names is unique and real (the D7
 * defect once left a staging plan repeating one refid 44 times; an item with no string refid counts
 * as placed yet cannot be removed by refid at all). *Remove all* and the asset card both refuse
 * rather than guess, on this one reading.
 */
const unsafeRefids = (
  refids: ReadonlySet<string>,
  layout: FloorPlannerLayout | null | undefined,
): { kind: 'safe' } | { kind: 'no-id' } | { kind: 'shared'; shared: string[] } => {
  if (refids.has('')) return { kind: 'no-id' };
  const seen = new Map<string, number>();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const entry of [...asArray(design.items), ...asArray(design.surfaces)]) {
      if (isLive(entry)) seen.set(entry.refid, (seen.get(entry.refid) ?? 0) + 1);
    }
  }
  const shared = Array.from(refids).filter(refid => (seen.get(refid) ?? 0) > 1);
  return shared.length > 0 ? { kind: 'shared', shared } : { kind: 'safe' };
};

/** Said only when a re-send found rows an earlier attempt had already deleted. */
const alreadyGoneSentence = (alreadyGone: number): string =>
  alreadyGone > 0 ? ` ${alreadyGone} had already been removed.` : '';

/**
 * The lead of Go Live's report of a failed save, at both of its stops. The reason or the retry
 * advice follows it — never both (`describeFailedSave`).
 */
const GO_LIVE_SAVE_LEAD =
  'Could not save the plan, so activation was stopped before anything was changed.';

/**
 * How many of a refused filing's faults are shown before the rest are counted.
 *
 * The server names EVERY fault rather than the first, which is right — a caller told about one at a
 * time fixes them one at a time. But a gym with fifty faults would produce a dialog nobody reads, so
 * the tail is counted rather than rendered. It is counted rather than dropped: "and 44 more" is a
 * different sentence from silence, and the console carries all of them.
 */
const MAX_FILING_FAULTS_SHOWN = 6;

/**
 * Everything the post-launch success summary needs except BI's part — held while Launch's BI step
 * waits on a Retry or a Continue (`twin-launch-history-bi-push` R5), so the summary can still be
 * built once the person has chosen.
 */
interface LaunchSummaryInputs extends Omit<BuildDeploymentSummaryParams, 'bi' | 'biOutcomeSave'> {
  modelDiff: ModelDiff | null;
  draftMarkerUnsaved: boolean;
  /** Equipment the launch activated — what BI's manufacturer update could at most have linked. */
  assetsLive: number;
  /**
   * Where the launch's BI outcome is recorded (`twin-launch-history-bi-outcome`): the twin, and the
   * launch id the record was written under. `launchUuid` is absent when there is nothing to attach
   * an outcome to — no id, or a record that DEFINITELY failed (an unknown one may have landed, so
   * it is tried: R3.4).
   */
  twinId: number;
  launchUuid?: string;
}

/** Twin details before the page or the step has read the twin (`twin-details-page-landing`, R3). */
const TWIN_DETAILS_PENDING: TwinDetailsState = {
  located: false,
  placeholder: false,
  dirty: false,
  address: null,
  timezone: null,
  read: 'pending',
};

/** The Launch step's name for a mode: a live twin is updated, any other launched. */
const launchLabelFor = (mode: OnboardingMode): string =>
  mode === 'UPDATE' ? 'Update Twin' : 'Launch Twin';

/** Is a plan map's picture held now (glass-align R2.1)? Read by the page as a boolean. */
const isPictureHeld = () => getHandover() !== null;

const OnboardingPage: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently, user } = useAuth0();
  /*
   * Who saves the plan. Sent as `modified_by` on every update: the
   * middleware stamps `twin_plan.modified_at` only when the caller names someone, and the *Draft
   * saved* tag reads that time on reopen (`observers-page-draft`, FR4). In a ref so `runPersistPlan`
   * stays stable across renders.
   */
  const modifiedByRef = useRef<string | undefined>(undefined);
  modifiedByRef.current = user?.email;
  // The before/after model comparison, the summary's second content (UPDATE only), is held in
  // `onboardingModalStore` (`setModalComparison`), so it outlives the page with a carried summary
  // (`onboarding-persistent-scene-launch-into-live`, R1.5). Null when closed. The "after" is read
  // live on open, so it carries its own loading/error status.
  // Which opening of the comparison is current: a read from an earlier one is dropped (R8 of
  // `onboarding-persistent-scene-launch-modals`).
  const comparisonGenRef = useRef(0);
  // Go Live activation state (converts the plan → dGraph via activatePlan). Declared here, ahead of
  // the Modal that shows it. `activating` locks the wizard; `activationPhase` says whether the live
  // twin has started changing yet (`onboarding-persistent-scene-spinner`, R4).
  const [activating, setActivating] = useState(false);
  const [activationPhase, setActivationPhase] = useState<ActivationPhase | null>(null);
  // What the page asks of its one design-system Modal: Launch's check, its activating content, the
  // summary and comparison, and the leave prompt. The Modal itself is drawn by
  // `OnboardingModalLayer` in the protected layout, from `onboardingModalStore` (launch-into-live,
  // R2), so a carried summary outlives the page.
  const { confirmChanges, showSummary, confirmLeave, endActivating } = useOnboardingModal({
    activating: activating ? activationPhase : null,
  });
  /** Launch's Go live button: focus goes back to it when its confirmation is cancelled. */
  const goLiveButtonRef = useRef<HTMLButtonElement>(null);
  // The org the OPERATOR currently has selected. This is a UI-context value only — it is
  // NOT the activation namespace. `handleGoLive` resolves that from the twin's own
  // `organisation_id` (see `resolveActivationOrg`) and refuses on a mismatch; using this
  // value for a dGraph write is what wrote one tenant's plan into another's namespace.
  const { authMetadata } = useUserStore();
  const selectedOrgBid = authMetadata?.orgBID;
  /*
   * The step on screen, and whether the wizard has decided it yet (`twin-details-page-landing`, R2).
   *
   * `chosenSection` is null until the landing is decided: a live twin opens on Floor Plan, anything
   * else on Twin details, once the page's twin read settles (the landing effect, below the read). Any
   * step the person picks first is a choice like any other — it sets `chosenSection`, and the landing
   * then leaves it alone, so a person's choice is never overridden.
   *
   * `section` keeps its `StepId` type for every reader: while pending it reads Twin details, today's
   * default, but no step is drawn — the content area says *Opening the twin…* instead.
   */
  const [chosenSection, setChosenSection] = useState<StepId | null>(null);
  /** The step the person is on now — read by the landing, which must not depend on it. */
  const chosenSectionRef = useRef(chosenSection);
  chosenSectionRef.current = chosenSection;
  /**
   * Whether the washed plan is offered now (`spec-onboarding-persistent-scene-wash`, R6.1): a move
   * between a washed step and an editor step is handed over only while there is a washed map to
   * join. Set in render, below the plan read.
   */
  const washedPlanRef = useRef(false);
  /** The page's `?twinBid=`, for `setSection` (declared below the URL read). */
  const twinBidRef = useRef<string | undefined>(undefined);
  /*
   * Every change of section goes through here (`spec-onboarding-persistent-scene-editor-pose`, R5.2):
   * the plan map being left hands its view back first, if the person moved it, for the next plan map
   * to open at. Only a real change does: the step already open chosen again, the landing re-read, a
   * draft swap, a retry or a remount hands nothing back, so no request waits for the same step's
   * next mount.
   *
   * `spec-onboarding-persistent-scene-handover` (R2, R4.1): a change between two plan-map steps is
   * handed over. The view is carried whether or not it moved, and the leaving map is held as a
   * picture before the change commits, so no blank frame is painted; the next map's arrival
   * dissolves it. Any other change releases a held picture at once and is today's instant swap.
   */
  const setSection = useCallback((next: SetStateAction<StepId | null>) => {
    const now = chosenSectionRef.current;
    const value = typeof next === 'function' ? next(now) : next;
    if (value !== now) {
      const carry =
        now !== null &&
        value !== null &&
        handsOver(now, value, { washedPlan: washedPlanRef.current });
      handBack({ carry });
      if (carry) holdScene();
      // The landing (a choice from none) keeps a hold the live viewer's Edit made for this twin: its
      // picture waits for Floor Plan's map (`spec-onboarding-persistent-scene-edit-flight`, R6.6).
      else if (!(now === null && heldEditFor(twinBidRef.current))) releaseHold({ immediate: true });
    }
    // Passed on as given: the landing's updater must still apply after a click queued before it.
    setChosenSection(next);
  }, []);
  const section: StepId = chosenSection ?? SECTION.twinDetails;
  /*
   * A plan map's picture is held on the backdrop (`spec-onboarding-persistent-scene-handover-glass-align`,
   * R2.1): while it is, nothing solid sits between it and the arriving step, so the content box and
   * the plan panes are see-through. On every step, glass or not: Floor Plan's loading line keeps its
   * own layout and still shows the picture.
   */
  // A boolean, so the page re-renders when a hold starts or ends, not on each change within one.
  const pictureHeld = useSyncExternalStore(subscribeHandover, isPictureHeld, isPictureHeld);
  const landingPending = chosenSection === null;
  /** Floor Plan's planner is on screen (not its loading line or start choice): `OnboardingPlanner` says. */
  const [planShown, setPlanShown] = useState(false);
  /*
   * The last change of step, and how many there have been (`onboarding-persistent-scene-panel-fades`,
   * R2.2): whether it fades (`stepJoinFades`), and the count each step's fade ends on when the person
   * moves again mid-fade. Derived during render, so both are right in the very commit the step
   * changes. The landing is a change from `null`, so it never fades.
   */
  const [lastJoin, setLastJoin] = useState({
    from: null as StepId | null,
    to: chosenSection,
    n: 0,
  });
  const stepJoin =
    lastJoin.to === chosenSection
      ? lastJoin
      : { from: lastJoin.to, to: chosenSection, n: lastJoin.n + 1 };
  if (stepJoin !== lastJoin) setLastJoin(stepJoin);
  const joinFades = stepJoinFades(stepJoin.from, stepJoin.to);
  /*
   * The bar's step title and the step's controls fade in with the step on a fading join (R4;
   * `onboarding-persistent-scene-step-titles` moved them into the nav bar): animated in place, so
   * the heading is the same element before and after and stays the focus target. They never fade
   * out: their words change with the step.
   */
  const wizardRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!joinFades || prefersReducedMotion()) return;
    const parts = wizardRef.current?.querySelectorAll<HTMLElement>(
      ':scope > header [data-onboarding-step-header], :scope > header .tl-nav-slots.is-right',
    );
    const fades = Array.from(parts ?? [])
      .filter(el => typeof el.animate === 'function')
      .map(el =>
        el.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: parseFloat(motion.durFast) * 1000,
          easing: motion.easeUi,
        }),
      );
    return () => fades.forEach(fade => fade.cancel());
  }, [joinFades, stepJoin.n]);
  /*
   * The step list shrunk to its numbers — honoured only on a step in `NAV_COLLAPSE_SECTIONS`, and
   * forgotten on every change of step: each step opens with the list whole (FR5: "unchanged on
   * arrival"), except a step in `NAV_COLLAPSED_ON_ARRIVAL`, which opens with it collapsed.
   */
  /*
   * Held WITH the step it was set on, and reset IN THE SAME RENDER as a change of step ("adjusting
   * state while rendering"): arriving on a step in `NAV_COLLAPSED_ON_ARRIVAL` draws the list
   * collapsed on the first frame, every arrival starts from the arrival value — a choice made on a
   * step never survives leaving it (review: an Observers collapse used to come back on return) —
   * and the announcement below never sees a width change that was an arrival.
   */
  const [navState, setNavState] = useState<{ section: StepId; collapsed: boolean }>({
    section,
    collapsed: NAV_COLLAPSED_ON_ARRIVAL.has(section),
  });
  if (navState.section !== section) {
    setNavState({ section, collapsed: NAV_COLLAPSED_ON_ARRIVAL.has(section) });
  }
  const navCollapsed = navCollapsedOn(navState, section);
  const setNavCollapsed = useCallback(
    (next: boolean | ((collapsed: boolean) => boolean)) =>
      setNavState(prev => {
        const current = navCollapsedOn(prev, section);
        return { section, collapsed: typeof next === 'function' ? next(current) : next };
      }),
    [section],
  );
  /** This step's list collapses by its own toggle, at the list's foot. */
  const navCollapses = NAV_COLLAPSE_SECTIONS.has(section);
  /** The list is drawn collapsed — honoured only on a step in the set. */
  const navIsCollapsed = navCollapses && navCollapsed;
  /** Said when the list changes width, and only then — never on arriving at a step. */
  const [navAnnouncement, setNavAnnouncement] = useState('');
  const navWasCollapsedRef = useRef(navIsCollapsed);
  const navSectionRef = useRef(section);
  useEffect(() => {
    // A change of step is an ARRIVAL, never announced — including arriving collapsed on a step in
    // `NAV_COLLAPSED_ON_ARRIVAL`. Only the toggle (or a step asking for it) is said.
    if (navSectionRef.current !== section) {
      navSectionRef.current = section;
      navWasCollapsedRef.current = navIsCollapsed;
      return;
    }
    if (navWasCollapsedRef.current === navIsCollapsed) return;
    navWasCollapsedRef.current = navIsCollapsed;
    setNavAnnouncement(navIsCollapsed ? 'Step list collapsed' : 'Step list expanded');
  }, [navIsCollapsed, section]);
  // Group assets uploaded during this onboarding run under a session, as well
  // as linking them to the twin being set up.
  const [sessionId] = useState(() => `onboarding-${crypto.randomUUID()}`);

  // The wizard runs against an existing twin, named by ?twinId= (numeric, for assets/feeds) and
  // ?twinBid= (for the twin-plans API) — reached by a twin's "Setup" spanner, or by Create Twin in
  // create mode (`?create=1`, below), which hands over to this same URL once the twin exists. Twins
  // made before that were created in the retired Create New Twin dialog (`twin-details-page-create`).
  const twinIdParam = Number(searchParams.get('twinId'));
  // `Number.isInteger`, matching `resolveRegisterScope`, the middleware's `parseTwinId` and the
  // admin planner. These three parses disagreed until `wizard-register-on-store`: `?twinId=1.5`
  // passed the wizard's own guard on `Number.isFinite`, rendered the whole wizard, and then could
  // not load a register — reporting zero assets on the Equipment tick while the planner sidebar
  // correctly refused to read anything.
  const twinId = Number.isInteger(twinIdParam) && twinIdParam > 0 ? twinIdParam : undefined;
  const twinBid = searchParams.get('twinBid') ?? undefined;
  twinBidRef.current = twinBid;
  // R5.4: a handed-back view never outlives the page's visit to one twin. R3.6: nor does a held
  // picture. Both on unmount and twin change only: React's development rehearsal unmounts and mounts
  // again at once, which must not drop the live viewer's Edit hand-on or its picture
  // (`spec-onboarding-persistent-scene-edit-flight`, R6), so the clear waits a microtask and a mount
  // for the same twin calls it off.
  useEffect(() => {
    cancelPageClear(twinBid);
    return () => schedulePageClear(twinBid);
  }, [twinBid]);
  /*
   * Create mode (`twin-details-page-create`, R2.1): the selector's *Create New Twin* opens
   * `?create=1`. A `twinId` wins — after a create the URL names the twin, so a reload never returns
   * to the form.
   */
  const createMode = twinId === undefined && searchParams.get('create') === '1';
  const location = useLocation();
  // R1.5: start the 3D plan's chunk with the wizard, not when the first plan pane mounts. A load that
  // fails is said by the plan panes themselves (`PlanRendererProviders`), so nothing is left to the
  // page here.
  useEffect(() => {
    preloadPlanSceneProviders().catch(() => undefined);
  }, []);

  // ---- Section completion state (drives the nav ticks + launch summary) ----

  const [accessToken, setAccessToken] = useState<string>();

  /**
   * The twin's Asset Register, read through `EquipmentCatalogStore` — the ONE register reader in
   * the twin-viewer (`wizard-register-on-store`, ledger row 27).
   *
   * This page kept its own `assets` state over its own `getTwinAssetsOrThrow` call, while the
   * planner's sidebar — mounted in this same page as soon as the operator reaches the Floor Plan
   * step — loaded the same rows for the same twin through the store's swallowing read. Two readers,
   * two error contracts, and two answers: a placement made in the planner creates a register row
   * and appends it to the store, so this page's copy undercounted the register by exactly the rows
   * the operator had just created until the next section change.
   *
   * `registerRows` is the raw server rows, because that is what this page needs — the completion
   * tick, the coverage index and the Launch summary read register columns the sidebar's projection
   * drops.
   */
  const registerRows = useEquipmentCatalogStore(s => s.registerRows);
  const loadRegisterAssets = useEquipmentCatalogStore(s => s.loadRegisterAssets);

  /*
   * The first token could not be had. Before `twin-details-page-landing` nothing waited on it; now
   * the landing does, so a rejection settles the twin read as failed (the wizard opens on Twin
   * details, as it always did) rather than leaving *Opening the twin…* up for ever.
   */
  const [tokenFailed, setTokenFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Bounded (PR review): the landing waits on this, so a stalled token request must reject and
    // settle the landing rather than leave *Opening the twin…* up.
    getTokenBounded(getAccessTokenSilently).then(
      token => {
        if (!cancelled) setAccessToken(token);
      },
      (error: unknown) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[onboarding] could not get a token on opening', { error: String(error) });
        setTokenFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  // Resolve the wizard's CREATE/UPDATE mode once the twin context is known. Derived from the twin's
  // `lifecycle_stage` (`< graphSaved` ⇒ CREATE, else UPDATE) and read by the wizard's surfaces for
  // copy ONLY — it must never gate activation (spec `twin-onboarding-onboarding-states`, FR5); the
  // delta guard in `handleGoLive` stays the sole authority over what Go Live does. The value is never
  // persisted; it is re-derived here on every mount. A failed read falls back to CREATE (the safe
  // default) because `getTwinById` swallows its errors and returns null.
  /**
   * The twin `?twinBid=` names, kept rather than discarded.
   *
   * It was already being fetched for the mode above and thrown away. Keeping it is what makes the
   * identity cross-check below possible without a second request — see `twinIdentityConflict`.
   */
  const [bidTwin, setBidTwin] = useState<AdminTwin | null>(null);
  /**
   * The read above has answered for the current `?twinBid=` — with a twin, or with nothing (no such
   * twin, a failed read, no token, or no `?twinBid=` at all; `getTwinById` cannot tell the first two
   * apart). `bidTwin` alone cannot say this: null is both "not yet" and "nothing". The landing, the
   * chip and the Twin details seed all wait on it (`twin-details-page-landing`, R2–R3).
   */
  const [bidTwinSettledFor, setBidTwinSettledFor] = useState<{
    twinBid: string | undefined;
  } | null>(null);
  /*
   * Keyed by the `?twinBid=` it answered for, so the render in which the address changes — before
   * the read effect has re-entered "unknown" — never reads the last twin's answer as this one's.
   */
  const bidTwinSettled = bidTwinSettledFor !== null && bidTwinSettledFor.twinBid === twinBid;
  /*
   * The twin a Go Live in this session took to `graphSaved`. The read above is not repeated after a
   * launch, so without this the route would still say `create` for a twin that is now live, and Twin
   * details would go on replacing its zone (`twin-details-page-address`, R4: a launched twin keeps
   * its stored zone).
   */
  const [graphSavedFor, setGraphSavedFor] = useState<string | null>(null);

  /*
   * Auth0's own getter, held in a ref.
   *
   * The lookup below now carries the identity guard, so it must be as reliable as the reads it
   * guards. `accessToken` is fetched ONCE on mount and never refreshed, and this is a long-lived
   * screen: on a session where that value has expired, `getTwinById` fails, the conflict reads as
   * "cannot tell", and the wizard proceeds on a mismatched address — the guard failing open on its
   * own lookup. The store keeps this getter as the register's write capability for exactly this
   * reason (`wizard-register-on-store`, ledger row 46).
   *
   * In a REF, not a dependency: `useAuth0()` returns a fresh object per render and promises no
   * stable function identity, so depending on it re-issues the read on every render. `accessToken`
   * stays in the array below as the "the session has hydrated" trigger; only the token actually
   * SENT comes from the getter.
   */
  const getAccessTokenRef = useRef(getAccessTokenSilently);
  getAccessTokenRef.current = getAccessTokenSilently;
  /** The Twin details queue's `noteRecord`, `readMark` and `whenSettled`, set once it exists below. */
  const noteTwinRecordRef = useRef<
    ((bid: string, twin: Partial<AdminTwin>, mark?: number) => void) | null
  >(null);
  const twinReadMarkRef = useRef<(() => number) | null>(null);
  const twinSavesSettledRef = useRef<(() => Promise<boolean>) | null>(null);

  useEffect(() => {
    // Re-enter "unknown" whenever the input changes. Without this the previous twin's record
    // survives a change of `?twinBid=` -- briefly during the re-fetch, and permanently if the
    // parameter is dropped -- and the cross-check below would then compare the current `?twinId=`
    // against a twin the address no longer names.
    setBidTwin(null);
    setBidTwinSettledFor(null);
    if (!twinBid) {
      setBidTwinSettledFor({ twinBid });
      return;
    }
    if (!accessToken) {
      if (tokenFailed) setBidTwinSettledFor({ twinBid });
      return;
    }
    let cancelled = false;
    void (async () => {
      let token: string;
      try {
        token = await getTokenBounded(getAccessTokenRef.current);
      } catch (error) {
        // Cannot tell, which is not a mismatch — the wizard continues. Logged because a guard that
        // silently stops guarding is the failure this whole change is about.
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[onboarding] could not get a token for the twin lookup', {
          error: String(error),
        });
        setBidTwinSettledFor({ twinBid });
        return;
      }
      if (cancelled) return;
      const mark = twinReadMarkRef.current?.();
      // Bounded like the step's own read: the landing waits on this one.
      const twin = await getTwinById(Config.middlewareApi, token, twinBid, {
        timeout: TWIN_REQUEST_TIMEOUT_MS,
      });
      if (cancelled) return;
      setBidTwin(twin ?? null);
      // What the twin holds now is what the next Twin details save says it saw
      // (`onboarding-multiplayer-twin-record-checks`, R3.1).
      if (twin) noteTwinRecordRef.current?.(twinBid, twin, mark);
      setBidTwinSettledFor({ twinBid });
      useOnboardingSessionStore.getState().setMode(resolveOnboardingMode(twin?.lifecycle_stage));
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, twinBid, tokenFailed]);

  /**
   * The wizard's two twin identifiers disagree (`planner-twin-integrity` FR11, ledger row 89).
   *
   * `?twinId=` scopes the Asset Register — every card in the planner's sidebar, and every row the
   * Equipment step counts. `?twinBid=` is what `createTwinPlan` files the plan under, and what
   * prefixes every entity identity on it. They are two independent inputs and **nothing compared
   * them**, so a URL that was bookmarked, hand-edited, or had one parameter changed in the address
   * bar could read one twin's register while saving a plan against another.
   *
   * That is not hypothetical. Five cross-ORGANISATION placements were found on AdminDB Staging and
   * deleted when this shipped: plans belonging to twin `PUL2-COL` carried register rows owned by
   * twin `PUL-CON`, and each stray item wore a `PUL2-COL-` identity beside the other twin's
   * `assetUid` — the signature of exactly this disagreement.
   *
   * The middleware now refuses a save carrying such a link (`plan-register-tenancy-guard`,
   * feature 40), so it can no longer reach the wrong customer's model this way. This check
   * stays because refusing the whole wizard up front is a far better answer than letting an
   * operator work and then rejecting the save.
   *
   * ⚠ **Only a POSITIVE disagreement refuses.** A missing parameter, a fetch that has not resolved
   * yet, and a failed fetch are all "cannot tell" — `getTwinById` swallows its errors and returns
   * `null`, so a truthiness check here would refuse the wizard every time the network hiccuped, on
   * a page that is otherwise perfectly able to continue. `bidTwin` null means unknown, never
   * mismatched.
   */
  const twinIdentityConflict =
    bidTwin !== null && twinId !== undefined && bidTwin.id !== twinId ? bidTwin : null;

  /*
   * The same answer, reachable from a callback that must not take it as a dependency.
   *
   * `reloadRegister` is re-created on identity change and re-runs the read; adding the conflict to
   * its dependency array would re-issue the very read the conflict exists to prevent.
   */
  const twinIdentityConflictRef = useRef(twinIdentityConflict);
  twinIdentityConflictRef.current = twinIdentityConflict;

  /*
   * Drop anything a read landed before the conflict was known.
   *
   * `getTwinById` resolves after the wizard has already mounted and started reading, so on a
   * mismatched address the register can be in the store by the time this fires. Leaving it there
   * would keep one twin's rows and their write capability alive behind a screen that says the
   * wizard has refused.
   */
  const clearedForConflictRef = useRef(false);
  useEffect(() => {
    if (!twinIdentityConflict) {
      clearedForConflictRef.current = false;
      return;
    }
    // ONCE. `clear()` mints new array identities, so an unguarded call re-renders the page, and
    // anything keyed on those identities would call it again.
    if (clearedForConflictRef.current) return;
    clearedForConflictRef.current = true;
    useEquipmentCatalogStore.getState().clear();
    /*
     * The plan too, and for the same reason.
     *
     * The resume read is issued on mount, before `getTwinById` can have resolved, so it cannot be
     * prevented on the first pass — only undone. Leaving the `?twinBid=` twin's layout in a
     * module-scoped store behind a screen that says the wizard has refused is the same residue as
     * leaving the other twin's register rows there.
     */
    useFloorplanStore.getState().reset();
  }, [twinIdentityConflict]);

  /** Focus the refusal when it replaces the wizard, so a keyboard user is not dropped to <body>. */
  const refusalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (twinIdentityConflict) refusalRef.current?.focus();
  }, [twinIdentityConflict]);

  /**
   * How the last register read went — the SAME rule the planner's sidebar branches on.
   *
   * It replaces a local `'loading' | 'ready' | 'error'` state machine whose `error` branch this
   * page had to build its own rejecting read to reach at all. The rule now lives in the store,
   * where it is testable without mounting this page (~1,960 lines, unmountable — the finding that
   * put `registerCompletion.ts` in its own module) and where the two surfaces cannot answer
   * differently.
   *
   * `no-twin` is unreachable here: the page early-returns "No twin selected" above without a
   * `twinId`. It is handled as "not loading, not failed" so a future caller cannot make this branch
   * lie.
   */
  const registerRead = useEquipmentCatalogStore(s => registerReadState(s, { twinId }));

  /**
   * The `?twinBid=` this session's plan was loaded under (`onboarding-polish-plan-labels`).
   *
   * The register's names may only be copied onto the plan when the plan and the register are the
   * same twin. `sessionId` is minted once per mount, but `?twinBid=` can change without a remount,
   * and the plan-load effect then KEEPS the session's layout ("never clobber work") while the
   * register moves to the new twin. So the session scope alone vouches for nothing; this records the
   * twin the session's plan actually belongs to, from the moment the store becomes session-scoped.
   */
  const [sessionPlanTwinBid, setSessionPlanTwinBid] = useState<string | undefined>(undefined);
  const twinBidForSessionRef = useRef(twinBid);
  twinBidForSessionRef.current = twinBid;
  useEffect(() => {
    if (useFloorplanStore.getState().twinBID === sessionId) {
      setSessionPlanTwinBid(twinBidForSessionRef.current);
    }
    return useFloorplanStore.subscribe((state, prev) => {
      if (state.twinBID === sessionId && prev.twinBID !== sessionId) {
        setSessionPlanTwinBid(twinBidForSessionRef.current);
      } else if (state.twinBID !== sessionId && prev.twinBID === sessionId) {
        setSessionPlanTwinBid(undefined);
      }
    });
  }, [sessionId]);

  /**
   * The store scope the register's names may be copied under — `sessionId`, or nothing.
   *
   * Two POSITIVE confirmations, never the absence of a conflict (a security-review finding: the
   * identity cross-check below fails OPEN while `getTwinById` is unresolved or has failed):
   *  - `?twinId=` (the register) and `?twinBid=` (the plan) are the same twin — `bidTwin` is known
   *    and its id is `twinId`;
   *  - the session's plan was loaded under the current `?twinBid=`.
   * Anything else answers `undefined`, and the planner sync falls back to its plain `assetUid`
   * mirror while Go live copies nothing — the safe direction.
   */
  const registerNameScope =
    bidTwin !== null &&
    twinId !== undefined &&
    bidTwin.id === twinId &&
    sessionPlanTwinBid === twinBid
      ? sessionId
      : undefined;

  /**
   * Give every plan entity that has no register row one — ruling **R19**'s one-way rule
   * (`dgraph-drift-into-register`, epic ledger row 80).
   *
   * The loop itself now lives in `@repo/front-end-state` (`admin-planner-live-twin`, feature 39),
   * because the admin console's two register surfaces were then its second and third consumers (both
   * since removed) and a second copy of it was exactly the bloat this epic forbids. Nothing about what this page DOES
   * changed in that move: the refusals, the sequential creates, the per-entity failure collection,
   * the five-failure cap and the deliberate absence of a post-write read are all still there, and
   * `OnboardingPage.registerJoin.test.tsx`'s fifteen tests over them are untouched, which is the
   * proof rather than the claim.
   *
   * Its messages are folded into this page's own banner state below, because `assetWriteError` and
   * `assetWriteNotice` also carry the OTHER register writes this page does — delete, edit, add —
   * and the hook has no business owning those.
   */
  const {
    addMissing: addMissingRegisterRows,
    addingProgress,
    error: addMissingError,
    notice: addMissingNotice,
    clearMessages: clearAddMissingMessages,
  } = useAddMissingRegisterRows({
    twinId,
    twinBid,
    registerRead,
    nextStepHint: 'Give them a product or a generic type to finish the Equipment step.',
  });

  /**
   * Re-read the register: on every section change, and after every register write.
   *
   * Same cadence as the `refreshAssets` this replaces. Memoised on stable inputs —
   * `loadRegisterAssets` is a zustand action and `getAccessTokenSilently` is `useCallback`-stable —
   * because the section effect depends on it, and an unstable identity here would re-fire the read
   * on every render.
   *
   * The token GETTER, not the `accessToken` state above: that is fetched once on mount and never
   * refreshed, and this is a long-lived screen. It is also what the store keeps as the register's
   * write capability, so a stale value here is the drop handler's 401 (ledger row 46).
   */
  const reloadRegister = useCallback(
    (options?: { afterWrite?: boolean }) => {
      /*
       * Refused outright when the two twin identifiers disagree.
       *
       * The refusal below is an early `return` from render, and render-only would not have been
       * enough: this read pulls the `?twinId=` twin's register into the module-scoped store AND
       * installs `registerContext`, the live write capability behind every register create. On the
       * one address the wizard declares poisoned, that would leave one twin's rows and a write handle
       * for them sitting beside another twin's plan. `clear()` covers the ordering where the conflict
       * is discovered after a read has already landed -- `getTwinById` resolves asynchronously.
       */
      if (twinIdentityConflictRef.current) return;
      /*
       * `afterWrite` is passed by every caller that has just WRITTEN, and it stops this read joining
       * one issued before that write (`register-write-ux-hardening`, epic ledger row 67).
       *
       * The row writes (`useEquipmentRowWrites`, *Not tracked*) go through `updateTwinAsset` directly
       * rather than through the store — `updateRegisterAsset` is deliberately confined to the two
       * plan-owned columns — and identify through `identifyTwinAssets`, so nothing
       * records the write for the store's merge to re-apply, and a joined older read answered with a
       * register that predated the match. The resolved product then did not appear in the table or
       * the coverage tick until something else triggered a read.
       */
      void loadRegisterAssets(
        Config.middlewareApi,
        getAccessTokenSilently,
        { twinId },
        { afterWrite: options?.afterWrite },
      );
    },
    [loadRegisterAssets, getAccessTokenSilently, twinId],
  );

  /*
   * Where the Equipment step's register writes stand, for its *Draft saved* tag
   * (`equipment-page-stage`, R15). Every register write this step makes reports its start and its
   * end here; Retry re-reads the register and never repeats a write (ruling 3). A successful write
   * also bumps `registerWriteSeq`, which re-reads the brand mix.
   */
  // Per twin: a failure, a saved time or an open write on one twin never shows on another's tag.
  const registerSave = useRegisterSaveState({
    registerRead,
    reloadRegister,
    scope: twinId ?? null,
  });
  const { begin: beginRegisterWrite, settle: settleRegisterSave } = registerSave;
  const [registerWriteSeq, setRegisterWriteSeq] = useState(0);
  const settleRegisterWrite = useCallback(
    (token: number | null, outcome: RegisterWriteOutcome) => {
      settleRegisterSave(token, outcome);
      // Every settle, not only a success: a failed or unconfirmed write may still have written rows
      // (the importer commits in chunks), and the mix is re-read after it (R17).
      setRegisterWriteSeq(n => n + 1);
    },
    [settleRegisterSave],
  );

  /** The start screen's *Add an Asset* dialog is open (asset-register-management §4.3). */
  const [addingAsset, setAddingAsset] = useState(false);
  const [assetWriteError, setAssetWriteError] = useState<string | null>(null);
  /**
   * Bumped with every refusal a register write reports, so the table's error Banner remounts and
   * a screen reader hears a repeated refusal — the same sentence twice is otherwise no change at
   * all to the live region (`adoption-start-again` review).
   */
  const [assetWriteErrorSeq, setAssetWriteErrorSeq] = useState(0);
  const reportWriteError = useCallback((message: string) => {
    setAssetWriteError(message);
    setAssetWriteErrorSeq(seq => seq + 1);
  }, []);
  /**
   * A register write SUCCEEDED and has something to say (`dgraph-drift-into-register`).
   *
   * A sibling of `assetWriteError` rather than a reuse of it, because "added 10 assets" is not a
   * failure and must not be painted in the error banner's tone. Every existing write on this page
   * is silent on success — the row simply appears — but a reconciliation creates rows the operator
   * then has to act on, so it says how many and what is left to do.
   */
  const [assetWriteNotice, setAssetWriteNotice] = useState<string | null>(null);
  /**
   * The last upload in this session, for the file card only: its name, time and row counts until
   * the server's record of it is read (`equipment-page-stage`, R6). What the upload did is the
   * summary's, read from that record (`equipment-page-landed`, `equipment-page-reimport`). Replaced
   * only by the next successful upload; in memory for the session.
   */
  const [importAttempt, setImportAttempt] = useState<PageImportAttempt | null>(null);

  /*
   * The attempt is scoped to the twin it describes, and nothing else was scoping it.
   *
   * `twinId` comes from `useSearchParams`, so changing `?twinId=` re-renders this page WITHOUT
   * remounting it. The register store performs its own documented stale-drop on the new key, but
   * the held attempt is page state and survived — naming twin A's file on twin B's card. The same shape as the `setBidTwin(null)` reset above, and for the same
   * reason: re-enter "nothing known" whenever the input changes.
   */
  useEffect(() => {
    setImportAttempt(null);
  }, [twinId]);

  /**
   * The twin currently on screen, readable from a callback that was created for an earlier one.
   *
   * `onUploaded` closes over the `twinId` of the render that started the upload; this is how that
   * callback asks what the twin is NOW, when its response finally lands.
   */
  const twinIdRef = useRef(twinId);
  useEffect(() => {
    twinIdRef.current = twinId;
  }, [twinId]);
  /*
   * The twin on screen, for the Equipment table's writes: none once the PAGE has gone. Page-scoped,
   * not the table's: moving from Equipment to Floor Plan unmounts the table, and a holder rename
   * finishing then must still send the row's own rename (Copilot, PR #2157). Leaving the page, or
   * another twin, drops the outcome so a late write never re-reads the old twin into the store.
   */
  const pageMountedRef = useRef(false);
  useEffect(() => {
    pageMountedRef.current = true;
    return () => {
      pageMountedRef.current = false;
    };
  }, []);
  const twinOnPage = useCallback(
    () => (pageMountedRef.current ? (twinIdRef.current ?? null) : null),
    [],
  );

  /** The register as it stands, for a callback made by an earlier render (`onUploaded`'s landing). */
  const registerRowsRef = useRef(registerRows);
  registerRowsRef.current = registerRows;
  /** The file card's *Upload Updated Register*, where focus returns when an upload closes it. */
  const uploadToggleRef = useRef<HTMLButtonElement>(null);

  /**
   * Save an add. REJECTS on failure rather than swallowing, because
   * `AssetEditDialog` renders the server's message against the form — and that message IS the
   * validation feedback, produced by the same validators the CSV import uses.
   */
  /**
   * An explicit clear of a machine's model, on a row with NO catalogue product, clears the plan's
   * model too (Rob, 2026-10-06), as the planner panel's clear does. The mirror cannot: it never
   * writes a null over a plan model. Only where the store holds this twin's session plan
   * (`registerNameScope`, as Go live checks); a failure here leaves the register saved and the
   * plan's copy standing — the never-blank state. Used by the asset card's 3D model clear
   * (`equipment-page-asset-card`, ruled in the build 2026-10-20).
   */
  const clearPlanModelForRow = useCallback(
    (rowId: number) => {
      if (!registerNameScope) return;
      try {
        const { layout: before, twinBID: storeScope } = useFloorplanStore.getState();
        if (storeScope === registerNameScope) {
          const after = clearPlanModelThroughJoin(before, registerRows, twinBid, rowId, {
            repairStaleLinks: true,
          });
          if (after && after !== before) useFloorplanStore.getState().replaceLayout(after);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[your-assets] could not clear the plan model', error);
      }
    },
    [registerNameScope, registerRows, twinBid],
  );

  const handleSaveAsset = useCallback(
    async (patch: AssetFormPatch) => {
      setAssetWriteError(null);
      // The reconciliation's notice describes one past action, not this one.
      setAssetWriteNotice(null);
      // The reconciliation's banner lives in the shared hook and takes precedence in the render,
      // so clearing only this page's pair would leave it standing over this write's outcome.
      clearAddMissingMessages();
      if (!twinId) throw new Error('Select a twin before editing its register.');
      /*
       * A CURRENT token, for the same reason the store's drop path takes one (ledger row 46). The
       * `accessToken` state this used to read is fetched once on mount and never refreshed, so on
       * a long onboarding session Add here would 401 while a drag-to-place on the very
       * next screen succeeded — an asymmetry that reads as "the plan works but the register is
       * broken" rather than as an expired session.
       */
      /*
       * BOUNDED, because the dialog has already disabled both its buttons.
       *
       * `AssetEditDialog` sets `saving` before calling this, which disables Cancel AND Add — and
       * `auth0-spa-js` serialises `getTokenSilently` across tabs on a browser lock, so a wedged
       * sibling tab can hold this for tens of seconds with the write's own 30 s bound not yet
       * started. That is the dead end this whole feature exists to remove, one phase earlier.
       * Ruled in scope by Rob, 2026-09-06; raised by the error-handling reviewer.
       */
      const token = await getTokenBounded(getAccessTokenSilently);
      const saveToken = beginRegisterWrite();
      try {
        await createTwinAsset(Config.middlewareApi, token, {
          ...patch,
          twinId,
          name: String(patch.name ?? ''),
        });
      } catch (error) {
        // The dialog shows the write's own error; the tag says the register was not saved.
        settleRegisterWrite(saveToken, {
          ok: false,
          reason: error instanceof Error ? error.message : null,
        });
        throw error;
      }
      settleRegisterWrite(saveToken, { ok: true });
      setAddingAsset(false);
      reloadRegister({ afterWrite: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `clearAddMissingMessages` is a stable useCallback([]) from the hook
    [beginRegisterWrite, settleRegisterWrite, getAccessTokenSilently, reloadRegister, twinId],
  );

  /**
   * The 3D model picker's source (`register-model-picker`): the `.glb` files at the root of the
   * platform asset store, via `GET /api/model-files`.
   *
   * A FRESH token per read, from `getAccessTokenSilently`, rather than the `accessToken` state
   * above — that is captured once on mount and never refreshed, and this is a long-lived screen.
   * An expired token here would 401 and the picker would read as "the model store is broken". The
   * SDK caches, so this costs nothing until a refresh is genuinely due.
   *
   * Not swallowed: `getModelFiles` rejects by contract and the asset card's picker renders the
   * rejection with a Retry, rather than showing an empty store.
   */
  const loadModelFiles = useCallback(
    async (options?: { refresh?: boolean }) =>
      getModelFiles(Config.middlewareApi, await getAccessTokenSilently(), options),
    [getAccessTokenSilently],
  );

  // Twin details: reported up by the step on load and on every edit, so the nav tick, the Launch
  // summary and Go Live's origin gate all read the same thing. `located` is the SAME predicate the
  // activation chain uses (`isUsableOrigin`), so the tick can never claim done while Go Live would
  // refuse. Held in a callback ref-stable setter — the step calls it from an effect.
  const [twinDetails, setTwinDetails] = useState<TwinDetailsState>(TWIN_DETAILS_PENDING);

  /*
   * The step Floor Plan's draft notices are shown on for the landing visit (R6): the step the wizard
   * landed on, until the person moves. Only Floor Plan uses it; Twin details shows them always.
   */
  const [landedOn, setLandedOn] = useState<StepId | null>(null);
  if (landedOn !== null && chosenSection !== landedOn) setLandedOn(null);

  /**
   * Position on map has been on screen in this twin visit (`twin-details-page-position-placeholder`,
   * ruled by maria@twinlabs.ai 2026-10-16: this visit only, nothing stored). Its only reader is the
   * step's grey "seen" tick — never `canLaunch`. Reset below with the twin visit.
   */
  const [positionSeen, setPositionSeen] = useState(false);

  /*
   * A change of `?twinBid=` without a remount is a different twin — a new VISIT, even when it is a
   * twin seen earlier in the session (A → B → A): forget the last twin's Twin details answer and its
   * landing visit ("adjusting state while rendering", so no frame draws the new twin with the old
   * one's tick). The step on screen stays — the person is working there, and a choice is never
   * overridden; the landing effect still runs for the new twin, fills the step only if none is chosen,
   * and shows the new twin's own notices if it is live and the person is on Floor Plan.
   */
  const [landingTwinBid, setLandingTwinBid] = useState(twinBid);
  const [twinVisit, setTwinVisit] = useState(0);
  if (landingTwinBid !== twinBid) {
    setLandingTwinBid(twinBid);
    setTwinVisit(v => v + 1);
    setLandedOn(null);
    setTwinDetails(TWIN_DETAILS_PENDING);
    setPositionSeen(false);
  }
  // Seen on arrival — render-phase, as the reset above, so the tick is right on the first paint back
  // on the step list.
  if (section === SECTION.positionOnMap && !positionSeen) setPositionSeen(true);

  /** The `?twinBid=` the landing was last decided for: once per twin. */
  const landingDecidedForRef = useRef<{ twinBid: string | undefined } | null>(null);

  /**
   * The landing (`twin-details-page-landing`, R2): once per twin, when the page's twin read settles.
   *
   * A live twin — manage, migrate, or live with its launch record unread — opens on Floor Plan, where
   * its commonest job is. Anything else opens on Twin details, as the wizard always did: a twin that
   * is not live yet, and a read that found nothing, which is never taken for "a new twin". The launch
   * record is not waited for, because all three live routes land in the same place.
   *
   * A step the person picked first stands: the landing only fills an empty choice.
   */
  useEffect(() => {
    if (!bidTwinSettled) return;
    // Create mode lands nowhere: there is no twin yet, and deciding a step here would leave it
    // chosen across the hand-off, so the new twin would never get its own landing
    // (`twin-details-page-create`, R6.3–R6.4).
    if (createMode) return;
    // `!== null` first: with no `?twinBid=`, `twinBid` is undefined, and so is `null?.twinBid` —
    // an optional chain here would read "already decided" before anything was.
    if (landingDecidedForRef.current !== null && landingDecidedForRef.current.twinBid === twinBid) {
      return;
    }
    landingDecidedForRef.current = { twinBid };
    const route = bidTwin
      ? resolveOnboardingRoute({ lifecycleStage: bidTwin.lifecycle_stage, launchRecord: 'unknown' })
      : null;
    const landing =
      route !== null && isExistingTwinRoute(route) ? SECTION.plan : SECTION.twinDetails;
    const chosen = chosenSectionRef.current;
    // No `?twinBid=` means no read was made, so there is nothing to warn about.
    if (!bidTwin && twinBid) {
      // eslint-disable-next-line no-console
      console.warn('[onboarding] landing: twin read returned nothing; opening on Twin details', {
        twinBid,
      });
    }
    // eslint-disable-next-line no-console
    console.info('[onboarding] landing', {
      twinBid,
      route,
      step: chosen ?? landing,
      chosenByUser: chosen !== null,
    });
    /*
     * An updater, not a value — the ONE guard on the person's choice. A click on the step list that
     * arrives in the same turn as the read is queued BEFORE this effect runs (React flushes pending
     * effects ahead of the click's render), so the ref above can still read "nothing chosen" and the
     * log can say `chosenByUser: false`. Applied in order, the person's choice is `current` here and
     * stands. `landedOn` is cleared in render whenever the step is not the landing.
     */
    setSection(current => current ?? landing);
    setLandedOn(landing);
  }, [bidTwinSettled, bidTwin, twinBid, createMode, setSection]);

  /*
   * Twin details at page level (R3): seeded from the twin the page already reads, so the step's tick
   * and the Launch summary are right on whichever step the wizard opens — not only once step 1 has
   * been visited. The step's own report wins from its first one onward, because only the step sees
   * unsaved edits. The stored zone, never the browser's: the step offers the browser's as a default
   * to save, but the page has nothing to save and must not claim a zone the twin does not hold.
   */
  /*
   * The visit the step last reported in — keyed by visit, not by twin, so returning to a twin whose
   * step reported on an earlier visit (A → B → A) is seeded afresh rather than left pending.
   */
  const stepReportedInVisitRef = useRef<number | null>(null);
  const twinVisitRef = useRef(twinVisit);
  twinVisitRef.current = twinVisit;
  const reportTwinDetails = useCallback((next: TwinDetailsState) => {
    stepReportedInVisitRef.current = twinVisitRef.current;
    setTwinDetails(next);
  }, []);
  useEffect(() => {
    if (!bidTwinSettled || stepReportedInVisitRef.current === twinVisit) return;
    if (!bidTwin) {
      setTwinDetails({ ...TWIN_DETAILS_PENDING, read: 'failed' });
      return;
    }
    const form = formFromTwin(bidTwin);
    setTwinDetails({
      located: isLocated(form),
      placeholder: looksLikePlaceholderOrigin(form),
      dirty: false,
      address: summaryAddress(form),
      timezone: form.iana_timezone.trim() || null,
      read: 'ready',
    });
  }, [bidTwinSettled, bidTwin, twinVisit]);

  /** The step heading — where focus goes after a swap (Block A). */
  const stepHeadingRef = useRef<HTMLHeadingElement | null>(null);

  /** Create mode's token for each of its requests, bounded as every other read here is. */
  const getCreateToken = useCallback(
    () => getTokenBounded(getAccessTokenSilently),
    [getAccessTokenSilently],
  );
  /*
   * Arriving from Create Twin (`twin-details-page-create`, R6.3): once the wizard has landed, say
   * the twin exists, put focus on the step heading, and consume the flag — replacing the history
   * entry's state, so a reload or Back does not say it again.
   */
  const [createdAnnouncement, setCreatedAnnouncement] = useState('');
  // History state can come from anywhere (a restored entry, another `navigate`): only a string counts.
  const createdState: unknown = (location.state as Record<string, unknown> | null)?.justCreated;
  const justCreated = typeof createdState === 'string' ? createdState : undefined;
  useEffect(() => {
    if (!justCreated || twinId === undefined || landingPending) return;
    setCreatedAnnouncement(`${justCreated} created. Every step is now unlocked.`);
    stepHeadingRef.current?.focus();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [justCreated, twinId, landingPending, navigate, location.pathname, location.search]);

  /**
   * Bumped after every swap that hydrated a new draft (`twin-details-page-versions`, R8.4). The step
   * content is keyed on it, so whichever editor is on screen — Floor Plan, Equipment, Observers —
   * remounts from the new draft rather than relying on each to follow a plan replaced under it.
   */
  const [draftGeneration, setDraftGeneration] = useState(0);
  /**
   * The onboarding stage's tunnel (`spec-onboarding-persistent-scene-stage`): held here, above the
   * draft key, so the stage and every remounted step share it.
   */
  const [stageTunnel] = useState(createStageTunnelStore);
  /**
   * The page's full-window backdrop, handed to the stage's tunnel once mounted and taken back on
   * unmount (`spec-onboarding-persistent-scene-full-bleed`, R1.2): a step whose scene is placed on
   * the page shows the map there, under the glass header and step list.
   */
  const setBackdrop = useCallback(
    (el: HTMLDivElement | null) => stageTunnel.setBackdrop(el),
    [stageTunnel],
  );
  /** The open, and the plan id, state and stamp every save works from (`planSync.ts`). */
  const planLoad = usePlanLoad({
    accessToken,
    twinId,
    twinBid,
    sessionId,
    twinIdentityConflict,
    userEmail: user?.email ?? null,
    modifiedByRef,
    onSwapped: () => setDraftGeneration(n => n + 1),
  });
  const {
    planId,
    planIdRef,
    planState,
    planLoading,
    planSavedAt,
    planSavedBy,
    planModifiedAtRef,
    getFirstMatchSource,
    restartMatchRef,
    draftChoice,
    draftChoiceRef,
  } = planLoad;

  /**
   * What the twin is measuring RIGHT NOW, loaded into the editing session
   * (`camera-observer-setup-load-existing`, FR1/FR2/FR9).
   *
   * Beside the plan-load effect above rather than inside the Observers stage, because FR9 needs it
   * on every entry route — a resumed plan, *Start from this twin*, and a live-model import
   * mid-session — and that stage only mounts when the operator reaches step 5. Ten of the sixteen
   * production twins carrying streams have no saved plan at all, so a load that only served the
   * resume path would serve six twins out of sixteen.
   *
   * It reads and never writes: the observers land in the plan, and Go Live remains the only thing
   * that touches the twin (FR11, epic ruling R3).
   */
  const liveConfiguration = useLiveTwinConfiguration({
    twinId,
    twinBid,
    accessToken,
    sessionId,
    // The running model's org is resolved from the TWIN, never from this selection — this is an
    // input to that check, exactly as `useLiveTwinPlan` takes it.
    selectedOrgBid,
    // Refused addresses read nothing, exactly as the register and plan reads above already do.
    enabled: !twinIdentityConflict,
    // Decided on open, before the plan is hydrated (R2.4).
    firstMatchObserverSource: getFirstMatchSource,
  });
  restartMatchRef.current = liveConfiguration.restartMatch;

  /*
   * The launch history (spec twin-launch-history-record): Go Live records each launch, and the Go
   * live button stays locked for 60 seconds after one — timed from the server's record, so a reload
   * or a second person sees the same lock.
   */
  const launchLock = useLaunchLock({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
    twinId: twinId ?? null,
    active: section === SECTION.launch,
  });
  /*
   * Whether the twin's opening hours let it launch (spec twin-details-page-launch-gate): read once
   * for the twin on any step, again each time Launch opens. Go live is held on it, and the launch
   * claim refuses on the same rule on the server.
   */
  const launchHours = useLaunchHours({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
    tokenFailed,
    twinBid: twinBid ?? null,
    active: section === SECTION.launch,
  });
  /*
   * The Opening hours step's read, its save queue and its save state (spec
   * twin-details-page-hours-week, R3). At page level so a save already queued finishes when the
   * person moves on, and so the title bar's *Saved* tag can read it. Every answer is adopted into
   * `launchHours`, so the step's tick, Launch and the step agree.
   */
  const hoursEditor = useOpeningHoursEditor({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
    tokenFailed,
    twinBid: twinBid ?? null,
    active: section === SECTION.openingHours,
    adoptLaunchHours: launchHours.adopt,
  });
  /**
   * A saved change is the twin's everywhere, whichever step saved it
   * (`retire-edit-mode-position-write-path`, R6): every key of the patch reaches the page's twin
   * record for that BID — so the 3D plan's anchor and Launch read what was saved — and a moved
   * position moves the page's Twin details answer too, so the nav tick, the Launch summary and Go
   * Live's gate agree with it. The step reports its own answer only while it is on screen, and the
   * page's seed from `bidTwin` stops once it has (`stepReportedInVisitRef`), so a save made on
   * Position on map would otherwise never reach them.
   *
   * A rename is also the twin's name in the twin selector without a reload
   * (`twin-details-page-summary`, R3.7): the selector's entry is renamed for whichever twin the save
   * was for, since that list holds every twin.
   */
  const onScreenTwinBidRef = useRef(twinBid);
  onScreenTwinBidRef.current = twinBid;
  /*
   * An accepted save of an address line or the country, for the twin on screen: the address lookup
   * below runs from it (`onboarding-persistent-scene-twin-details-map`, R3.1).
   */
  const [addressSavedTick, setAddressSavedTick] = useState(0);
  const onTwinDetailsSaved = useCallback((bid: string, patch: TwinDetailsPatch) => {
    setBidTwin(prev => recordAfterSave(prev, bid, patch));
    if (bid === onScreenTwinBidRef.current && carriesAddress(patch)) {
      setAddressSavedTick(tick => tick + 1);
    }
    if ('latitude' in patch && bid === onScreenTwinBidRef.current) {
      // The builders always send the pair together (wire rule 3), so the patch alone says where.
      const pair = { latitude: patch.latitude ?? '', longitude: patch.longitude ?? '' };
      const located = isLocated(pair);
      const placeholder = looksLikePlaceholderOrigin(pair);
      setTwinDetails(prev =>
        prev.located === located && prev.placeholder === placeholder
          ? prev
          : { ...prev, located, placeholder },
      );
    }
    const { name } = patch;
    if (name === undefined) return;
    // The twin selector's list is read once per organisation and kept in the twin store, so its
    // entry is renamed in place rather than waiting for the next organisation change.
    const store = useTwinContext.getState();
    const listed = store.accessibleTwins;
    if (listed?.some(twin => twin.bid === bid)) {
      store.setAccessibleTwins(listed.map(twin => (twin.bid === bid ? { ...twin, name } : twin)));
    }
  }, []);
  /*
   * The Twin details step's save queue (`twin-details-page-address`, R6): every change there saves
   * straight to the twin. At page level for the same two reasons as the hours editor's — a save
   * already queued finishes when the person presses *Next*, and the title bar's tag reads it.
   */
  const twinDetailsSave = useTwinDetailsSave({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
    twinBid: twinBid ?? null,
    onSaved: onTwinDetailsSaved,
  });
  noteTwinRecordRef.current = twinDetailsSave.noteRecord;
  twinReadMarkRef.current = twinDetailsSave.readMark;
  twinSavesSettledRef.current = twinDetailsSave.whenSettled;
  const twinWrittenSinceRef = useRef(twinDetailsSave.writtenSince);
  twinWrittenSinceRef.current = twinDetailsSave.writtenSince;
  const twinClashFieldsRef = useRef<string[]>([]);
  twinClashFieldsRef.current = twinDetailsSave.clash?.fields ?? [];
  /*
   * A Twin details save was refused because someone else changed a field
   * (`onboarding-multiplayer-twin-record-checks`, R3.3): read the twin again so the page's record —
   * the title bar's name, the position Twin details follows — shows theirs. Replaced in place, never
   * through the read above, which re-enters "unknown" and would close the step behind its landing.
   * A failed read leaves the record as it is; a newer clash's read wins.
   */
  const clashSeq = twinDetailsSave.clash?.seq;
  const clashBid = twinDetailsSave.clash?.bid;
  useEffect(() => {
    if (clashSeq === undefined || !clashBid || clashBid !== twinBid) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        // The rest of the refused save lands first, so the record read is never older than it.
        await twinSavesSettledRef.current?.();
        if (cancelled) return;
        const token = await getTokenBounded(getAccessTokenRef.current);
        if (cancelled) return;
        const mark = twinReadMarkRef.current?.();
        const twin = await getTwinById(Config.middlewareApi, token, clashBid, {
          timeout: TWIN_REQUEST_TIMEOUT_MS,
        });
        if (cancelled || !twin || twin.bid !== clashBid) return;
        // Only the clashed fields, and not one saved again while the read was in flight: the rest of
        // the record keeps what was saved and shown (Copilot review).
        const written = new Set(
          mark === undefined ? [] : twinWrittenSinceRef.current(clashBid, mark),
        );
        const clashed = new Set(twinClashFieldsRef.current.filter(key => !written.has(key)));
        const theirs = Object.fromEntries(
          Object.entries(twin).filter(([key]) => clashed.has(key)),
        ) as Partial<AdminTwin>;
        // Shown, never noted as seen: the step that shows the field notes it from its own read, so a
        // step whose read failed still checks the next edit against what it shows (Copilot review).
        setBidTwin(prev => (prev && prev.bid === clashBid ? { ...prev, ...theirs } : prev));
      } catch {
        // The step's own re-read still runs; the record catches up at the next read.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per clash: the sequence number is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clashSeq]);
  /** The page's record of the twin's position, for Twin details to follow (twin-details-map R3.3). */
  const recordLatitude = bidTwinSettled ? bidTwin?.latitude : undefined;
  const recordLongitude = bidTwinSettled ? bidTwin?.longitude : undefined;
  const recordSource = bidTwinSettled ? bidTwin?.position_source : undefined;
  const twinDetailsRecordPosition = useMemo(
    () =>
      bidTwinSettled && bidTwin
        ? {
            latitude: recordLatitude == null ? '' : String(recordLatitude),
            longitude: recordLongitude == null ? '' : String(recordLongitude),
            source: recordSource ?? null,
          }
        : null,
    // The record's position fields are the dependencies, not the record object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bidTwinSettled, recordLatitude, recordLongitude, recordSource],
  );
  /*
   * Saving an address flies the washed map there, and sets the position while the twin has none
   * (`onboarding-persistent-scene-twin-details-map`, R3). Here, not in Twin details, so a lookup
   * outlives the step's fade when the person presses *Next* straight after typing (plan ruling 1).
   */
  const addressFlight = useAddressFlight({
    twinBid,
    twin: bidTwinSettled ? bidTwin : null,
    addressSavedTick,
    save: twinDetailsSave,
    mapboxToken: authMetadata?.tokens?.mapboxToken,
    onTwinDetails: !landingPending && section === SECTION.twinDetails,
    // The refusal screens below: nothing is looked up or written behind them (Copilot review).
    enabled: twinId !== undefined && !twinIdentityConflict,
  });
  const launchRecord = useLaunchRecord(Config.middlewareApi);
  /**
   * An observer edit the stage has made but not yet written into the store (its write-through
   * waits for the next animation frame). A save landing meanwhile sent a layout without it, so it
   * must not mark the store clean (`runPersistPlan`).
   */
  const observerEditPendingRef = useRef(false);

  /*
   * Warn before leaving with unsaved plan work. Mounted here rather than on the Observers stage
   * because the exit it guards — the header's logo button — lives here, and because `beforeunload`
   * should be armed for the whole wizard rather than one step of it.
   */
  const leaveGuard = useLeaveGuard({ confirmLeave });

  /*
   * The launch history's re-read key (`twin-details-page-landing`, R4): bumped on arrival at Twin
   * details from another step — a launch made on the Launch step is then in the panel on return —
   * and when a launch completes (Go Live, below).
   */
  const [launchHistoryKey, setLaunchHistoryKey] = useState(0);

  /*
   * One watcher on the step the person is on (`chosenSection`, null while the landing is pending).
   *
   * The landing is an ARRIVAL, not a step left behind: the register refresh skips it (the mount's
   * read already covers it, so opening makes no more requests than it did), and so does the launch
   * history (its opening read is already in flight). Every later move refreshes the register, so
   * ticks reflect work done in the step just left, and arriving at Twin details re-reads the history.
   */
  const previousChosenRef = useRef<StepId | null | undefined>(undefined);
  useEffect(() => {
    const previous = previousChosenRef.current;
    previousChosenRef.current = chosenSection;
    if (previous === null && chosenSection !== null) return;
    reloadRegister();
    if (
      chosenSection === SECTION.twinDetails &&
      previous != null &&
      previous !== SECTION.twinDetails
    ) {
      setLaunchHistoryKey(k => k + 1);
    }
  }, [reloadRegister, chosenSection]);

  /**
   * Reset the register store when the wizard unmounts.
   *
   * The store is module-scoped, so a torn-down wizard leaves both a twin's rows and a live write
   * capability (`registerContext`, which carries the token getter) reachable from
   * `useEquipmentCatalogStore.getState()`. `clear()` had no production caller at all before this,
   * which made it a mitigation that read as present and was not.
   *
   * Here rather than in `EquipmentSidebar`, which is where the epic's ledger row first suggested:
   * the sidebar unmounts whenever the operator leaves the Floor Plan step, and the Equipment step,
   * the nav ticks and the Launch summary now read this store.
   */
  useEffect(() => () => useEquipmentCatalogStore.getState().clear(), []);

  // Creating this twin, or updating an established one? Derived on mount (see the effect above),
  // read here for copy only — the nav label, the nav bar's step title and the Launch action. Never
  // consulted by `handleGoLive` (FR5).
  const onboardingMode = useOnboardingSessionStore(s => s.mode);
  const launchLabel = launchLabelFor(onboardingMode);
  /**
   * The name of the button the activation messages tell the operator to press.
   *
   * `OnboardingLaunch` renders `updating ? 'Update twin' : 'Go live'`, and the messages used to
   * hard-code one or the other — so in UPDATE mode they named a control that was not on screen.
   * Derived once here so they cannot drift apart again.
   *
   * ⚠ **EIGHT messages, not six** — and one more since: Go Live's duplicate-composite refusal
   * (`launch-composite-refusals`), which takes this value as its `actionLabel`. An earlier version of this comment said six, and two of the
   * eight were then left hard-coding "Go live" — caught by Copilot on the PR, after ten reviewers
   * and a real-app validation run in UPDATE mode had all read past them. If you add a message that
   * names this button, it uses this value: `grep -c goLiveButtonLabel` should equal the number of
   * messages naming the button, plus its own declaration.
   */
  const goLiveButtonLabel = onboardingMode === 'UPDATE' ? 'Update twin' : 'Go live';

  // Plan + cameras: read live from the floorplan store.
  const layout = useFloorplanStore(s => s.layout);
  const storeTwinBID = useFloorplanStore(s => s.twinBID);
  const sessionLayout = layout && storeTwinBID === sessionId ? layout : null;
  // `spec-onboarding-persistent-scene-wash`, R2.4 and R6.1: the washed layer's own rule, on the
  // inputs it is given (the settled twin record, the plan read, this twin's session plan).
  const washedPlan = washedPlanAvailable({
    twinRecord: bidTwinSettled ? bidTwin : undefined,
    planRead: planState,
    layout: sessionPlanTwinBid === twinBid ? sessionLayout : null,
  });
  washedPlanRef.current = washedPlan;
  /*
   * `onboarding-persistent-scene-twin-details-map`, R2.3 and R2.4: a twin with a plan of at least
   * one floor but no real position is told, on Twin details, why the plan is not on the map. Once
   * per visit per twin: the page holds which twins' message was closed, in state (never storage),
   * until it unmounts. A new twin has no plan, so the create route never shows it.
   */
  const sessionFloors: unknown = (sessionPlanTwinBid === twinBid ? sessionLayout : null)?.floors;
  const planWithoutPosition =
    bidTwinSettled &&
    planState === 'loaded' &&
    Array.isArray(sessionFloors) &&
    sessionFloors.length > 0 &&
    !hasRealPosition(bidTwin);
  const [unplacedNoticeSeen, setUnplacedNoticeSeen] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const unplacedNotice = useMemo(
    () => ({
      open: planWithoutPosition && twinBid !== undefined && !unplacedNoticeSeen.has(twinBid),
      onClose: () => {
        if (twinBid === undefined) return;
        setUnplacedNoticeSeen(prev => (prev.has(twinBid) ? prev : new Set(prev).add(twinBid)));
      },
    }),
    [planWithoutPosition, twinBid, unplacedNoticeSeen],
  );
  /*
   * R3: the wash. Shown while the step's entry is washed and the washed map has drawn: never over an
   * empty page. The map shows whether or not a plan is drawn on it
   * (`onboarding-persistent-scene-twin-details-map`, R1), so "drawn" is the map's first draw, and a
   * plan arriving later (a position saved on Twin details) lands under a wash already shown. Faded by
   * `usePresence` over `motion.durFast`; between two washed steps `shown` does not change, so nothing
   * fades or remounts (R1.4). The landing cuts, as the panel fades do; reduced motion cuts in the hook.
   *
   * "Drawn" belongs to one washed layer: the twin's and the draft's. A twin change or a draft swap
   * mounts a new layer, which washes only once it reports its own draw, and leaving the form steps
   * forgets it.
   */
  const entryWashed = !landingPending && STEP_SCENES[section].underlay === 'washed';
  /*
   * The address flight the washed map has taken (twin-details-map R3.3). Held here, not by the
   * layer, so a request made while the layer's renderer is still loading is flown once it mounts,
   * and one made while no washed step is on screen is dropped rather than flown on the way back
   * (Copilot review on #2505).
   */
  const [flyTakenId, setFlyTakenId] = useState<number | null>(null);
  const flyRequest =
    addressFlight.flyRequest && addressFlight.flyRequest.id !== flyTakenId
      ? addressFlight.flyRequest
      : null;
  if (!entryWashed && flyRequest) setFlyTakenId(flyRequest.id);
  // The create route is Twin details' create form, before the twin exists: washed too, with no
  // landing to wait for (`onboarding-persistent-scene-twin-details-map`, R1.1).
  const mapWashed = createMode || entryWashed;
  const washedLayerKey = `${twinBid}:${draftGeneration}`;
  const [washedDrawnKey, setWashedDrawnKey] = useState<string | null>(null);
  if (!mapWashed && washedDrawnKey !== null) setWashedDrawnKey(null);
  const washedDrawn = washedDrawnKey === washedLayerKey;
  const onWashedDrawState = useCallback(
    (state: PlanDrawState) =>
      setWashedDrawnKey(prev =>
        state.kind === 'drawn' ? washedLayerKey : prev === washedLayerKey ? null : prev,
      ),
    [washedLayerKey],
  );
  const washShown = mapWashed && washedDrawn;
  const wash = usePresence(washShown, { animate: stepJoin.from !== null });
  // Aggregate across ALL floors — the section ticks, launch summary and Go Live
  // count the whole plan, not just the active floor. Every level goes through the shared
  // array-safe readers: a resumed layout arrives from a JSON column, so `floors`, `designs` and
  // each collection can be any shape, and `?? []` would let a non-array through to `.map` and
  // crash the whole page on render.
  const floorDesigns = layoutCurrentDesigns(sessionLayout);
  // `isLive` rather than `!x?._deleted`: the latter reads as null-safe but ADMITS nulls
  // (`null?._deleted` is `undefined`, so the negation is `true`), which then throw at the next
  // property read — `c.rtspEndpointId` and `o.targetRefid` just below. It is also a type guard, so
  // these stay non-nullable arrays.
  const placedItems = floorDesigns.flatMap(d => asArray(d?.items)).filter(isLive);
  const cameras = floorDesigns.flatMap(d => asArray(d?.cameras)).filter(isLive);

  // Observers persisted into the plan (they survive reload). The Observers step saves itself as
  // a draft (`observers-page-draft`); the Launch gate only opens once at least one observer is
  // *saved*.
  const savedObservers = floorDesigns.flatMap(d => asArray(d?.observers)).filter(isLive);
  /**
   * Asset Register coverage (asset-register-management §4.1), built once for the whole register.
   *
   * Held HERE because the delete refusal needs it: the Rows view's removal gate
   * (`equipmentIsPlaced`, below). On step 2 the register is now the Rows view
   * (`equipment-page-table`); admin's `AssetRegisterList` builds its own from the same inputs.
   */
  /**
   * The plan as the register sees it — every adopted item joined to its register row
   * (`plan-register-bid-match`, epic ledger row 79).
   *
   * DERIVED, never written back. The planner's own sync hook persists the same join into the plan
   * JSON when the Floor Plan step is open; this page needs the answer whether or not that step has
   * been visited, and computing it is cheaper and safer than mounting a second writer here (which
   * would dirty the plan before the operator has touched it).
   *
   * Two consumers below key on the register link and BOTH must see it, or they are wrong for
   * exactly as long as the operator has not opened the Floor Plan step: the coverage counts, and
   * the Rows view's refusal to delete a row that is placed on the plan (`equipmentIsPlaced`). On UGG-FIN, without
   * this, all 153 adopted items report as not tracing back to the register and every one of its 143
   * matching rows is freely deletable — the fail-open `isPlacementKnown` exists to prevent, one join
   * earlier.
   *
   * ⚠ `twinBid` is the wizard's `?twinBid=` — the twin's real dGraph BID — NOT the floorplan store's
   * `twinBID`, which on this page is the SESSION id. Prefixing an entity identity with a session id
   * would mint an identity no twin has ever had. Same trap `OnboardingPlanner`'s prop doc and the
   * duplicate-code check below both warn about.
   *
   * Everything else on this page deliberately keeps reading `sessionLayout`: the geometry counts, the
   * duplicate-identity check and the Go Live payload do not care about the register link, and giving
   * them a second layout reference would be a difference with no meaning.
   */
  const registerJoinedLayout = useMemo(
    () =>
      joinRegisterOntoPlan(sessionLayout, registerRows, twinBid, {
        /*
         * Repair a stale link too (`register-link-repair`, ledger row 83) — but only on a read
         * that actually succeeded, which is the whole of the gate. `registerRead` is the single
         * rule for that question and already answers `loading` for the window before the load is
         * even issued; `registerRows.length` cannot see that window at all, and is compatible
         * with `error` besides, because a failed refresh keeps the previous read's rows.
         *
         * This layout feeds the coverage index, the delete refusal and the Equipment list's
         * drift, so it MUST agree with what the planner's own sync hook does to the same plan —
         * otherwise the register would count an item as stranded that the canvas has already
         * repaired.
         */
        repairStaleLinks: registerRead === 'ready',
      }),
    [sessionLayout, registerRows, twinBid, registerRead],
  );

  /*
   * The Floor plan's search, in its list panel (`onboarding-polish-search`, FR4): every area, machine and
   * camera on every floor of the plan the planner shows — the register-joined layout, so a machine
   * renamed on Your Assets is found by its new name. Machines are also searched by their register
   * manufacturer and catalogue equipment type (`usePlanSearchSources`). The Observers step builds
   * its own index (it owns the camera feeds) and shows its box in its own list panel.
   */
  const planSearch = usePlanSearchSources();
  const planMode = useEditorStore(s => s.planMode);
  /*
   * Built only while the Floor plan's box is on screen. `registerJoinedLayout` changes on every
   * pointer move of a drag, and the index walks every floor — work for nothing on every other step
   * and in Align floors (found by review; `registerDrift` below is gated for the same reason).
   */
  const planSearchShown = section === SECTION.plan && !!sessionLayout && planMode !== 'align';
  /*
   * The register's rows that are not on the plan (`floor-plan-page-search`, R2): listed only once
   * the register was read back, each row's state by the Equipment page's own rule over the joined
   * layout — so the search never claims *Not on plan* about a register it has not read, or a plan
   * row the join has linked.
   */
  const planSearchRegister = useMemo(() => {
    if (!planSearchShown || registerRead !== 'ready') return undefined;
    const index = placementIndexFor(registerJoinedLayout);
    return {
      rows: registerRows,
      planStateOf: (row: (typeof registerRows)[number]) => assetPlanStateFor(row, index),
    };
  }, [planSearchShown, registerRead, registerRows, registerJoinedLayout]);
  const planSearchIndex = useMemo(
    () =>
      planSearchShown
        ? buildPlanSearchIndex(registerJoinedLayout, {
            registerByAssetUid: planSearch.registerByAssetUid,
            catalogueByReference: planSearch.catalogueByReference,
            register: planSearchRegister,
          })
        : NO_PLAN_SEARCH_ENTRIES,
    [
      planSearchShown,
      registerJoinedLayout,
      planSearch.registerByAssetUid,
      planSearch.catalogueByReference,
      planSearchRegister,
    ],
  );
  /*
   * The register's state, said at the foot of the open list (R9): while it loads, and when it could
   * not be read — then the plan's matches still show, and the note says rows not on the plan are
   * missing from them rather than letting "nothing matches" stand as a claim about the register.
   */
  const planSearchNote = useMemo(
    () =>
      registerRead === 'loading'
        ? { busy: true, text: 'Loading the register…' }
        : registerRead === 'error'
          ? {
              tone: 'error' as const,
              text: "The register couldn't be read, so rows not on the plan aren't searched.",
            }
          : undefined,
    [registerRead],
  );
  /*
   * Every match of a query that has a place on the plan, uncapped — ONE definition shared by the
   * light and the frame, so the two agree on what "on the plan" means (review). The matcher scores
   * and sorts every match whatever its cap (≈0.2 ms over 500 entries, warm), so uncapped costs no
   * more than the list's own pass.
   */
  const planHitsFor = useCallback(
    (query: string) =>
      matchPlanEntities(planSearchIndex, query, {
        twinBid,
        limit: Number.POSITIVE_INFINITY,
      }).hits.filter(
        h =>
          (h.kind === 'surface' || h.kind === 'item' || h.kind === 'camera') &&
          h.floorIndex != null,
      ),
    [planSearchIndex, twinBid],
  );
  /*
   * The query the box holds while it has focus (R4): every match of it on the plan is lit — all of
   * them, not the list's first 50 — and the light goes when the box is left, cleared or picked from.
   * The light is written to the editor store (`searchLitIds`), which the planner reads; the page
   * keeps the query in a ref, so a keystroke re-renders the plan, never this page (review). Spaces
   * and machines only: the design's lit look has no camera form.
   */
  const planSearchQueryRef = useRef<string | null>(null);
  const lightPlanSearch = useCallback(
    (query: string | null) => {
      planSearchQueryRef.current = query;
      useEditorStore.getState().setSearchLit(
        query
          ? planHitsFor(query)
              .filter(h => h.kind !== 'camera')
              .map(h => h.refid)
          : [],
      );
    },
    [planHitsFor],
  );
  // Re-light when the index changes under a held query (the register arrives, the plan changes),
  // and put the light out when the box leaves the screen (another step, Align floors) or the page
  // goes — the box's own unmount reports nothing.
  useEffect(() => {
    if (!planSearchShown) {
      planSearchQueryRef.current = null;
      useEditorStore.getState().setSearchLit([]);
      return undefined;
    }
    lightPlanSearch(planSearchQueryRef.current);
    return undefined;
  }, [planSearchShown, lightPlanSearch]);
  useEffect(() => () => useEditorStore.getState().setSearchLit([]), []);
  /*
   * Enter with nothing highlighted (R5): fit every plan match in view — on the floor shown when any
   * is there, else on the lowest floor that has one, switching to it first. Register rows have no
   * place, so a query that finds only those moves nothing and says so.
   */
  const framePlanMatches = useCallback(
    (query: string): string => {
      const onPlan = planHitsFor(query);
      if (onPlan.length === 0) return 'None of the matches is on the plan.';
      const shown = useFloorplanStore.getState().activeFloorIndex;
      const floorIndex = onPlan.some(h => h.floorIndex === shown)
        ? shown
        : Math.min(...onPlan.map(h => h.floorIndex as number));
      if (floorIndex !== shown) useFloorplanStore.getState().setActiveFloor(floorIndex);
      const ids = onPlan.filter(h => h.floorIndex === floorIndex).map(h => h.refid);
      useEditorStore.getState().frameEntities(ids);
      const floorName = onPlan.find(h => h.floorIndex === floorIndex)?.floorName;
      const count = ids.length === 1 ? '1 match' : `${ids.length} matches`;
      return floorName ? `Framed ${count} on ${floorName}.` : `Framed ${count}.`;
    },
    [planHitsFor],
  );
  /*
   * The list's empty sentence names the register only when the register was read: with no twin, or
   * before the read lands, "nothing in its register matches" would be a claim about rows nobody read
   * (review). The note at the list's foot says which (R9).
   */
  const planSearchEmptyText = useCallback(
    (q: string) =>
      registerRead === 'ready'
        ? `Nothing on this twin or in its register matches “${q}”.`
        : `Nothing on this twin’s plan matches “${q}”.`,
    [registerRead],
  );
  const [stepSearchSlot, setStepSearchSlot] = useState<HTMLDivElement | null>(null);
  /*
   * Where the Observers step portals its draft-notices chip — the one-bar header's slot
   * (`observer-autogen-review-layout`, L2). Only mounted while that header is.
   */
  const [noticesSlot, setNoticesSlot] = useState<HTMLDivElement | null>(null);

  /*
   * A Floor plan pick does what a Hierarchy row does, from wherever the operator is — the hit's
   * floor, then select and pulse — and, unlike a row, flies the plan to it at the current zoom (Rob,
   * 2026-10-06: a hit can be anywhere on a 150-machine floor, where a row sits beside the plan already
   * in view). `plan-fly-to`, R5.1: the one fly-to, which glides and lands clear of the card it opens;
   * the glow is `onboarding-status-colours-plan-machines` R6.
   */
  const pickOnPlan = useCallback((entry: { refid: string; floorIndex: number | null }) => {
    flyToOnPlan(entry.refid, { floorIndex: entry.floorIndex, select: true, glow: true });
  }, []);

  /*
   * `planAbsent` only when the plans were READ and there are none — the state a twin is in straight
   * after an adoption. Then nothing can be placed, so Remove and an import undo may proceed. A read
   * still in flight or one that failed leaves a null layout UNKNOWN, which stays a refusal.
   */
  const planAbsent = planState === 'none';

  /*
   * ── THE EQUIPMENT FRAME (`equipment-page-stage`) ────────────────────────────────────────────
   *
   * ONE identify model per register read (R3), over the same joined layout the list's Coverage
   * column reads. The score card, the chips, the main panel's title and the list's rows all come
   * from it, so nothing on the step counts a state a second way.
   */
  const identifyModel = useMemo(() => {
    /*
     * Guarded, as Floor Plan's planner guards the same call (`usePlanChecks`): a register row the
     * model cannot read would otherwise take the whole wizard down. Unbuilt, the frame shows no
     * number and the list keeps its own filters — nothing is counted on a model that does not exist.
     */
    try {
      return buildIdentifyModel(registerRows, { layout: registerJoinedLayout, planAbsent });
    } catch (err) {
      // The whole error (a local computation's, carrying no token or URL), so its stack survives.
      console.warn('[equipment] the identify model could not be built; no counts are shown', {
        twinId,
        rows: registerRows.length,
        error: err,
      });
      return null;
    }
  }, [registerRows, registerJoinedLayout, planAbsent, twinId]);
  // One chip at a time, *All* on arrival; the search tops the side panel (`onboarding-assets-search-in-panel`, R1). Page state for
  // this twin only: a new twin starts from *All* and an empty search.
  const [equipmentChip, setEquipmentChip] = useState<EquipmentChip>('all');
  const [equipmentQuery, setEquipmentQuery] = useState('');
  // The list follows the search a beat behind the typing, so a keystroke re-renders the box first.
  const deferredEquipmentQuery = useDeferredValue(equipmentQuery);
  // Whether *Upload Updated Register* has the uploader open (R6, ruling 2).
  const [uploaderOpen, setUploaderOpen] = useState(false);
  /*
   * Where the step lands (`equipment-page-landed`, R6–R8): the chip and the *Rows / Groups* view are
   * set from the model on arriving at a twin and after each upload lands; between those moments
   * they are the operator's. `landingKey` re-opens the Groups view's first group on each landing.
   *
   * A landing WAITS for rows that answer it: on arrival, the first ready model; after an upload,
   * the first ready model built from rows newer than the ones on screen when the upload landed —
   * `onUploaded` asks for the re-read, and landing on the pre-upload counts would open the wrong
   * chip.
   */
  const [equipmentView, setEquipmentView] = useState<'rows' | 'groups'>('rows');
  /*
   * The Rows view's sort (`equipment-page-table`, R5): unsorted on arrival, so the register's own
   * order shows; it survives chip and search changes and resets with the twin. Page state because
   * the count line names it (*· sorted by product*).
   */
  const [equipmentSort, setEquipmentSort] = useState<EquipmentSort>({
    field: null,
    direction: 'asc',
  });
  /*
   * *From this import* (`equipment-page-reimport`, R11): after an upload that updated or left out
   * rows, the Rows view shows what it wrote, added first, until the operator picks a chip, a view,
   * a sort or a search, undoes, adds an asset, or the twin changes. Session only, like the landing.
   */
  const [fromImport, setFromImport] = useState<FromImport | null>(null);
  const [equipmentLandingKey, setEquipmentLandingKey] = useState(0);
  const pendingLandingRef = useRef<{ after: readonly unknown[] | null } | null>({ after: null });
  // True from a landing until the operator picks a chip or a view.
  const landedUntouchedRef = useRef(false);
  /** A landing: its chip and view, and the Groups view's first group opened again. */
  const landOn = useCallback((landing: { chip: EquipmentChip; view: 'rows' | 'groups' }) => {
    setEquipmentChip(landing.chip);
    setEquipmentView(landing.view);
    setEquipmentLandingKey(key => key + 1);
  }, []);
  useEffect(() => {
    setEquipmentChip('all');
    setEquipmentView('rows');
    setEquipmentSort({ field: null, direction: 'asc' });
    setEquipmentQuery('');
    setFromImport(null);
    setUploaderOpen(false);
    pendingLandingRef.current = { after: null };
    landedUntouchedRef.current = false;
  }, [twinId]);
  /*
   * A chip no longer offered (*Keep off plan* at 0, *Doesn't fit* once its last row is kept, or the
   * plan loading) hands back to *All* — in the SAME render, as `shownChip`, so the rows never pass
   * through a render filtered by a chip that is gone: that render would close the asset card on
   * the row just kept (review, H2). The state follows in the effect below. *Needs a look* is a
   * landing no chip offers: its emptying is the re-land's, below.
   */
  const { equipmentChips, shownChip } = useMemo(() => {
    if (!identifyModel) {
      return {
        equipmentChips: { todo: [], done: [], plan: [] } as EquipmentChipGroups,
        shownChip: equipmentChip,
      };
    }
    const groups = equipmentChipGroups(identifyModel, equipmentChip);
    return equipmentChip === 'needs_a_look' || isChipOffered(groups, equipmentChip)
      ? { equipmentChips: groups, shownChip: equipmentChip }
      : { equipmentChips: equipmentChipGroups(identifyModel, 'all'), shownChip: 'all' as const };
  }, [identifyModel, equipmentChip]);
  useEffect(() => {
    if (shownChip !== equipmentChip) setEquipmentChip(shownChip);
  }, [shownChip, equipmentChip]);
  const toggleEquipmentChip = useCallback((key: EquipmentChip) => {
    // The operator's choice stands from here; a landing no longer re-chooses for them.
    landedUntouchedRef.current = false;
    setFromImport(null);
    setEquipmentChip(active => (active === key ? 'all' : key));
  }, []);
  const equipmentVisibleIds = useMemo(
    () =>
      identifyModel
        ? equipmentRowFilter(registerRows, identifyModel, {
            chip: shownChip,
            query: deferredEquipmentQuery,
          })
        : undefined,
    [registerRows, identifyModel, shownChip, deferredEquipmentQuery],
  );
  /*
   * The step's ONE selection, shared by the Rows and Groups views (`equipment-page-table`, R11–R12):
   * cleared on a new chip, search or twin, and never holding a row the register or the filter no
   * longer shows.
   */
  const registerIds = useMemo(() => new Set(registerRows.map(row => row.id)), [registerRows]);
  const equipmentSelection = useEquipmentSelection({
    twinId,
    filterKey: `${shownChip}|${deferredEquipmentQuery}`,
    registerIds,
    visibleIds: equipmentVisibleIds,
  });
  /*
   * Whether the rows on screen are THIS twin's, read at least once — independent of a re-read in
   * flight. Every write is followed by a re-read, and a re-read reports `loading`; keying the frame
   * on `registerRead === 'ready'` tore the tag, the search, the chips and the list's filtering down
   * and back up after every save, dropping keyboard focus (review, `equipment-page-stage`). The store
   * keeps a register's rows through its re-read, and stamps `loadedKey` only when a read of that
   * register lands, so this is true exactly while the rows shown belong to this twin.
   */
  const registerRowsAreThisTwins = useEquipmentCatalogStore(s => {
    const scope = resolveRegisterScope({ twinId });
    return scope !== null && s.loadedKey === scope.key;
  });
  const equipmentRowsShown = registerRowsAreThisTwins && registerRows.length > 0;
  /*
   * The register as the value check names an updated file against it (`equipment-page-reimport`,
   * R3): only while the rows are this twin's, so another twin's names never number this one's file.
   */
  const registerNameRows = useMemo<readonly RegisterNameRow[]>(
    () =>
      registerRowsAreThisTwins
        ? registerRows.map(row => ({
            id: row.id,
            name: row.name ?? null,
            assetName: row.asset_name ?? null,
            identifier: row.customer_asset_identifier ?? null,
          }))
        : [],
    [registerRows, registerRowsAreThisTwins],
  );
  const equipmentPanel: EquipmentPanel = !twinId
    ? 'none'
    : registerRead === 'error'
      ? 'error'
      : equipmentRowsShown
        ? identifyModel
          ? 'ready'
          : 'uncounted'
        : registerRead === 'loading'
          ? 'loading'
          : 'none';
  // The chips, the search and the Rows view's filtering need the model; without it the Rows view
  // shows every row, read-only (`equipment-page-table`).
  const equipmentFiltered = equipmentPanel === 'ready';
  // What landed: the twin's latest upload, from the server's record (`equipment-page-landed`, R1–R5).
  // Read on EVERY step once the rows are this twin's, not only on Equipment: the step list's tick
  // reads its late refusals (`equipment-page-reimport`, R14), and an unread record never ticks. One
  // read per twin visit and refresh key — changing step does not read again.
  const [importSummaryKey, setImportSummaryKey] = useState(0);
  // Bumped after a Keep or a Remove on the summary's *Not in this file* list: the same record, read
  // again without clearing the summary under the operator (`equipment-page-reimport`, R7, R8).
  const [importSummaryQuietKey, setImportSummaryQuietKey] = useState(0);
  const importSummary = useEquipmentImportSummary({
    active: registerRowsAreThisTwins,
    twinId: twinId ?? null,
    conflicted: twinIdentityConflict !== null,
    refreshKey: importSummaryKey,
    quietKey: importSummaryQuietKey,
    baseUrl: Config.middlewareApi,
    getToken: () => getTokenBounded(getAccessTokenRef.current),
  });
  const latestImport = importSummary.record;
  /*
   * The landing reads the import's mode (`equipment-page-adoption`, R11): an adoption lands on
   * *Needs a look*. The record is read only once the rows are this twin's, so waiting for it would
   * hold every landing a round-trip; the step lands at once on what it knows, and lands again on
   * *Needs a look* when the record settles as an adoption, unless the operator has chosen since. A
   * failed read, or no record, leaves the customer landing.
   */
  const importMode = importSummary.settled ? (latestImport?.mode ?? null) : null;
  useEffect(() => {
    const pending = pendingLandingRef.current;
    if (!pending) return;
    /*
     * The re-read an upload asked for failed: the page says so (its error panel), and the landing is
     * dropped rather than left armed — armed, it would fire on some later, unrelated change of rows
     * and throw away the chip, view and open groups the operator has chosen since (review).
     */
    if (pending.after !== null && registerRead === 'error') {
      pendingLandingRef.current = null;
      return;
    }
    if (equipmentPanel !== 'ready' || !identifyModel) return;
    // Only on rows a READ answered: the panel stays ready through a refresh, and an optimistic
    // local write can replace the rows without one (Copilot review, PR #2077).
    if (registerRead !== 'ready') return;
    // After an upload: not until the re-read has replaced the rows the upload landed on.
    if (pending.after !== null && registerRows === pending.after) return;
    pendingLandingRef.current = null;
    // An upload that updated or left out rows lands on *From this import*: Rows, *All* (R11).
    landOn(
      fromImport ? { chip: 'all', view: 'rows' } : equipmentLanding(identifyModel, importMode),
    );
    landedUntouchedRef.current = true;
  }, [equipmentPanel, identifyModel, registerRows, registerRead, importMode, landOn, fromImport]);
  // The record settled as an adoption after the step landed: land on *Needs a look* (R11).
  useEffect(() => {
    if (importMode !== 'adopt' || !identifyModel || fromImport) return;
    if (!landedUntouchedRef.current || pendingLandingRef.current) return;
    if (equipmentChip === 'needs_a_look') return;
    const landing = equipmentLanding(identifyModel, importMode);
    if (landing.chip !== 'needs_a_look') return;
    landOn(landing);
  }, [importMode, identifyModel, equipmentChip, landOn, fromImport]);
  /*
   * A landing the register moved out from under. The step-open guess refresh (or any re-read) can
   * empty the chip a landing chose — older rows gaining stored guesses move from *To identify* to
   * *To confirm* — leaving "No assets match" on a step that has work. Until the operator chooses a
   * chip or a view themselves, an emptied landed chip lands again (Copilot review, PR #2077).
   */
  useEffect(() => {
    if (!landedUntouchedRef.current || pendingLandingRef.current || !identifyModel) return;
    if (equipmentPanel !== 'ready' || registerRead !== 'ready' || equipmentChip === 'all') return;
    if (equipmentChipCount(identifyModel, equipmentChip) > 0) return;
    // An emptied *Needs a look* lands again too: on To identify, To confirm or All (R11).
    landOn(equipmentLanding(identifyModel, importMode));
  }, [identifyModel, equipmentPanel, registerRead, equipmentChip, importMode, landOn]);
  const chooseEquipmentView = useCallback((view: 'rows' | 'groups') => {
    landedUntouchedRef.current = false;
    setFromImport(null);
    setEquipmentView(view);
  }, []);
  const equipmentBrandMix = useEquipmentBrandMix({
    active: section === SECTION.assets,
    twinId: twinId ?? null,
    ready: equipmentRowsShown && registerRead !== 'error',
    conflicted: twinIdentityConflict !== null,
    baseUrl: Config.middlewareApi,
    getToken: () => getTokenBounded(getAccessTokenRef.current),
    reloadRegister,
    writeSeq: registerWriteSeq,
  });

  /**
   * *Not Tracked* on a group still to identify (`equipment-page-landed`, R13–R14), from the group's
   * button or the identify card's *Not tracked* answer (`equipment-page-identify`, R13).
   *
   * With `ifUnidentified` (`equipment-page-identify`, R14): a row someone identified before this
   * write landed answers 409 *already identified* and keeps its product — reported, not failed, not
   * retried. Without it the mark would clear that match.
   *
   * One `PATCH twin-assets/:id` with `noCatalogueProduct: true` per row — there is no bulk route —
   * at most four in flight. The write is idempotent for a row still without a product (a row that
   * has one answers 409 and is not written), so a row whose outcome is unknown is safe to send
   * again; the register is re-read once
   * whatever happened, and that read, not the responses, decides what shows. Not transactional:
   * rows that were written stay written, and the caller is told which were not. A twin switch
   * mid-flight stops sending. Bounds: each attempt is `updateTwinAsset`'s 30 s, body phase
   * included; no automatic retry — *Try again* is the person's.
   */
  const markGroupNotTracked = useCallback(
    async (
      _groupKey: string,
      rowIds: number[],
    ): Promise<{
      written: number[];
      failed: number[];
      alreadyIdentified: number[];
      stale?: boolean;
    }> => {
      /*
       * The twin this callback belongs to — the render's, not whatever is on screen when it runs.
       * A *Try again* held open in a toast across a twin switch calls an old render's callback; it
       * must refuse rather than write the old twin's rows and re-read the old register under the new
       * twin's page (review: failure paths, security). The same rule `onUploaded` applies.
       */
      const issuedFor = twinId;
      if (issuedFor !== twinIdRef.current) {
        return { written: [], failed: rowIds, alreadyIdentified: [], stale: true };
      }
      const saveToken = beginRegisterWrite();
      const written: number[] = [];
      const failed: number[] = [];
      const alreadyIdentified: number[] = [];
      let lastStatus: number | null = null;
      let reason: string | null = null;
      const queue = [...rowIds];
      const worker = async () => {
        while (queue.length > 0) {
          if (twinIdRef.current !== issuedFor) {
            failed.push(...queue.splice(0));
            return;
          }
          const id = queue.shift() as number;
          try {
            const token = await getTokenBounded(getAccessTokenRef.current);
            // Again after the token: a twin switch while it was fetched must not send this row
            // either (Copilot review, PR #2077). The row goes back as unsent, with the rest.
            if (twinIdRef.current !== issuedFor) {
              failed.push(id, ...queue.splice(0));
              return;
            }
            await updateTwinAsset(Config.middlewareApi, token, id, {
              noCatalogueProduct: true,
              ifUnidentified: true,
            });
            written.push(id);
          } catch (error) {
            if (isAlreadyIdentifiedError(error)) {
              alreadyIdentified.push(id);
              continue;
            }
            failed.push(id);
            reason = error instanceof Error ? error.message : 'The row could not be marked.';
            const status = (error as { status?: unknown } | null)?.status;
            if (typeof status === 'number') lastStatus = status;
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, rowIds.length) }, worker));
      settleRegisterWrite(saveToken, failed.length === 0 ? { ok: true } : { ok: false, reason });
      const stale = twinIdRef.current !== issuedFor;
      // A twin switch is not a failure: rows left unsent then are not logged as refused ones.
      if (failed.length > 0 && !stale) {
        // Ids, counts and a status only. Not the group key: it is built from the customer's own
        // register text (manufacturer, model, name), which the coding standard keeps out of logs.
        console.warn('[equipment] not tracked failed', {
          twinId: issuedFor,
          rows: rowIds.length,
          failed: failed.length,
          firstFailedId: failed[0],
          status: lastStatus,
        });
      }
      if (!stale) reloadRegister({ afterWrite: true });
      // In the order asked, so a retry re-sends rows as the operator saw them.
      const order = new Map(rowIds.map((id, index) => [id, index]));
      const byOrder = (a: number, b: number) => (order.get(a) ?? 0) - (order.get(b) ?? 0);
      return {
        written: written.sort(byOrder),
        failed: failed.sort(byOrder),
        alreadyIdentified: alreadyIdentified.sort(byOrder),
        // Another twin is on screen now: its operator is told nothing about this one's rows.
        stale,
      };
    },
    [twinId, beginRegisterWrite, settleRegisterWrite, reloadRegister],
  );

  /*
   * The add-missing reconciliation reports its end through its own state, not its promise, so its
   * outcome reaches the *Draft saved* tag on the render after it finishes (R15). Its error is set
   * before the promise resolves, so by the render `addMissingSettledSeq` lands on, it is current.
   */
  const addMissingTokenRef = useRef<number | null>(null);
  const [addMissingSettledSeq, setAddMissingSettledSeq] = useState(0);
  useEffect(() => {
    if (addMissingSettledSeq === 0 || addMissingTokenRef.current === null) return;
    const token = addMissingTokenRef.current;
    addMissingTokenRef.current = null;
    settleRegisterWrite(
      token,
      addMissingError ? { ok: false, reason: addMissingError } : { ok: true },
    );
    // Deliberately keyed on the settle signal only: the error is read as it stood at that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMissingSettledSeq]);
  const addMissingTracked = useCallback(
    (entities: readonly DriftEntity[]) => {
      addMissingTokenRef.current = beginRegisterWrite();
      void addMissingRegisterRows(entities).finally(() => setAddMissingSettledSeq(n => n + 1));
    },
    [addMissingRegisterRows, beginRegisterWrite],
  );
  const coverage = useMemo(
    () => buildAssetCoverageIndex(registerJoinedLayout, registerRows, { planAbsent }),
    [registerJoinedLayout, registerRows, planAbsent],
  );
  /*
   * The Equipment table's ONE placement gate (`equipment-page-table`, R14): a row is kept when Floor
   * Plan's rule says it is on the plan OR the coverage index places it. The coverage rule is the
   * stricter — it counts a plan item with no refid as placed, which Floor Plan's index skips — so a
   * removal from the table can never send a row that is on the plan.
   */
  const equipmentIsPlaced = useCallback(
    (assetId: number) =>
      identifyModel?.planStateOf(assetId) === 'on_plan' ||
      coverage.placementsFor(assetId).length > 0,
    [identifyModel, coverage],
  );
  // *Remove* waits for the plan: both rules must be able to say where every row is (R14).
  const equipmentRemoveAvailable = coverage.planKnown && Boolean(identifyModel?.planKnown);
  /*
   * The selection in the order the open view shows it (`equipment-page-bulk-actions`, R2): the
   * same calls each view makes, with the same inputs the page hands each view. Computed only while
   * something is selected, so an empty selection costs nothing per render (NFR1).
   */
  const equipmentSelectionShown = useMemo(() => {
    if (equipmentSelection.selected.length === 0) return [];
    const groups = equipmentView === 'groups' && identifyModel !== null;
    const shown = equipmentShownOrder({
      view: groups ? 'groups' : 'rows',
      rows: registerRows,
      visibleIds: groups
        ? equipmentVisibleIds
        : equipmentFiltered
          ? equipmentVisibleIds
          : undefined,
      sort: equipmentSort,
      model: groups ? identifyModel : equipmentPanel === 'ready' ? identifyModel : null,
      fromImport: groups ? null : fromImport,
    });
    return selectionInShownOrder(equipmentSelection.selected, shown, registerRows);
  }, [
    fromImport,
    equipmentSelection.selected,
    equipmentView,
    identifyModel,
    registerRows,
    equipmentVisibleIds,
    equipmentFiltered,
    equipmentSort,
    equipmentPanel,
  ]);
  /*
   * How many rows *From this import* shows (R11), for the Rows view's head — the same rows the view
   * draws (`fromImportOrder`). Null while the override does not stand, or the Groups view is open.
   */
  const fromImportShownCount = useMemo(
    () =>
      fromImport && equipmentView === 'rows'
        ? fromImportOrder(
            registerRows,
            equipmentFiltered ? equipmentVisibleIds : undefined,
            fromImport,
          ).length
        : null,
    [fromImport, equipmentView, registerRows, equipmentFiltered, equipmentVisibleIds],
  );
  const [equipmentBulkBusy, setEquipmentBulkBusy] = useState(false);
  const equipmentPlanStateOf = useCallback(
    (id: number) => identifyModel?.planStateOf(id),
    [identifyModel],
  );
  // The same, read when called: a put back's Undo toast outlives the bar and the card, and must
  // see a row placed since on Floor Plan (this page, and its toasts, stay mounted across steps).
  const equipmentPlanStateRef = useRef(equipmentPlanStateOf);
  equipmentPlanStateRef.current = equipmentPlanStateOf;
  const equipmentLivePlanStateOf = useCallback(
    (id: number) => equipmentPlanStateRef.current(id),
    [],
  );
  const createRegisterAsset = useEquipmentCatalogStore(s => s.createRegisterAsset);
  const registerIdsRef = useRef(registerIds);
  registerIdsRef.current = registerIds;
  // The gate, as it stands NOW: nothing while the plan is unknown, never a placed row (R14).
  const removeGateRef = useRef((ids: readonly number[]): number[] => []);
  removeGateRef.current = ids =>
    equipmentRemoveAvailable ? ids.filter(id => !equipmentIsPlaced(id)) : [];
  const equipmentSelectionClearRef = useRef(equipmentSelection.clear);
  equipmentSelectionClearRef.current = equipmentSelection.clear;
  /** What the table's writes need from the page (`useEquipmentRowWrites`). */
  const equipmentWrites = useMemo<EquipmentWriteDeps>(
    () => ({
      twinId: twinId ?? null,
      currentTwinId: twinOnPage,
      baseUrl: Config.middlewareApi,
      getToken: () => getTokenBounded(getAccessTokenRef.current),
      begin: beginRegisterWrite,
      settle: settleRegisterWrite,
      reload: () => reloadRegister({ afterWrite: true }),
      discard: async ids => {
        if (!twinId) throw new Error('No twin is open.');
        return discardInBatches(await getTokenBounded(getAccessTokenRef.current), twinId, ids);
      },
      create: async ({ name, idempotencyKey }) => {
        const created = await createRegisterAsset({ name, idempotencyKey });
        // A manual add ends *From this import* (R11): the new row is not in the import's order, so
        // the re-read that follows would otherwise hide it.
        setFromImport(null);
        return created;
      },
      forgetWrites: ids => useEquipmentCatalogStore.getState().forgetRegisterWrites(ids),
      registerIds: () => registerIdsRef.current,
      // Read through a ref at press time: a toast's *Try again* outlives the render that raised it.
      removable: ids => removeGateRef.current(ids),
      onRetryRemoved: () => {
        equipmentSelectionClearRef.current();
        document.getElementById('equipment-main-title')?.focus();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `twinOnPage` is a stable useCallback([]) reading refs
    [twinId, beginRegisterWrite, settleRegisterWrite, reloadRegister, createRegisterAsset],
  );
  /**
   * The Launch Floor Plan block's *not yet placed* list (`launch-summary-detail`, R3) — the same
   * index, so it is the set the register table's *Not on plan* column marks. It replaced
   * `registerRows.length - placedItems.length`, which a Library-placed item or a twice-placed row
   * put out, and which had no read state at all. Every branch lives in `launchPlacementState`,
   * where a test can reach it: which read failed, and what a settled read with no plan means.
   */
  const launchPlacement = useMemo(
    () => launchPlacementState({ planState, registerRead, rows: registerRows, index: coverage }),
    [coverage, planState, registerRead, registerRows],
  );
  /**
   * The Launch step's *BI hierarchies* block (`launch-bi-gap-preview`, R2): each placed item's
   * catalogue reference by the lookup Go Live writes into `definitions.equipment_id`, asked of BI
   * while Launch is open. Who is placed is only known once the plan and the register are read, so
   * nothing is asked before then.
   *
   * ⚠ The items come from `registerJoinedLayout`, NOT `placedItems`. Go Live converts the plan
   * joined onto the register (`joinRegisterOntoPlan`, same options), and an adopted plan stores no
   * `assetUid` of its own — only the join links its machines to their register rows. Read raw,
   * every adopted item has no reference: on UGG-FIN all 142 placed items read as having none.
   */
  const referenceIdByAssetUid = useMemo(() => referenceIdsByAssetUid(registerRows), [registerRows]);
  const biInputs = biInputsOf(launchPlacement);
  const biPlacedItems = useMemo(
    () =>
      layoutCurrentDesigns(registerJoinedLayout)
        .flatMap(d => asArray(d?.items))
        .filter(isLive),
    [registerJoinedLayout],
  );
  const biReferences =
    biInputs === 'ready'
      ? biGapGroups(biPlacedItems, referenceIdByAssetUid, undefined).references
      : [];
  const launchBiCheck = useLaunchBiCheck({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
    tokenFailed,
    getToken: () => getTokenBounded(getAccessTokenSilently),
    references: biReferences,
    active: section === SECTION.launch,
  });
  /**
   * Why a row's placement is unknown, in the operator's terms — shared by Remove and the import undo,
   * so the two refusals cannot describe the same state differently. A FAILED read names the
   * remedy that can work (a reload); "has not loaded" would send them to wait for something that
   * is not coming.
   */
  const placementUnknownMessage = useCallback(
    (subject: string) =>
      planState === 'failed'
        ? `${subject} cannot be removed yet — the twin's plan could not be read, so we cannot ` +
          'tell whether it is placed. Removing a placed asset would leave the plan pointing at ' +
          'nothing. Reload the page to try again.'
        : `${subject} cannot be removed yet — the twin's plan has not loaded, so we cannot ` +
          'tell whether it is placed. Removing a placed asset would leave the plan pointing at ' +
          'nothing. Reopen the Equipment step once the plan has loaded.',
    [planState],
  );

  /**
   * Why each plan item that does not trace back to a register row does not
   * (`dgraph-drift-into-register`, epic ledger rows 80 and 84, ruling R19).
   *
   * **Gated on the Equipment step being open, and that gate is the point.** The Equipment list is
   * the only surface that renders this, and `registerJoinedLayout` changes per POINTER-MOVE while
   * a polygon vertex or a wall endpoint is being dragged on the Floor Plan step (`PolygonEditor`
   * and `WallEditor` both write the store from `onDragMove`). Measured at 159 µs per pass on a
   * 256-item plan, so it is not ruinous — but it is pure waste for a surface nobody is looking at,
   * and the same reasoning already put a cached index inside the join itself.
   *
   * Computed HERE rather than inside `AssetRegisterList` because `diagnosePlanRegisterDrift` needs
   * the register's WIRE rows: `JoinSourceRow` requires `twin_id` precisely so a lookalike cannot
   * assign to it, and the list's own `RegisterListAsset` is one of the lookalikes that guard
   * exists to reject. See that prop's comment.
   */
  const registerDrift = useMemo(
    () =>
      section === SECTION.assets
        ? diagnosePlanRegisterDrift(registerJoinedLayout, registerRows, twinBid)
        : null,
    [section, registerJoinedLayout, registerRows, twinBid],
  );

  /**
   * The customer's own asset register has not been imported yet, and pressing Add would pre-empt it
   * (`admin-planner-live-twin`, epic feature 39; Rob's ruling 2026-09-05).
   *
   * The rule lives in `@repo/front-end-state` so any surface that mounts the register says the same
   * thing about the same state. `onboardingMode` is already derived on this page from the twin's
   * `lifecycle_stage`, so this costs no extra read.
   *
   * Shown only when there is something the button would actually create: a warning about creating
   * rows is noise on a plan with none to create. It does NOT disable the button — a twin whose
   * customer has no register still needs the rows, and the failure being prevented is pressing in
   * the wrong ORDER rather than pressing at all.
   */
  const preImportWarning =
    section === SECTION.assets &&
    registerRead === 'ready' &&
    (registerDrift?.missingCount ?? 0) > 0 &&
    awaitingCustomerRegisterImport(onboardingMode, registerRows)
      ? preImportWarningText(registerDrift?.missing.length ?? 0)
      : null;

  /*
   * Uploads in flight. They gate *Undo This Import* (`equipment-page-undo-import`, R8): an undo
   * racing an upload (or an upload racing an undo) is the one ordering in which rows escape — an
   * upsert on a row deleted mid-request re-inserts it (`updateRows`' documented resurrection race).
   * `uploaderResetToken` tells the uploader to reopen for the next file after an undo lands.
   *
   * A COUNT, not a flag: after a remount two uploads can be open at once, and the first to end must
   * not re-enable the undo while the other is still upserting (Copilot, PR #2060). An end with no
   * start (a host that never saw it begin) cannot take it below zero.
   */
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const uploadInFlight = uploadsInFlight > 0;
  const endUpload = useCallback(() => setUploadsInFlight(n => Math.max(0, n - 1)), []);
  /** An undo is in flight: the uploader is disabled meanwhile. */
  const [importUndoing, setImportUndoing] = useState(false);
  const [uploaderResetToken, setUploaderResetToken] = useState(0);
  /** The undone upload's mode, which the uploader reopens in (R19). */
  const [uploaderResetMode, setUploaderResetMode] = useState<'customer' | 'adopt' | undefined>();

  /**
   * What this plan measures — the ONE rule, shared with the Observers stage and the complex-measures
   * editor (`entityCoverage.ts`, epic ledger row 82).
   *
   * ⚠ This replaced `new Set(savedObservers.map(o => o.targetRefid)).size` and a subtraction of it
   * from `placedItems.length + surfaces.length`. That was arithmetic over two set sizes rather than
   * a set operation, so an observer naming an entity that has LEFT the plan made it go negative —
   * which is what the `Math.max(0, …)` around it was really for. One such observer exists on the
   * staging twin this feature was measured against. The clamp hid the direction of the error
   * instead of fixing it.
   */
  const measurementCoverage = useMemo(
    () =>
      summarisePlanCoverage({
        layout: sessionLayout,
        observerCountsByRef: observerCountsFromLayout(sessionLayout),
        measures: planMeasures(sessionLayout?.composites),
      }),
    [sessionLayout],
  );
  /**
   * Shared with the Observers stage, so the two surfaces cannot disagree about what has landed.
   *
   * ⚠ **The plan is the other half of "knowable", and it is not covered by the live read.** Before
   * a twin's layout is in hand `sessionLayout` is `null` — and so it is whenever the store holds a
   * DIFFERENT twin's layout, which is every render between opening a twin and its plan arriving.
   * `liveReadState(null)` is `ready` by design (a twin being created from scratch has no live
   * configuration and its plan is the whole truth), so without this the coverage block would take
   * an empty plan for a complete one and render "everything is tracked", with a tick, over a plan
   * it has not read. That is the affirmative false claim the Failure States table forbids, reached
   * from the one direction the live read cannot see.
   */
  const coverageReadState = liveReadState(liveConfiguration);
  /**
   * How the PLAN itself is doing — the other half of "knowable", which the configuration read
   * cannot see.
   *
   * ⚠ **Absent is not loading, and getting that wrong spins for ever.** A twin may simply have no
   * saved plan, and `loadPlan` swallows its own error and leaves `sessionLayout` null — so treating
   * null as "still reading" would park the block on a spinner on exactly the paths where nothing is
   * coming. That is the same trap `liveReadState` closes for a failed configuration read, one level
   * out.
   */
  const coveragePlanState: 'loading' | 'absent' | 'present' = !sessionLayout
    ? planLoading
      ? 'loading'
      : 'absent'
    : // The layout is in hand but the twin's own configuration may not be ON it yet.
      liveConfigurationPlaced(liveConfiguration)
      ? 'present'
      : 'loading';
  /**
   * The twin as a composite line names it, resolved ONCE so the Launch list and Go Live's own
   * refusal cannot name the twin-level total two different ways.
   */
  const twinDisplayName = bidTwin?.name ?? twinBid ?? 'the whole twin';
  /**
   * The plan's composite measures, sorted into Launch's groups and named as the operator reads them
   * (`composites-page-launch-refusals`): every total the twin would refuse (the one thing that holds
   * Go Live), empty measures, unfinished ones and generated totals to review — each measure in at
   * most one (a finished one that nothing refuses is in none).
   *
   * ⚠ **The SAME verdicts the Composite measures step shows** (`compositeVerdicts.ts`), handed the
   * twin's name exactly as the step is (`refTwinName`), so a refused total reads the same sentence on
   * both screens. Two screens giving different answers about one measure is the defect this epic
   * exists to remove.
   *
   * ⚠ **Only while Launch is showing** (spec R8). The verdict pass walks the plan's containment, and
   * every plan edit in every other step would otherwise pay for a block nobody can see. Go Live asks
   * again at press time from the store, so nothing depends on this being computed early. `section`
   * rather than `isLaunchSection`, which is declared further down the component.
   *
   * ⚠ **Named by their TARGET**, because a total has no name of its own: it is the total of
   * something, and that something is what the operator recognises.
   */
  const launchShowing = section === LAUNCH_SECTION;
  const launchComposites = useMemo(
    () =>
      launchShowing
        ? launchCompositeGroups({
            layout: sessionLayout,
            twinName: twinDisplayName,
            twinBid,
            refTwinName: bidTwin?.name,
          })
        : NO_LAUNCH_COMPOSITES,
    [launchShowing, sessionLayout, twinDisplayName, twinBid, bidTwin?.name],
  );

  const coverageBlockProps = coverageBlock({
    summary: measurementCoverage,
    readState: coverageReadState,
    plan: coveragePlanState,
    unfinished: launchComposites.unfinished,
    nothingMeasured: launchComposites.nothingMeasured,
    refused: launchComposites.refused,
    refusalCheck: launchComposites.refusalCheck,
    empty: launchComposites.empty,
    review: launchComposites.review,
  });
  const [activationError, setActivationError] = useState<string | null>(null);
  const [activationNotice, setActivationNotice] = useState<string | null>(null);
  /*
   * Launch's BI step (`twin-launch-history-bi-push`): BI's spatial, then manufacturer, hierarchy
   * update, after the model and configuration are live. A Retry fetches a fresh token — by the time
   * someone presses it, the one Go Live held may have expired.
   */
  const biPush = useLaunchBiPush({
    middlewareApi: Config.middlewareApi,
    getToken: () => getTokenBounded(getAccessTokenSilently),
  });
  // The success summary's inputs while the BI banner waits on Retry or Continue. Null otherwise.
  const pendingSummaryRef = useRef<LaunchSummaryInputs | null>(null);
  // The person's Retry (once it succeeded) or Continue while its BI outcome is being saved
  // (`twin-launch-history-bi-outcome`, R3.8): the banner stays up, showing the failure it showed,
  // with the pressed button busy, until the summary opens.
  const [biOutcomeSaving, setBiOutcomeSaving] = useState<'retry' | 'continue' | null>(null);
  const [biSavingFailure, setBiSavingFailure] = useState<BiImportFailure | null>(null);
  // Entity activation that has already succeeded for the plan as it currently
  // stands. `activatePlan` is destructive (it versions, deletes and re-inserts
  // the whole tree), so a retry after an observer failure must resume at the
  // observer step rather than replay it. Cleared whenever the user leaves the
  // Launch stage to change the plan.
  // Also carries the model counts for the success summary, so a retry that resumes at the observer
  // step (skipping the activation block where `parsed`/`delta` are computed) can still report what
  // went live rather than "0 entities".
  const activatedRef = useRef<{
    refidToBid: Record<string, string>;
    entitiesLive: number;
    entitiesRemoved: number;
    /** Equipment in the activated tree — BI's manufacturer update links at most this many. */
    assetsLive: number;
    /** The activated entities' names by BID, to name what BI did not fully match. */
    assetNameByBid: ReadonlyMap<string, string>;
    /** UPDATE-mode field-level diff (live-before vs new) for the success modal's "View changes". */
    modelDiff: ModelDiff | null;
    /** The model that was live before this activation — the "before" of the model comparison. */
    prevModel: Entity | null;
    /**
     * The renumberings captured at step 2b (`floor-plan-page-launch-bid-changes`, R11, R15). Carried
     * so a resume files EXACTLY what the first press would have, rather than rebuilding the record
     * from the plan — which can differ once the model has been replaced (review, 2026-10-17).
     */
    codeChanges: EntityCodeChange[];
    /** The org the twin lives in, resolved during activation — needed to re-read the "after" model. */
    orgBID: string;
    /**
     * The configuration that was running when the operator was shown what they would lose, or
     * `null` when nothing was running.
     *
     * Carried across a resume so the guard immediately before activation can tell whether the
     * twin is still measuring what the statement was computed against — see step 6c. The
     * pre-flight itself is skipped on a resume, so without this the retry has no memory of what
     * was agreed.
     */
    statedAgainstConfigId: number | null;
    /**
     * The launch history's idempotency key and the dGraph copies `activatePlan` took, carried
     * across a resume so the launch recorded after a retried filing is the SAME launch, with the
     * copies it owns (spec twin-launch-history-record, R2.2).
     */
    launchUuid?: string;
    modelCopySuffix?: string | null;
    sensorCopySuffix?: string | null;
  } | null>(null);
  // Generation counter for the above. The nav is freely clickable, so a user can
  // leave Launch while `handleGoLive` is still awaiting — and the in-flight run
  // would then repopulate `activatedRef` *after* the section-change effect
  // cleared it, leaving a stale `refidToBid` that a later Go Live would reuse to
  // link observers against entity BIDs the plan no longer mints. Bumping this on
  // every exit from Launch lets a superseded run recognise itself and write
  // nothing.
  const activationRunRef = useRef(0);
  // Re-entrancy guard, independent of the `activating` UI state.
  //
  // `activating` disables the Go live button and, with the run's phase, drives the launch Modal's
  // activating content. Launch's confirmation deliberately clears it (nothing destructive has run, so
  // the wizard should not look locked while it asks a question). Between Yes and `setActivating(true)`
  // re-locking, the Modal stays up (the hook holds the activating content) over a briefly enabled
  // button; between Cancel and the run's `finally` there is a render with no Modal and an enabled
  // button. A second run started in either window would share this run's `runId`, so `superseded()`
  // would not catch it and both could reach `activatePlan`.
  const goLiveInFlightRef = useRef(false);
  /**
   * Forget the last Go Live run: a previously-successful entity activation no longer describes what
   * would be activated next once the plan may change. Bumping the run id also invalidates any Go Live
   * still in flight (see `activationRunRef`).
   */
  const forgetLaunchRun = useCallback(() => {
    activationRunRef.current += 1;
    activatedRef.current = null;
    setActivationError(null);
    setActivationNotice(null);
    // A BI step waiting on Retry or Continue belonged to that launch: the summary it held is gone.
    pendingSummaryRef.current = null;
    biPush.reset();
    setBiOutcomeSaving(null);
    setBiSavingFailure(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `biPush.reset` only; the `biPush` object is new every render
  }, [biPush.reset]);
  // Leaving Launch means the plan may change.
  useEffect(() => {
    if (section !== LAUNCH_SECTION) forgetLaunchRun();
  }, [section, forgetLaunchRun]);
  // So does a swap, which Versions can make with Launch on screen (`twin-details-page-versions`;
  // Copilot review): a run that activated and then failed to file must not lend its entity mapping
  // to the next Go Live of a different plan.
  useEffect(() => {
    if (draftGeneration > 0) forgetLaunchRun();
  }, [draftGeneration, forgetLaunchRun]);

  // Completion per section.
  //
  // The rule itself lives in `registerCompletion.ts` so it can be tested without mounting this
  // page (`generic-match-status`, ruling R8) — and so that "a generic placement does not un-tick
  // the step" is a property a named test asserts rather than a claim a PR made. Behaviour is
  // unchanged for `auto`/`manual`/`unmatched`: the counts below are the same two numbers.
  // Decided with NO product (`uncatalogued-terminal-state`, ledger row 119) is neither awaiting nor
  // matched — so "matched" below stays a claim about a catalogue product.
  //
  // The rows behind the Launch counts (`launch-summary-detail`, R6), built ONCE per register and
  // counted from, so the Equipment step's tick, the Launch sentence and the Launch list read one
  // value rather than three passes that could drift.
  const launchAssets = useMemo(() => launchAssetLists(registerRows), [registerRows]);
  const unknownAssets = launchAssets.unknown.length;
  const matchedAssets = registerRows.length - unknownAssets - launchAssets.uncatalogued.length;
  /**
   * Did the latest upload leave lines unwritten (`equipment-page-reimport`, R14)?
   *
   * The register read alone cannot answer this — a refused line simply is not there, so
   * `registerRows.length > 0 && unknownAssets === 0` is satisfied by a HALF-written file and the
   * step would tick green while the operator's data is missing. It is the latest live import
   * record's `refused` count, so it survives a reload, and undoing that import or uploading again
   * clears it. `'unknown'` until the record is read, or when its read failed: the step does not
   * tick on a read that never answered. A read that found no live record is 0. A record without
   * `refused` at all (an older middleware) is `'unknown'` too, never "none refused".
   */
  const lateRefusals: number | 'unknown' =
    importSummary.status !== 'read'
      ? 'unknown'
      : !latestImport
        ? 0
        : latestImport.refused === undefined
          ? 'unknown'
          : (latestImport.refused?.length ?? 0);
  const assetsComplete = equipmentStepComplete({
    totalAssets: registerRows.length,
    awaitingResolution: unknownAssets,
    lateRefusals,
  });

  // Grey "skipped" tick for Equipment: set when the operator advances past the step with an empty
  // register (the "Skip" press). `assetsComplete` (green) takes precedence in the nav, and the
  // `registerRows.length === 0` guard on the nav item drops the grey tick the moment any asset is added.
  const [assetsSkipped, setAssetsSkipped] = useState(false);

  /**
   * Is the register KNOWN to be empty, as opposed to unread?
   *
   * `registerRows` is `[]` before the first read resolves and after one fails, as well as when the
   * twin genuinely has no assets. The bar's Skip label (since removed), the grey skipped tick and the Launch summary all
   * used to read `length === 0` and say "empty" for all three — the same affirmative claim the
   * read-failure sentence exists to replace, on the three surfaces that were not looking at it.
   */
  const knownEmpty = registerKnownEmpty(registerRead, registerRows.length);

  /**
   * Does the Equipment step show its START SCREEN — the drop zone, the tiles and the links
   * (`equipment-page-start`, R1)? Exactly while:
   *
   *  - a twin is loaded, and the rows on screen are this twin's;
   *  - the register is KNOWN to be empty: a read that has answered with no rows. A read in flight
   *    or one that failed never shows it — "drop the asset register here" over a register we could
   *    not read would invite a second upload on top of rows that exist (Failure States);
   *    An upload that wrote nothing — every line refused late — lands here too, as after any undo:
   *    the start screen says the summary's late-refusal line (`equipment-page-reimport`, R12, R14).
   *  - the plan holds no equipment the register is missing. A twin whose plan was built first —
   *    a live twin coming through onboarding, or one built on Floor Plan from the Library — has
   *    the register list's *Add N to the register* (and the warning to import the customer's file
   *    first) as its way in, which frame 01 does not draw. Those twins keep today's screen; the
   *    start screen is frame 01's route A, a new twin with nothing yet (plan deviation, recorded
   *    in `review-equipment-page-start.md`).
   */
  const equipmentStart =
    // Only ON the Equipment step: `registerDrift` is not worked out anywhere else, so off the step
    // its term would read as "nothing missing" for every twin (review).
    section === SECTION.assets &&
    Boolean(twinId) &&
    registerRowsAreThisTwins &&
    knownEmpty &&
    (registerDrift?.missingCount ?? 0) === 0;

  /*
   * What the Equipment step last showed ON the step, held while it fades out
   * (`onboarding-persistent-scene-panel-fades`): the three are worked out only on the step, so off it
   * the fading step would swap its start screen for the register and drop its warnings mid-fade.
   */
  const equipmentOnStep = { start: equipmentStart, drift: registerDrift, preImportWarning };
  const [equipmentHeld, setEquipmentHeld] = useState(equipmentOnStep);
  if (
    section === SECTION.assets &&
    (equipmentHeld.start !== equipmentStart ||
      equipmentHeld.drift !== registerDrift ||
      equipmentHeld.preImportWarning !== preImportWarning)
  ) {
    setEquipmentHeld(equipmentOnStep);
  }

  /*
   * Focus the start screen hands on, once what replaces it has rendered (review): a keyboard
   * operator who pressed *Upload CSV* or saved *Add an Asset* on the start screen would otherwise
   * be left on <body>, because the control they pressed goes with the start screen.
   *
   *  - after the first upload lands: *Upload Updated Register* on the file card, or the
   *    uploader's own disclosure toggle where no file card is drawn;
   *  - after an asset added by hand: the table's *Add an asset* (`data-register-add`).
   */
  /*
   * The register's own write messages: the reconciliation's when it has something to say, else
   * this page's. Named once, because the register list and the start screen that replaces it both
   * say them and must pick the same one (review).
   */
  const registerActionError = addMissingError ?? assetWriteError;
  const registerActionNotice = addMissingNotice ?? assetWriteNotice;

  const focusAfterStartUploadRef = useRef(false);
  const focusAfterStartAddRef = useRef(false);
  useEffect(() => {
    if (focusAfterStartUploadRef.current) {
      /*
       * The upload has landed once rows are shown — or once the record says every line was refused,
       * which saves none (Copilot, PR #2148 round 4; read from the record since
       * `equipment-page-reimport`, R14).
       */
      // Only once the re-read has SETTLED empty: a file that saved some rows and refused others
      // reads as "refusals outstanding, no rows" while that read runs, and the rows it brings
      // replace the toggle this would focus (Copilot, PR #2152).
      const allRefused = knownEmpty && (latestImport?.refused?.length ?? 0) > 0;
      if (!equipmentRowsShown && !allRefused) return;
      /*
       * REPAIR LOST FOCUS ONLY. A slow upload outlives the press: an operator who has tabbed on —
       * to the step list — keeps their place; only focus that went with the
       * start screen is handed on (Copilot, PR #2148 round 4).
       */
      const active = document.activeElement;
      const lost = !active || active === document.body || !active.isConnected;
      if (!lost) {
        focusAfterStartUploadRef.current = false;
        focusAfterStartAddRef.current = false;
        return;
      }
      // An upload that wrote nothing leaves the start screen up (R14): its *Choose File* takes focus.
      const target =
        uploadToggleRef.current ??
        document.querySelector<HTMLElement>('.import-controls .import-bar button') ??
        (allRefused
          ? document.querySelector<HTMLElement>('[data-start] .tl-dz-actions button')
          : null);
      if (!target) return;
      focusAfterStartUploadRef.current = false;
      focusAfterStartAddRef.current = false;
      target.focus();
      return;
    }
    if (!equipmentRowsShown) return;
    if (focusAfterStartAddRef.current && !addingAsset) {
      /*
       * A VISIBLE target only: the Rows view's *Add an asset* sits in a wrapper the stage hides while
       * Groups shows (a view kept from before, or chosen by a landing), and focusing a hidden control
       * strands focus as surely as <body> does (Copilot, PR #2148). The main panel's heading —
       * focusable by script, there in both views — takes it instead.
       */
      const add = Array.from(document.querySelectorAll<HTMLElement>('[data-register-add]')).find(
        el => !el.closest('[hidden]'),
      );
      const target = add ?? document.getElementById('equipment-main-title');
      if (!target) return;
      focusAfterStartAddRef.current = false;
      target.focus();
    }
  }, [equipmentRowsShown, equipmentFiltered, addingAsset, equipmentView, knownEmpty, latestImport]);

  const planDrawn = layoutHasGeometry(sessionLayout);
  const planComplete = planDrawn;

  /*
   * The Floor Plan step's *manufacturer identified* score, for the step list (`floor-plan-page-checks`,
   * R7; frame 02, note 1). Kept for the life of the page so the step list still shows it on the other
   * steps, and keyed by twin so another twin's number never shows. Only a score the model could
   * actually work out: the register read, the checks built, and something to score.
   */
  const [planScore, setPlanScore] = useState<PlanScore | null>(null);
  const handlePlanChecks = useCallback(
    (checks: PlanChecks | null) => setPlanScore(prev => nextPlanScore(prev, checks, twinId)),
    [twinId],
  );
  /*
   * Off the Floor Plan step the planner is unmounted and reports nothing, so the kept score follows the
   * page's own identify model — the same score, over the same plan — as register edits change it
   * (Copilot review, round 3). Held while the register is re-reading; cleared once it cannot be read,
   * as the Checks model's own gate would.
   */
  useEffect(() => {
    if (section === SECTION.plan || registerRead === 'loading') return;
    setPlanScore(prev =>
      syncPlanScore(prev, registerRead === 'ready' ? identifyModel : null, twinId),
    );
  }, [section, registerRead, identifyModel, twinId]);

  const linkedCameras = cameras.filter(c => c.rtspEndpointId != null).length;
  /*
   * The tick no longer requires a placed camera — see `feedsCompletion.ts` for the rule and why it
   * changed. `cameras.length > 0` made placement a precondition, which is exactly what this epic
   * removes: on staging the only two twins with a saved plan hold zero cameras between them,
   * against 14 and 8 live streams, so the step could never tick on either.
   */
  const cameraFeedsComplete = feedsComplete(liveConfiguration.streams, cameras);

  const observersComplete = savedObservers.length > 0;

  /*
   * The Composite measures stage, which edits the PLAN.
   *
   * Its tick is the plan's own count rather than a number the child reports back, and its
   * enablement is the plan's own entities rather than the twin's configuration: a complex measure
   * totals things, so a plan with nothing on it has nothing to total. `liveEntityRefids` is the
   * same definition the live-configuration re-match guard uses for "the plan's entities", so the
   * stage and the loader cannot disagree about whether a plan has any.
   */
  const planComposites = asArray(sessionLayout?.composites).filter(isLive);
  const compositesComplete = planComposites.length > 0;
  const compositeStageDisabledReason = !sessionLayout
    ? 'Draw the floor plan first'
    : liveEntityRefids(sessionLayout).length === 0
      ? 'Add zones or equipment to the floor plan first'
      : undefined;
  const setPlanComposites = useCallback(
    (next: FloorPlannerComposite[]) => useFloorplanStore.getState().setComposites(next),
    [],
  );
  const latestPlanComposites = useCallback(
    () => asArray(useFloorplanStore.getState().layout?.composites),
    [],
  );

  /*
   * ⛔ **GENERATED PARENT-SPACE TOTALS** (`complex-measures-in-configuration-parent-space-composites`,
   * epic ledger rows 177–181). Every space with child spaces, no cameras of its own and no hand-made
   * total gets a total of its children, kept in step with the plan on every change — and on an
   * adopted twin the first pass fills in the totals its configuration was missing, which is the
   * point (Rob, 2026-10-01).
   *
   * ⚠ **Only once the twin's running configuration has SETTLED.** Its own totals arrive on the plan
   * through the composite match; generating before they land would put a generated total beside a
   * hand-made one on the same target, which Launch Twin then refuses as a duplicate. Settled is: no
   * configuration at all (a twin never taken live — absent, not failed), or the statement read and
   * matched. The match writes the store synchronously inside the hook's effect, and this effect is
   * declared after the hook, so in the commit where `compositeMatch` is set the store already holds
   * the merged totals.
   *
   * ⚠ **A FAILED read pauses generation**, and the stage says so. Paused is not "nothing to do": the
   * configuration's totals are unknown, so any total generated now could be a duplicate.
   *
   * The write happens only when the pure function returns a DIFFERENT array, which is what stops
   * reconcile → write → reconcile.
   */
  // Read defensively: a host or test double may hand the page a partial read, and a missing field
  // must mean "not settled", never a crash.
  const liveConfigRead = liveConfiguration?.config;
  const liveStatementRead = liveConfiguration?.statement;
  const generationPaused =
    liveConfigRead?.status === 'failed' || liveStatementRead?.status === 'failed';
  /*
   * ⚠ **The statement must be the CURRENT configuration's.** On a re-read the configuration goes
   * back to `loading` while the previous statement and match stay `ready`, so a gate on the
   * statement alone stays open over a configuration whose totals are not on the plan yet — and a
   * total generated in that window could sit beside one of them (review finding).
   */
  const generationSettled =
    !generationPaused &&
    (liveConfigRead?.status === 'none' ||
      (liveConfigRead?.status === 'ready' &&
        liveStatementRead?.status === 'ready' &&
        liveStatementRead.value?.configId === liveConfigRead.id &&
        liveConfiguration?.compositeMatch != null));
  const pausedReason =
    liveConfigRead?.status === 'failed'
      ? liveConfigRead.reason
      : liveStatementRead?.status === 'failed'
        ? liveStatementRead.reason
        : null;
  useEffect(() => {
    if (pausedReason !== null) {
      console.warn(
        '[parent-space-composites] reconciliation paused: configuration read failed',
        pausedReason,
      );
    }
  }, [pausedReason]);

  /** FR10: what the session's first settled reconciliation — the adoption pass — added, said once. */
  const firstAddAnnounced = useRef(false);
  const [generatedAdded, setGeneratedAdded] = useState<FloorPlannerCompositeRef[] | null>(null);
  const clearGeneratedAdded = useCallback(() => setGeneratedAdded(null), []);
  /*
   * `twinBid` is a search param, so a twin change re-renders without remounting: re-enter "nothing
   * announced" (the `setBidTwin(null)` discipline), or the previous twin's unread announcement is
   * shown under this one's name and its closed window stops this twin's own adoption pass (PR #1852
   * review). Declared before the reconciliation effect so a commit that runs both resets first.
   */
  useEffect(() => {
    firstAddAnnounced.current = false;
    setGeneratedAdded(null);
  }, [twinBid]);

  useEffect(() => {
    if (!generationSettled || !sessionLayout) return;
    // The store at THIS moment, not the render's copy — but only while it still holds this
    // session's plan, the same guard `sessionLayout` applies.
    const state = useFloorplanStore.getState();
    if (state.twinBID !== sessionId) return;
    const current = state.layout;
    if (!current) return;
    const before = asArray(current.composites);
    const next = reconcileGeneratedComposites(current, { newRefid: newCompositeRefid });
    /*
     * FR10 announces the ADOPTION pass — the session's first settled reconciliation — and nothing
     * after it. Closing the window here, whether or not that pass added anything, is what stops a
     * deleted-then-regenerated total from being announced later as "this twin was not adding up"
     * (review finding: the regenerated total arrived as the session's first add).
     */
    const isAdoptionPass = !firstAddAnnounced.current;
    firstAddAnnounced.current = true;
    if (next === before || (next.length === 0 && before.length === 0)) return;
    // Added means a TARGET that had no generated total before — a total's identity is what it
    // totals (the reload merge's own rule), never its refid, which is minted and can repeat.
    const generatedBefore = new Set(
      before.filter(c => isLive(c) && c.generated === true).map(c => encodeRef(c.target)),
    );
    const addedTargets = next
      .filter(c => isLive(c) && c.generated === true && !generatedBefore.has(encodeRef(c.target)))
      .map(c => c.target);
    /*
     * The plan changed with nobody pressing anything, so it is NOT marked unsaved and nothing asks
     * for a save (`plan-save-version-check`, R9): merely opening the wizard must not write a plan,
     * because a stale tab's open-time write is what overwrote a colleague's work. The totals ride
     * the person's next save, and always Go Live's pre-flight save.
     */
    useFloorplanStore.getState().setComposites(next, { keepDirty: true });
    if (isAdoptionPass && addedTargets.length > 0) setGeneratedAdded(addedTargets);
  }, [generationSettled, sessionId, sessionLayout]);

  // Complete once the twin has a usable position and nothing is unsaved. A dirty form is NOT
  // complete: the tick would otherwise promise something the twin record does not yet hold. Off the
  // step its last report is stale (*Next* never waits for a save, R7.1), so the page-level queue
  // says what is still unsaved.
  const twinDetailsComplete =
    twinDetails.located &&
    !(section === SECTION.twinDetails ? twinDetails.dirty : twinDetailsSave.pending);

  // Two entities sharing an entity code mint one dGraph bID and merge into a single entity at
  // activation — silently, and the loser then shows up as a "removal" on every later Go Live. The
  // planner marks the offending field red; saving or activating a plan in that state is blocked
  // outright, because the damage is done at conversion time and is invisible afterwards.
  // `twinBid` (the wizard's `?twinBid=`), NOT the floorplan store's `twinBID` — this wizard keys
  // the store on `sessionId`, so the store's value is a session id and prefixing an identity with
  // it would mint a BID no twin has ever had. Same trap `OnboardingPlanner`'s prop doc warns about.
  const duplicateCodes = findDuplicateEntityCodes(sessionLayout, twinBid).codes;
  const hasDuplicateCodes = duplicateCodes.size > 0;
  const duplicateCodeHint = hasDuplicateCodes
    ? `Duplicate entity code${duplicateCodes.size === 1 ? '' : 's'}: ${Array.from(duplicateCodes)
        .sort()
        .join(
          ', ',
        )}. Two entities cannot share a code — open the Floor Plan step and give each one its own.`
    : undefined;

  // Launch unlocks as soon as there's at least one observer — reaching that
  // point means the plan, a camera feed and an observer all exist, which is
  // enough for a usable twin. The per-section ticks still reflect full
  // completion; the Launch summary flags anything skipped (e.g. no equipment).
  const canLaunch = observersComplete;

  /** What each pre-Launch step's item shows. The ORDER comes from `STEPS`, never from here. */
  const stepNav: Record<PreLaunchStepId, Omit<OnboardingNavItem, 'id' | 'label'>> = {
    twinDetails: { complete: twinDetailsComplete },
    // The launch rule's own answer (`launch-gate`'s predicate): green exactly when the hours let the
    // twin launch, and refreshed by every save the step makes (`twin-details-page-hours-week`).
    openingHours: { complete: openingHoursReady(launchHours.state) },
    assets: {
      complete: assetsComplete,
      skipped: assetsSkipped && knownEmpty,
    },
    plan: {
      complete: planComplete,
      trailing: planScoreShown(planScore, twinId),
      trailingWords: 'manufacturer identified',
    },
    cameras: { complete: cameraFeedsComplete },
    observers: { complete: observersComplete },
    compositeMeasures: {
      complete: compositesComplete,
      disabled: compositeStageDisabledReason !== undefined,
      disabledReason: compositeStageDisabledReason,
    },
    // Never complete and never disabled: a placeholder, grey-ticked once seen (frame 10).
    // Its number, 8, from `STEPS`' order (`onboarding-persistent-scene-position-twin`, R1.2).
    positionOnMap: { placeholder: true, skipped: positionSeen },
  };
  const navItems: OnboardingNavItem[] = [
    ...STEPS.map(step => ({ id: step.id, label: step.title, ...stepNav[step.id] })),
    {
      id: SECTION.launch,
      label: launchLabel,
      isLaunch: true,
      icon: STEP_ICONS.launch,
      disabled: !canLaunch || hasDuplicateCodes,
    },
  ];

  const {
    persistPlan,
    resetDraftMarker,
    saveStampedCodes,
    saveRetiredCodes,
    saveActivatedPlan,
    flushForSwap,
    registerPendingWriteFlush,
    currentLayoutOf,
    stampFirstEdit,
    underlayImages,
    draft,
  } = usePlanSave({
    load: planLoad,
    accessToken,
    twinBid,
    sessionId,
    draftSaveEnabled: DRAFT_SAVE_SECTIONS.has(section),
    duplicateCodeHint,
    goLiveInFlightRef,
    observerEditPendingRef,
    modifiedByRef,
    getAccessTokenSilently,
  });

  /**
   * *Restore dropped draft* (R4.4). Unsaved work is saved first — restoring over it would drop it
   * without putting it in the slot — and a save in flight is waited for, so the stamp the swap
   * compares is the one the server holds. The hook's busy state covers all of it.
   */
  const { restore: restoreDropped, loadLaunch } = draftChoice;
  /** The twin chip, which opens Versions — where focus lands after a swap made from it. */
  const chipRef = useRef<HTMLButtonElement | null>(null);
  /**
   * Focus the chip once no dialog is up. A dialog makes the rest of the page inert, and a swap's
   * continuation runs before React has unmounted it, so focusing at once would land nowhere.
   */
  const [focusChipPending, setFocusChipPending] = useState(false);
  /** Versions, off the chip (`twin-details-page-versions`), and the button to focus when it opens. */
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsFocus, setVersionsFocus] = useState<VersionsFocus | null>(null);
  /** The *Discard Draft* confirmation. */
  const [discardOpen, setDiscardOpen] = useState(false);
  const handleRestoreDropped = useCallback(async () => {
    const planIdNow = planIdRef.current;
    if (planIdNow == null) return;
    await restoreDropped({
      planId: planIdNow,
      planName: useFloorplanStore.getState().layout?.name || PLAN_NAME,
      flush: flushForSwap,
      currentStamp: () => planModifiedAtRef.current,
    });
    // Restore sits in Versions (`twin-details-page-versions`). Focus returns to the chip once the
    // restoring dialog has gone — it makes the page inert while it is up, so focusing now does nothing.
    setFocusChipPending(true);
  }, [restoreDropped, flushForSwap, planIdRef, planModifiedAtRef]);

  /** The launch the *Load as draft* confirmation is about; null while it is closed. */
  const [loadTarget, setLoadTarget] = useState<TwinLaunchSummary | null>(null);
  const { clearLoadError } = draftChoice;
  const openLoad = useCallback(
    (launch: TwinLaunchSummary) => {
      clearLoadError();
      setLoadTarget(launch);
    },
    [clearLoadError],
  );
  /** Cancel on the Load confirmation: back to Versions, which it was opened from (R2.3). */
  const closeLoad = useCallback(() => {
    clearLoadError();
    setLoadTarget(null);
    setVersionsOpen(true);
  }, [clearLoadError]);
  // A different twin, in place: a confirmation about the last twin's launch says nothing about this
  // one, and neither does its Versions.
  useEffect(() => {
    setLoadTarget(null);
    setVersionsOpen(false);
    setDiscardOpen(false);
  }, [twinBid, sessionId]);

  /**
   * The twin's launch history, read at page level whenever the twin is known
   * (`twin-details-page-landing`, R4): it decides the route — First launch or Updating live twin —
   * whichever step the wizard opens on, and it feeds Twin details' panel (`twin-launch-history-panel`).
   * Enabled once the token's outcome is known — a token, or `tokenFailed` — so the hook's own "cannot
   * be made → failed" answer (the panel's failed state, with Reload) is given for a refused token,
   * and never for the moment before the token arrives.
   */
  const launchHistory = useLaunchHistory({
    middlewareApi: Config.middlewareApi,
    accessToken,
    twinId,
    enabled: !twinIdentityConflict && (!!accessToken || tokenFailed),
    readKey: launchHistoryKey,
  });
  /*
   * The launch record, from the most recent READY read for this twin. A re-read that is loading or
   * has failed keeps the last ready answer, so the chip does not flicker on every visit to Twin
   * details, and a live twin is `migrate` or `manage` only on an answer actually read — never on a
   * failure, which leaves it `unknown` (`live-unread`).
   */
  const lastReadyLaunchRecordRef = useRef<{ twinId: number; read: LaunchRecordRead } | null>(null);
  if (launchHistory.state === 'ready' && launchHistory.twinId === twinId) {
    lastReadyLaunchRecordRef.current = {
      twinId: launchHistory.twinId,
      read: launchHistory.history.productionLaunchUuid !== null ? 'present' : 'absent',
    };
  }
  const launchRecordRead: LaunchRecordRead =
    lastReadyLaunchRecordRef.current !== null && lastReadyLaunchRecordRef.current.twinId === twinId
      ? lastReadyLaunchRecordRef.current.read
      : 'unknown';
  useEffect(() => {
    if (launchHistory.state !== 'failed') return;
    // eslint-disable-next-line no-console
    console.warn('[onboarding] launch history read failed', { twinId });
  }, [launchHistory.state, twinId]);
  /** The twin's route (R1), for the chip: null until the twin read has settled with a twin. */
  const route =
    bidTwinSettled && bidTwin
      ? resolveOnboardingRoute({
          lifecycleStage:
            graphSavedFor !== null && graphSavedFor === twinBid
              ? TwinLifecycleStageValues.GraphSaved
              : bidTwin.lifecycle_stage,
          launchRecord: launchRecordRead,
        })
      : null;
  /*
   * The organisation the twin BELONGS TO, for the start screen's *Uploading to* target (R4) — never
   * the operator's selected one. Read only while the start screen shows.
   */
  const twinOrganisationName = useTwinOrganisationName({
    // `undefined` while the twin on screen is not the one read (a switch in flight), `null` when it
    // is known and has no organisation (logged once by the hook).
    organisationId:
      bidTwin && bidTwin.id === twinId ? (bidTwin.organisation_id ?? null) : undefined,
    twinBid,
    active: equipmentStart,
    baseUrl: Config.middlewareApi,
    getToken: () => getAccessTokenRef.current(),
  });
  /*
   * The other twins of the organisation the twin BELONGS TO, for the Opening hours step's *Copy
   * another twin* (spec twin-details-page-hours-start, R4.1). Read only while that step shows its
   * start panel: the step's read is in, and no standard covers the twin's today or later.
   */
  const hoursToday =
    hoursEditor.read.read === 'ready' ? hoursEditor.read.hours.launchHours.today : null;
  const organisationTwins = useOrganisationTwins({
    // `null` once the twin's record read has settled without this twin: absent, said as failed —
    // never a loading row with no end.
    organisationId:
      bidTwin && bidTwin.id === twinId
        ? (bidTwin.organisation_id ?? null)
        : bidTwinSettled
          ? null
          : undefined,
    twinBid,
    active:
      section === SECTION.openingHours &&
      hoursToday !== null &&
      startPanelShows(hoursEditor.versions, hoursToday),
    baseUrl: Config.middlewareApi,
    getToken: () => getAccessTokenRef.current(),
  });
  /** The start screen's target line: the twin, then *{organisation} · {route}* (R4). */
  const equipmentStartTarget = {
    // Only the twin on screen's own name: a previous twin's record must never sit beside
    // *Uploading to* while a switch is in flight (review).
    name: (bidTwin && bidTwin.id === twinId ? bidTwin.name : null) || twinBid || '',
    label: 'Uploading to',
    icon: <Icon name="cube" variant="solid" size="xs" />,
    detail: [twinOrganisationName, routeChipLabel(route)].filter(Boolean).join(' · ') || undefined,
  };
  /**
   * The Equipment step's Skip — the start screen's *Skip, and build it on Floor Plan from the
   * Library* (R6): skipping an empty register grey-ticks the step. Otherwise a person moves on
   * through the step list (`onboarding-assets-search-in-panel`, R6).
   */
  const skipOrAdvance = () => {
    setAssetsSkipped(knownEmpty);
    setSection(nextStep(section));
    // The pressed Skip goes with the step; land on the next step's heading, as *Use
    // production* does, rather than on <body> (review).
    requestAnimationFrame(() => stepHeadingRef.current?.focus());
  };
  /*
   * This twin's history, as last read ready. A re-read (opening Versions asks for one) keeps the last
   * answer on screen meanwhile, as the route does, so the chip, Discard and the pills do not flicker,
   * and a swap names production as the person was shown it (the server refuses it if a newer launch
   * has completed since).
   */
  const lastReadyHistoryRef = useRef<{ twinId: number; history: TwinLaunchHistoryResponse } | null>(
    null,
  );
  if (launchHistory.state === 'ready' && launchHistory.twinId === twinId) {
    lastReadyHistoryRef.current = { twinId: launchHistory.twinId, history: launchHistory.history };
  }
  const historyReady =
    lastReadyHistoryRef.current !== null && lastReadyHistoryRef.current.twinId === twinId
      ? lastReadyHistoryRef.current.history
      : null;
  const productionUuid = historyReady?.productionLaunchUuid ?? null;
  /** Production's row in the history, when it is known (Versions' Discard, the chooser below). */
  const productionLaunch =
    historyReady && productionUuid
      ? (historyReady.launches.find(l => l.launchUuid === productionUuid) ?? null)
      : null;
  const productionCompletedAt = productionLaunch?.completedAt ?? null;

  /**
   * *Load as draft*, confirmed (R4): unsaved work saved first, then the launch's plan swapped in as
   * the draft. Closes the confirmation and returns focus to the twin chip when it swapped; a
   * refusal stays in the confirmation, and a swap that may have landed blocks until a reload.
   */
  const handleLoadLaunch = useCallback(async () => {
    const planIdNow = planIdRef.current;
    if (!loadTarget || planIdNow == null || !productionUuid) return;
    const outcome = await loadLaunch({
      launch: loadTarget,
      productionUuid,
      planId: planIdNow,
      planName: useFloorplanStore.getState().layout?.name || PLAN_NAME,
      flush: flushForSwap,
      currentStamp: () => planModifiedAtRef.current,
    });
    // Only a definite refusal keeps the confirmation open, with its reason. A swap that may have
    // landed hands over to the reload dialog — and the confirmation must not stay behind it with a
    // live Load button — and a change of twin makes it about the wrong twin.
    if (outcome === 'error') return;
    setLoadTarget(null);
    if (outcome === 'swapped') setFocusChipPending(true);
  }, [loadTarget, productionUuid, loadLaunch, flushForSwap, planIdRef, planModifiedAtRef]);

  /**
   * *Discard Draft*, confirmed (`twin-details-page-versions`, R8.2): production loaded as the draft,
   * through the same swap *Load as Draft* uses — unsaved work saved first, the draft into the slot.
   * A refusal stays in the confirmation; anything else closes it.
   */
  const handleDiscard = useCallback(async () => {
    const planIdNow = planIdRef.current;
    if (!productionLaunch || planIdNow == null || !productionUuid) return;
    const outcome = await loadLaunch({
      launch: productionLaunch,
      productionUuid,
      planId: planIdNow,
      planName: useFloorplanStore.getState().layout?.name || PLAN_NAME,
      flush: flushForSwap,
      currentStamp: () => planModifiedAtRef.current,
      intent: 'discard',
    });
    if (outcome === 'error') return;
    setDiscardOpen(false);
    if (outcome === 'swapped') setFocusChipPending(true);
  }, [productionLaunch, productionUuid, loadLaunch, flushForSwap, planIdRef, planModifiedAtRef]);

  /**
   * The plan the wizard holds, for Versions' draft header and the Discard gate; null until one is
   * hydrated. The
   * later of this session's last save and the stamp the plan was hydrated with: a swap re-hydrates
   * (stamping the swap) without touching the saver's own time.
   */
  const sessionSaveIsLatest =
    !!draft.savedAt && (!planSavedAt || draft.savedAt.getTime() >= planSavedAt.getTime());
  const draftSavedAt = sessionSaveIsLatest ? draft.savedAt : planSavedAt;
  useEffect(() => {
    if (!focusChipPending || draftChoice.busy !== null || loadTarget || discardOpen) return;
    setFocusChipPending(false);
    chipRef.current?.focus();
  }, [focusChipPending, draftChoice.busy, loadTarget, discardOpen]);
  // A Restore that changed nothing says why in Versions, which it was started from (R2.3).
  const { restoreError } = draftChoice;
  useEffect(() => {
    if (!restoreError) return;
    setFocusChipPending(false);
    setVersionsFocus('restore');
    setVersionsOpen(true);
  }, [restoreError]);
  /*
   * The wizard holds a plan: one was read on opening, or a twin that had none has since saved its
   * first (that save assigns the id and leaves `planState` at `none`, which other steps read as "no
   * plan was found"). Versions and its draft header need either (Copilot review).
   */
  const planHeld = planState === 'loaded' || (planState === 'none' && planIdRef.current !== null);
  const historyDraft = useMemo(
    () =>
      planHeld && layout
        ? {
            layout,
            modifiedAt: draftSavedAt ? draftSavedAt.toISOString() : null,
            modifiedBy: sessionSaveIsLatest ? (user?.email ?? planSavedBy) : planSavedBy,
          }
        : null,
    [planHeld, layout, draftSavedAt, sessionSaveIsLatest, user?.email, planSavedBy],
  );

  const draftBasis = layout?.draftBasis ?? null;
  const draftHasUnlaunched = historyDraft
    ? hasUnlaunchedEdits(historyDraft.layout, historyDraft.modifiedAt, productionLaunch)
    : false;
  const draftNotLaunched = route === 'create' || route === 'migrate' || draftHasUnlaunched;
  const canDiscard = canDiscardDraft({
    route,
    hasProduction: productionLaunch !== null,
    hasUnlaunched: draftHasUnlaunched,
  });
  const versionsAvailable = !landingPending && route !== null && planHeld;
  const chipVersionText = route ? versionText({ route, draftBasis, history: historyReady }) : null;
  /** Launch plans, read once per page for Versions, Discard and Load (`load-launch`, R5.1). */
  const launchPlans = useLaunchPlans({ middlewareApi: Config.middlewareApi, accessToken, twinId });
  /*
   * The page's one "what changed in this draft" source (`onboarding-status-colours-change-source`):
   * the dialogs' counts, and entity by entity for the steps, from one comparison. Floor Plan passes
   * `byEntity` to its planner for the lasting marks (`onboarding-status-colours-plan-spaces`).
   */
  const draftWants = {
    canDiscard,
    versionsOpen,
    discardOpen,
    // Load counts the draft's changes too (`load-launch`, R3.1), on any draft — one equal to
    // production counts none.
    loadOpen: loadTarget !== null,
  };
  const dialogsWant = dialogsWantChanges(draftWants);
  const draftSource = useDraftChanges({
    // The dialogs' reads as they always were, Floor Plan's lasting marks and Observers' added group.
    wanted: draftChangesWanted({
      ...draftWants,
      onFloorPlan: section === SECTION.plan,
      onObservers: section === SECTION.observers,
    }),
    // Load closing back to Versions is a fresh opening, so a failed read is asked again (PR #2272).
    dialog: loadTarget
      ? `load:${loadTarget.launchUuid}`
      : discardOpen
        ? 'discard'
        : versionsOpen
          ? 'versions'
          : null,
    plans: launchPlans,
    // The last ready history for this twin, so a re-read loading or failing changes nothing, and a
    // history not yet read is never "never launched".
    history: draftHistoryState(historyReady, launchHistory, twinId),
    productionUuid,
    layout: layout ?? null,
    sessionLayout,
    // The session keeps the last twin's plan across an in-place change of `?twinBid=` (see
    // `sessionPlanTwinBid`), so the steps' half asks for the positive match `registerNameScope` asks.
    planIsThisTwins: sessionPlanTwinBid !== undefined && sessionPlanTwinBid === twinBid,
    twinBid: twinBid ?? null,
  });
  // The dialogs read the changes only when they would have before Floor Plan wanted them (R8).
  const draftChanges = dialogsWant ? draftSource.changes : null;
  const retryDraftChanges = draftSource.retry;
  // The Launch step's *Renumbered at Launch* rows (`floor-plan-page-launch-bid-changes`, R16).
  const renumberedRows = useMemo(
    () => renumberedRowsFor(sessionLayout, draftSource.renumbered),
    [sessionLayout, draftSource.renumbered],
  );
  /*
   * The Load confirmation's note on the draft it drops, held still while the load runs: the swap
   * hydrates the loaded plan before the dialog closes, and the note must not recount against it.
   */
  const loadDraftNote = useRef<{ changes: ChangesState | null; editedAt: string | null }>({
    changes: null,
    editedAt: null,
  });
  if (draftChoice.busy !== 'load')
    loadDraftNote.current = {
      changes: loadTarget ? draftChanges : null,
      editedAt: draftBasis?.editedAt ?? null,
    };
  const { difference: loadDifference, retry: retryLoadDifference } = useLoadComparison({
    target: loadTarget,
    productionUuid,
    plans: launchPlans,
    twinBid: twinBid ?? null,
  });

  /*
   * Arriving on a draft step with unsaved plan work — an unsaved Floor Plan change, say — saves it:
   * the draft is the whole plan document, and the step has no Save button to do it with
   * (maria@twinlabs.ai, 2026-10-02: "unsaved work should be saved anyway"). Leaving one with unsaved
   * work saves it at once rather than leaving it to the timer (FR8) — by firing, not by flushing,
   * because the stage's last write-through lands as it unmounts, after the step has changed, and
   * its request is then (rightly) not this step's. Store reads, not the render's `planDirty`: both
   * must see the flag as it is now.
   */
  const { retry: saveDraftNow, request: requestDraft } = draft;

  /*
   * A composite edit — the pop-up's Save, or a Delete — written into the plan store, then a save
   * asked for (`composites-page-draft`, FR2). Store first: the saver's clean-at-fire-time check must
   * see the edit.
   */
  const handleCompositeChange = useCallback(
    (next: FloorPlannerComposite[]) => {
      setPlanComposites(next);
      // Stamped now as well as on the save, so a re-match landing first sees the edit (R2.4).
      stampFirstEdit();
      requestDraft();
    },
    [setPlanComposites, requestDraft, stampFirstEdit],
  );

  /*
   * A camera change on Camera Feeds, already in the plan store — the stage's own writes (link,
   * place, unlink, fields, the name once its field is left) and the plan's gestures (move, turn,
   * the camera tool, delete) through `TwinPlanner`'s `onCameraEdited` (`camera-feeds-page-draft`,
   * FR2). Stamped now as well as on the save, so a re-match landing first sees the edit (R2.4).
   */
  const handleCameraEdit = useCallback(() => {
    stampFirstEdit();
    requestDraft();
  }, [requestDraft, stampFirstEdit]);

  /*
   * Floor Plan's watcher (`floor-plan-page-draft`, FR2). The planner writes to the plan from some
   * twenty-five places — walls, spaces, corners, equipment, cameras, floors, references, the
   * properties panel, drops, undo and redo, the register's renames — and the undo history misses
   * several of them (`R`, register link and unlink, each keystroke after the first), so the step
   * does not wait for a signal from each. While the step is on screen, any change that leaves the
   * PLAN unsaved stamps the first edit (so a re-match landing first sees it, R2.4) and asks for a
   * save. A drawn-against record owed with the plan itself clean is not an edit: the records'
   * own subscription (above) asks for that quiet save, and stamping here would mark a confirm as
   * unlaunched work (`onboarding-polish-observer-notices`, R12; `/review-code`, 2026-10-12). `useDraftSaver`'s header warns against a store subscription because a
   * load writes into the store and then restores the clean flag; this one is safe because it does
   * not ask on the change itself. It waits for the call stack to finish — a promise continuation,
   * not `queueMicrotask`, which Jest's fake timers fake — and only then reads the store again:
   * - opening a saved plan, a draft swap and a twin change leave the store clean;
   * - the live-twin match sets the flag and puts it back in the same call
   *   (`useLiveTwinConfiguration`, `wasClean`), so it reads clean by then;
   * - so a load never asks, and the tag never flickers to *Saving…* for one.
   * A burst of writes in one turn asks once. The stamp's own layout change (`setDraftBasis`) is
   * seen too, and asks again while a request is already waiting, which costs nothing. A
   * continuation still queued when the step, the twin or the page goes is dropped (`disposed`).
   */
  useEffect(() => {
    if (section !== SECTION.plan) return undefined;
    let queued = false;
    let disposed = false;
    const unsubscribe = useFloorplanStore.subscribe((next, prev) => {
      // A new layout, or the flag going up on the same one (`setDirty(true)`, which some writers
      // use after an in-place change).
      const changed = next.layout !== prev.layout || (next.isDirty && !prev.isDirty);
      if (!changed || queued) return;
      queued = true;
      void Promise.resolve().then(() => {
        queued = false;
        if (disposed) return;
        const store = useFloorplanStore.getState();
        if (store.twinBID !== sessionId || !store.isDirty) return;
        stampFirstEdit();
        requestDraft();
      });
    });
    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [section, sessionId, stampFirstEdit, requestDraft]);

  /*
   * An observer edit, already in the plan store (the stage's write-through, at most once per
   * animation frame): stamp the plan's `draftBasis` on the first edit — when, and from which
   * launch — then ask for a save. Stamped here rather than only when the save fires, so a re-match
   * landing before the first save already sees the edit as unlaunched work (R2.4).
   */
  const handleObserverEditPending = useCallback(() => {
    observerEditPendingRef.current = true;
  }, []);
  const handleObserverEdit = useCallback(() => {
    observerEditPendingRef.current = false;
    stampFirstEdit();
    requestDraft();
  }, [requestDraft, stampFirstEdit]);
  const previousSectionRef = useRef(section);
  useEffect(() => {
    const previous = previousSectionRef.current;
    previousSectionRef.current = section;
    if (previous === section) return;
    const { isDirty, layout } = useFloorplanStore.getState();
    // An owed drawn-against record is saved on the way out, like an edit.
    const dirty = isDirty || referencesUnsaved(layout) || decisionsUnsaved();
    if (DRAFT_SAVE_SECTIONS.has(previous) && dirty) saveDraftNow();
    // The saver itself ignores a request on a step it does not serve.
    else if (dirty) requestDraft();
  }, [section, saveDraftNow, requestDraft]);

  /*
   * The wizard's own exit. A save the draft saver is about to make — or is making — is let finish
   * first, so the leave guard only asks about work that genuinely is not saved: without this, a
   * click within a second of an edit offered *Leave and discard* for work about to be persisted.
   */
  const { settle: settleDraft } = draft;
  const { guardLeave } = leaveGuard;
  const beforeLeave = useCallback(async () => {
    await settleDraft();
    return guardLeave();
  }, [settleDraft, guardLeave]);

  /**
   * *Remove all* (`register-remove-all`, epic `complex-measures-in-configuration` ledger 168–170).
   *
   * Two choices, both behind one dialog. Removing the UNPLACED rows touches the register only.
   * Removing ALL of them also removes their placements on every floor, the observers drawn on those
   * placements and every composite input naming one (`removePlacementsFromLayout`) — and the order
   * is the whole of its safety:
   *
   * 1. **The plan is saved before any row is deleted** — without the placements, and with any
   *    unsaved edits, because the server's discard judges placement against the SAVED plan and
   *    refuses a row it still places (409). A plan left pointing at a deleted row is the dangling
   *    `assetUid` the per-row refusal exists to prevent. A failed save deletes nothing.
   * 2. **Rows go through the `discard` route**, in batches of `DISCARD_MAX_IDS`,
   *    stopping at the first one refused.
   *
   * No register, activation or filing write touches dGraph here (ruling 170). The one exception is
   * `persistPlan`'s own lifecycle-stage write after any save, which re-sends the twin's existing
   * AdminDB values — every Save does the same.
   */
  const [removeAllOpen, setRemoveAllOpen] = useState(false);
  const [removeAllBusy, setRemoveAllBusy] = useState<RemoveAllChoice | null>(null);
  const [removeAllError, setRemoveAllError] = useState<RemoveAllFailure | null>(null);
  /**
   * The counts the dialog showed when an action started, held until it settles. The prune rewrites
   * the plan the live preview reads, and without this the open dialog would re-describe the removal
   * mid-run — "none of them is on the floor plan" — while it is happening.
   */
  const [removeAllFrozenCounts, setRemoveAllFrozenCounts] = useState<RemoveAllCounts | null>(null);
  const planDirty = useFloorplanStore(s => s.isDirty);
  /**
   * Stable, so `Modal`'s focus effect does not re-run on every render while the dialog is open — and
   * lazy, so the lookup runs only when the dialog closes and asks for it.
   */
  const removeAllFocusFallback = useMemo(
    () => ({
      get current() {
        return removeAllReturnFocus().current;
      },
    }),
    [],
  );
  /**
   * Set by a removal expected to empty the register. The dialog closes before the re-read lands, so
   * focus goes back to *Remove all* — which then unmounts with the last row. This lands it on *Add an
   * asset* instead, once the empty register has rendered.
   */
  const focusAddAfterRemoveRef = useRef(false);
  useEffect(() => {
    if (!focusAddAfterRemoveRef.current || registerRows.length > 0) return;
    // On an empty register the start screen's *Add an Asset* carries the hook
    // (`equipment-page-start`); it renders once the read lands, so wait for it rather than
    // spend the flag on a render that has nothing to focus yet.
    const add = document.querySelector<HTMLElement>('[data-register-add]');
    if (!add) return;
    focusAddAfterRemoveRef.current = false;
    add.focus();
  }, [registerRows, equipmentStart]);
  // A twin change without a remount must not carry an open dialog, or its error, to another twin.
  useEffect(() => {
    setRemoveAllOpen(false);
    setRemoveAllError(null);
  }, [twinId]);

  /** What each choice would remove — worked out by the same function the action runs, dry. */
  const removeAllPreview = useMemo(() => {
    if (!removeAllOpen) return null;
    const allIds: number[] = [];
    const unplacedIds: number[] = [];
    const refids = new Set<string>();
    let placed = 0;
    for (const row of registerRows) {
      allIds.push(row.id);
      const placements = coverage.placementsFor(row.id);
      if (placements.length === 0) {
        unplacedIds.push(row.id);
        continue;
      }
      placed += 1;
      for (const refid of placements) refids.add(refid);
    }
    const { removed } = removePlacementsFromLayout(sessionLayout, refids, {
      dryRun: true,
    });
    const counts: RemoveAllCounts = {
      total: allIds.length,
      unplaced: unplacedIds.length,
      placed,
      observers: removed.observers,
      compositesAmended: removed.compositesAmended,
      compositesRemoved: removed.compositesRemoved,
    };
    return { allIds, unplacedIds, refids, counts };
  }, [removeAllOpen, registerRows, coverage, sessionLayout]);

  const openRemoveAll = useCallback(() => {
    setAssetWriteError(null);
    clearAddMissingMessages();
    setAssetWriteNotice(null);
    // The list marks the control unavailable until the plan is known, and the click still lands
    // here — the single refusal authority, whose alert says why (the per-row Remove's pattern).
    if (!coverage.planKnown) {
      reportWriteError(placementUnknownMessage('The register'));
      return;
    }
    setRemoveAllError(null);
    setRemoveAllOpen(true);
  }, [clearAddMissingMessages, coverage, placementUnknownMessage, reportWriteError]);

  const closeRemoveAll = useCallback(() => {
    if (removeAllBusy) return;
    setRemoveAllOpen(false);
    setRemoveAllError(null);
  }, [removeAllBusy]);

  /**
   * Save the plan ahead of a removal — pruned of `refids` when there are any — and say whether it
   * landed. Skipped when there is nothing to prune and nothing unsaved: the saved plan already is
   * the screen's.
   *
   * On a failed save the previous plan goes back on screen (only if nothing has replaced the store
   * meanwhile) and the store is left DIRTY, whatever it was before. A save whose outcome is unknown
   * may have landed; marking the store clean would leave the screen and the server disagreeing with
   * no Save and no leave guard to reconcile them. One redundant Save is the cheaper mistake.
   */
  const saveBeforeRemoval = useCallback(
    async (
      refids: ReadonlySet<string>,
      issuedFor: number,
      /** Where a refusal is said: *Remove all*'s dialog, or the asset card's confirmation (R20). */
      report: (failure: RemoveAllFailure) => void,
    ): Promise<
      | {
          saved: true;
          /** A save actually ran (there was something to prune, or unsaved edits). */
          wrote: boolean;
          pruned: boolean;
          removed: ReturnType<typeof removePlacementsFromLayout>['removed'];
        }
      | { saved: false }
    > => {
      const store = useFloorplanStore.getState();
      const nothingRemoved = { items: 0, observers: 0, compositesAmended: 0, compositesRemoved: 0 };
      if (refids.size === 0 && !store.isDirty) {
        return { saved: true, wrote: false, pruned: false, removed: nothingRemoved };
      }
      const prev = store.layout;
      if (!prev || store.twinBID !== sessionId) {
        report({
          title: NOTHING_REMOVED,
          message: "The twin's plan is not loaded here, so nothing was removed.",
        });
        return { saved: false };
      }
      const pruned = removePlacementsFromLayout(prev, refids, {});
      const changed = pruned.layout !== prev;
      if (changed) store.replaceLayout(pruned.layout);
      // `replaceLayout` stores a CLONE, so the layout to hold the save to is the store's own.
      const target = useFloorplanStore.getState().layout ?? undefined;
      const saved = await persistPlan({ unchangedSince: target });
      if (!saved.saved) {
        const now = useFloorplanStore.getState();
        if (changed && target && now.layout === currentLayoutOf(target)) now.replaceLayout(prev);
        if (now.twinBID === sessionId) now.setDirty(true);
        if (issuedFor !== twinIdRef.current) return { saved: false };
        report({
          title: NOTHING_REMOVED,
          message: describeFailedSave(
            'The plan could not be confirmed saved, so no register row was removed.',
            saved.reason,
            'Save the plan so it matches the screen, then try again.',
          ),
        });
        return { saved: false };
      }
      // Undo would bring back placements whose register rows are about to be deleted.
      if (changed) useEditorStore.getState().clearHistory();
      return { saved: true, wrote: true, pruned: changed, removed: pruned.removed };
    },
    [persistPlan, sessionId, currentLayoutOf],
  );

  const runRemoveUnplaced = useCallback(async () => {
    const preview = removeAllPreview;
    if (!preview || removeAllBusy || !twinId) return;
    setRemoveAllError(null);
    const issuedFor = twinId;
    setRemoveAllFrozenCounts(preview.counts);
    setRemoveAllBusy('unplaced');
    try {
      let token: string;
      try {
        token = await getTokenBounded(getAccessTokenSilently);
      } catch (error) {
        setRemoveAllError({
          title: NOTHING_REMOVED,
          message:
            error instanceof Error
              ? error.message
              : 'Could not sign the request. Please try again.',
        });
        return;
      }
      if (issuedFor !== twinIdRef.current) return;
      // Unsaved plan edits go first: the server judges "unplaced" against the SAVED plan.
      const saved = await saveBeforeRemoval(new Set(), issuedFor, setRemoveAllError);
      if (!saved.saved || issuedFor !== twinIdRef.current) return;

      const removeToken = beginRegisterWrite();
      const outcome = await discardInBatches(token, issuedFor, preview.unplacedIds);
      settleRegisterWrite(
        removeToken,
        outcome.failure === undefined
          ? { ok: true }
          : { ok: false, reason: discardFailure(outcome, false).title },
      );
      if (issuedFor !== twinIdRef.current) return;
      // Before the re-read: a row added this session is otherwise re-read as added.
      useEquipmentCatalogStore
        .getState()
        .forgetRegisterWrites([...outcome.deleted, ...outcome.alreadyGone]);
      reloadRegister({ afterWrite: true });
      if (outcome.failure !== undefined) {
        setRemoveAllError(discardFailure(outcome, false));
        return;
      }
      focusAddAfterRemoveRef.current = preview.unplacedIds.length === preview.allIds.length;
      setRemoveAllOpen(false);
      const n = outcome.deleted.length;
      setAssetWriteNotice(
        `Removed ${removeAllPlural(n, 'asset', 'assets')} that ${n === 1 ? 'was' : 'were'} not ` +
          'on the floor plan.' +
          (saved.wrote
            ? ' Your unsaved plan changes were saved first.'
            : ' The plan is unchanged.') +
          alreadyGoneSentence(outcome.alreadyGone.length),
      );
    } finally {
      setRemoveAllBusy(null);
      setRemoveAllFrozenCounts(null);
    }
  }, [
    beginRegisterWrite,
    getAccessTokenSilently,
    reloadRegister,
    removeAllBusy,
    removeAllPreview,
    saveBeforeRemoval,
    settleRegisterWrite,
    twinId,
  ]);

  const runRemoveAll = useCallback(async () => {
    const preview = removeAllPreview;
    if (!preview || removeAllBusy || !twinId) return;
    setRemoveAllError(null);

    /*
     * Removal is by refid, which is safe only while every refid it names is unique and real. The D7
     * defect (epic `asset-register-and-library`, ledger row 71) once left a staging plan repeating one
     * refid 44 times, so removing by it would take entities nobody asked to remove; and an item with
     * no string refid counts as placed (`buildAssetCoverageIndex` records it as '') yet cannot be
     * removed by refid at all. Refuse rather than guess.
     */
    const unsafe = unsafeRefids(preview.refids, sessionLayout);
    if (unsafe.kind === 'no-id') {
      setRemoveAllError({
        title: NOTHING_REMOVED,
        message:
          'An item on the plan that holds one of these assets has no id, so it cannot be removed ' +
          'safely in bulk. Remove it from the plan by hand first. Nothing was removed.',
      });
      return;
    }
    if (unsafe.kind === 'shared') {
      const { shared } = unsafe;
      setRemoveAllError({
        title: NOTHING_REMOVED,
        message:
          `This plan has ${shared.length} ${shared.length === 1 ? 'id' : 'ids'} shared by more ` +
          `than one item or space (${shared.slice(0, 10).join(', ')}${shared.length > 10 ? ', …' : ''}), ` +
          'so they cannot be removed safely in bulk. Remove them from the plan by hand first. ' +
          'Nothing was removed.',
      });
      return;
    }

    const issuedFor = twinId;
    setRemoveAllFrozenCounts(preview.counts);
    setRemoveAllBusy('all');
    try {
      // The token FIRST: a sign-in failure after the plan was saved would leave the job half done.
      let token: string;
      try {
        token = await getTokenBounded(getAccessTokenSilently);
      } catch (error) {
        setRemoveAllError({
          title: NOTHING_REMOVED,
          message:
            error instanceof Error
              ? error.message
              : 'Could not sign the request. Please try again.',
        });
        return;
      }
      if (issuedFor !== twinIdRef.current) return;

      const saved = await saveBeforeRemoval(preview.refids, issuedFor, setRemoveAllError);
      if (!saved.saved || issuedFor !== twinIdRef.current) return;
      const cascade = saved.removed;

      const removeToken = beginRegisterWrite();
      const outcome = await discardInBatches(token, issuedFor, preview.allIds);
      settleRegisterWrite(
        removeToken,
        outcome.failure === undefined
          ? { ok: true }
          : { ok: false, reason: discardFailure(outcome, false).title },
      );
      if (issuedFor !== twinIdRef.current) return;
      // Before the re-read: a row added this session is otherwise re-read as added.
      useEquipmentCatalogStore
        .getState()
        .forgetRegisterWrites([...outcome.deleted, ...outcome.alreadyGone]);
      reloadRegister({ afterWrite: true });
      if (outcome.failure !== undefined) {
        setRemoveAllError(discardFailure(outcome, saved.pruned));
        return;
      }
      // Removed and amended composites are told apart: one that only lost an input still exists.
      const parts = [
        ...(cascade.items > 0
          ? [removeAllPlural(cascade.items, 'placed item', 'placed items')]
          : []),
        ...(cascade.observers > 0
          ? [removeAllPlural(cascade.observers, 'observer', 'observers')]
          : []),
        ...(cascade.compositesRemoved > 0
          ? [removeAllPlural(cascade.compositesRemoved, 'composite measure', 'composite measures')]
          : []),
      ];
      const amended =
        cascade.compositesAmended > 0
          ? ` ${removeAllPlural(
              cascade.compositesAmended,
              'composite measure no longer adds',
              'composite measures no longer add',
            )} that equipment up.`
          : '';
      focusAddAfterRemoveRef.current = true;
      setRemoveAllOpen(false);
      setAssetWriteNotice(
        `Removed ${removeAllListOf([
          removeAllPlural(outcome.deleted.length, 'asset', 'assets'),
          ...parts,
        ])} from the ${parts.length > 0 || amended ? 'plan and register' : 'register'}.` +
          `${amended}${alreadyGoneSentence(outcome.alreadyGone.length)} The live twin is unchanged until ` +
          'you press Go Live.',
      );
    } finally {
      setRemoveAllBusy(null);
      setRemoveAllFrozenCounts(null);
    }
  }, [
    beginRegisterWrite,
    getAccessTokenSilently,
    reloadRegister,
    removeAllBusy,
    removeAllPreview,
    saveBeforeRemoval,
    sessionLayout,
    settleRegisterWrite,
    twinId,
  ]);

  /*
   * The asset card (`equipment-page-asset-card`, R1–R5, R18–R20): the Rows view's open row, and its
   * card in the step's dock. A row click opens it; with it open, the table's arrow keys call the same
   * handler for the row they land on, so the card follows. Page state, because the dock, the table
   * and the close rules all read it.
   */
  const [openAssetId, setOpenAssetId] = useState<number | null>(null);
  const [assetCardFolds, setAssetCardFolds] = useState<AssetCardFolds>({
    costs: false,
    notes: false,
  });
  const assetCardRef = useRef<EquipmentAssetCardHandle>(null);
  const openAssetIdRef = useRef(openAssetId);
  openAssetIdRef.current = openAssetId;
  /** Opens the card on `row`. A field holding an edit on the row being left is saved first (R3). */
  const openAssetCard = useCallback((row: TwinAssetResponse) => {
    if (openAssetIdRef.current !== null && openAssetIdRef.current !== row.id) {
      void assetCardRef.current?.commitPending();
    }
    setOpenAssetId(row.id);
  }, []);
  const closeAssetCard = useCallback(() => {
    setOpenAssetId(null);
    // The folds reset when the card closes; they persist only while it moves between rows (R16).
    setAssetCardFolds({ costs: false, notes: false });
  }, []);
  const openAssetRow = useMemo(
    () =>
      openAssetId === null ? null : (registerRows.find(row => row.id === openAssetId) ?? null),
    [openAssetId, registerRows],
  );
  /*
   * The card closes when its row leaves the rows shown (R5): a chip or the search hides it, it is
   * removed, the view switches to Groups (where the identify card docks), or the twin changes.
   */
  const openAssetHidden =
    openAssetId !== null &&
    (openAssetRow === null ||
      equipmentView !== 'rows' ||
      (equipmentFiltered &&
        equipmentVisibleIds !== undefined &&
        !equipmentVisibleIds.has(openAssetId)));
  /**
   * The open row's element, captured while it is open: the card returns focus to it on close
   * (`DockedCard`'s `returnFocusTo`), and the row has lost `aria-current` by then. Its place among
   * the rows drawn is captured with it, for a close that finds the row already gone.
   */
  const openAssetRowEl = useRef<HTMLElement | null>(null);
  const openAssetRowAt = useRef(-1);
  useLayoutEffect(() => {
    if (openAssetId === null) return;
    const el = document.querySelector<HTMLElement>('.rows-table [role="row"][aria-current="true"]');
    if (!el) return;
    openAssetRowEl.current = el;
    openAssetRowAt.current = Array.from(
      document.querySelectorAll<HTMLElement>(ASSET_BODY_ROWS),
    ).indexOf(el);
  });
  /**
   * Focus for a card whose row has left the table (R20): once the table is drawn without it, the
   * row now in its place, else the one before it, else the table's Tab stop — else, a search or
   * chip having shown only this row, the Rows view's *Add an Asset* (Copilot, PR #2177).
   * `onlyIfLost`: leave focus where it is unless it fell to the body (a card closed by the page,
   * whose `returnFocusTo` row is gone).
   */
  const focusRowInPlaceOfOpen = useCallback((at: number, onlyIfLost = false) => {
    window.setTimeout(() => {
      if (onlyIfLost) {
        const active = document.activeElement;
        if (active && active !== document.body) return;
      }
      // Rows that take focus: never the table's *No assets match* line, which is drawn as a row.
      const now = Array.from(document.querySelectorAll<HTMLElement>(ASSET_BODY_ROWS)).filter(
        el => el !== openAssetRowEl.current && el.isConnected && el.hasAttribute('tabindex'),
      );
      // The removed row may still be drawn, and hold the table's Tab stop, until the re-read lands.
      const gone = openAssetRowEl.current;
      const tabStop = Array.from(
        document.querySelectorAll<HTMLElement>('.rows-table [tabindex="0"]'),
      ).find(el => el.isConnected && !(gone && gone.contains(el)));
      const target =
        now[Math.max(0, Math.min(at, now.length - 1))] ??
        tabStop ??
        document.querySelector<HTMLElement>('[data-register-add]');
      target?.focus();
    }, 0);
  }, []);
  useEffect(() => {
    if (!openAssetHidden) return;
    /*
     * Its row left the rows shown — a Keep on *Doesn't fit* or *Needs a look*, an identification on
     * *To identify*. The card's `returnFocusTo` row is gone, so focus held in the card (or already
     * fallen from it) would land on the body: it moves on to a row as a removal's does (review, H2).
     * A view switch or a search keeps focus where the operator put it.
     */
    const active = document.activeElement;
    const lost =
      !active ||
      active === document.body ||
      !!(active as HTMLElement).closest?.('.equipment-asset-card');
    closeAssetCard();
    if (lost && equipmentView === 'rows') focusRowInPlaceOfOpen(openAssetRowAt.current, true);
  }, [openAssetHidden, closeAssetCard, focusRowInPlaceOfOpen, equipmentView]);
  useEffect(() => {
    closeAssetCard();
  }, [twinId, closeAssetCard]);

  /** The page's removal steps for the card, bound to the twin on screen (R19–R20). */
  const assetRemoval = useMemo<AssetRemoval>(() => {
    const issuedFor = twinId ?? null;
    return {
      isPlacementKnown: assetId => coverage.isPlacementKnown(assetId),
      placementsFor: assetId => coverage.placementsFor(assetId),
      // One line per placement (R19): a row placed twice is named in both places.
      whereFor: assetId =>
        coverage.placementsFor(assetId).map(refid => {
          const where = placementWhereForRefid(registerJoinedLayout, refid);
          if (!where || !where.floorName) return 'on the plan';
          return where.spaceName ? `${where.floorName} › ${where.spaceName}` : where.floorName;
        }),
      cascadeFor: refids =>
        removePlacementsFromLayout(sessionLayout, refids, { dryRun: true }).removed,
      refusalFor: refids => {
        const unsafe = unsafeRefids(refids, sessionLayout);
        if (unsafe.kind === 'no-id') {
          return (
            'The item on the plan that holds this asset has no id, so it cannot be removed safely. ' +
            'Remove it from the plan by hand first. Nothing was removed.'
          );
        }
        if (unsafe.kind === 'shared') {
          return (
            `This plan has the id ${unsafe.shared.join(', ')} on more than one item or space, so ` +
            'it cannot be removed safely. Remove it from the plan by hand first. Nothing was removed.'
          );
        }
        return null;
      },
      planUnknown: subject => placementUnknownMessage(subject),
      getToken: () => getTokenBounded(getAccessTokenRef.current),
      saveBeforeRemoval: (refids, report) =>
        issuedFor === null
          ? Promise.resolve({ saved: false as const })
          : saveBeforeRemoval(refids, issuedFor, report),
      discard: async (token, ids) => {
        if (issuedFor === null) throw new Error('No twin is open.');
        const removeToken = beginRegisterWrite();
        const outcome = await discardInBatches(token, issuedFor, ids);
        settleRegisterWrite(
          removeToken,
          outcome.failure === undefined
            ? { ok: true }
            : { ok: false, reason: discardFailure(outcome, false).title },
        );
        // `twinOnPage`: null once the page has unmounted, so a late answer never re-reads the twin
        // it left into the shared store (Copilot, PR #2177).
        if (issuedFor === twinOnPage()) {
          // Before the re-read: a row added this session is otherwise re-read as added (the
          // table's own remove does the same).
          useEquipmentCatalogStore
            .getState()
            .forgetRegisterWrites([...outcome.deleted, ...outcome.alreadyGone]);
          reloadRegister({ afterWrite: true });
        }
        return outcome;
      },
      describeFailure: discardFailure,
      stillOn: () => issuedFor === twinOnPage(),
    };
  }, [
    twinId,
    twinOnPage,
    coverage,
    registerJoinedLayout,
    sessionLayout,
    placementUnknownMessage,
    saveBeforeRemoval,
    beginRegisterWrite,
    settleRegisterWrite,
    reloadRegister,
  ]);
  /*
   * *Undo This Import* (`equipment-page-undo-import`): the page's half. The flow runs in
   * `EquipmentImportUndoHost`, inside the wizard's toast host; these are what it reads and sets here.
   */
  /*
   * Which host's run holds the gate. Leaving the step unmounts the host while its run may still be
   * pending; a new host on return is a second owner, so only the holder may release the gate, and
   * no other may claim it meanwhile (Copilot, PR #2378).
   */
  const importUndoOwnerRef = useRef<object | null>(null);
  const onImportUndoBusy = useCallback(
    (busy: boolean, forTwin: number, owner: object): boolean => {
      // A run that outlived its twin settles here late: the twin on screen has its own gate.
      if (forTwin !== twinIdRef.current) return false;
      const next = nextUndoHolder(importUndoOwnerRef.current, busy, owner);
      if (next === undefined) return false;
      importUndoOwnerRef.current = next;
      setImportUndoing(busy);
      if (busy) {
        setAssetWriteError(null);
        clearAddMissingMessages();
        setAssetWriteNotice(null);
      }
      return true;
    },
    [clearAddMissingMessages],
  );
  // A new twin starts with no undo running; the last twin's run, if any, is ignored above.
  useEffect(() => {
    importUndoOwnerRef.current = null;
    setImportUndoing(false);
  }, [twinId]);
  /** The undo landed: reopen the uploader in the same mode, for the file meant instead (R19). */
  const onImportUndone = useCallback((mode: 'customer' | 'adopt') => {
    // The rows it wrote are gone: *From this import* ends (R11).
    setFromImport(null);
    setImportAttempt(null);
    setUploaderOpen(true);
    setUploaderResetMode(mode);
    setUploaderResetToken(value => value + 1);
  }, []);
  /**
   * *Keep* and *Keep All* on the summary's *Not in this file* list (`equipment-page-reimport`, R7):
   * written to the record, then the summary is read again. Rejects with the server's sentence, which
   * the summary's toast says. Never retried here; the route is idempotent, so pressing again is safe.
   *
   * The summary is read again on a failure too: a 503 after the write, or a timeout, leaves the
   * outcome unknown, and the record is what says whether the rows were kept.
   */
  const keepAbsentRows = useCallback(
    async (importId: number, request: { ids: number[] } | { all: true }) => {
      if (!twinId) throw new Error('No twin is loaded.');
      const issuedFor = twinId;
      try {
        const token = await getTokenBounded(getAccessTokenRef.current);
        await keepAbsentImportRows(
          Config.middlewareApi,
          token,
          { twinId: issuedFor, importId },
          request,
        );
      } finally {
        // Another twin's record must not re-read this one's summary (the read is keyed by twin
        // anyway).
        if (issuedFor === twinIdRef.current) setImportSummaryQuietKey(key => key + 1);
      }
    },
    [twinId],
  );
  const reloadAfterImportUndo = useCallback(() => {
    reloadRegister({ afterWrite: true });
    setImportSummaryKey(key => key + 1);
  }, [reloadRegister]);
  const forgetUndoneRows = useCallback((ids: number[]) => {
    // Before the re-read: a row added this session is otherwise re-read as added.
    useEquipmentCatalogStore.getState().forgetRegisterWrites(ids);
  }, []);
  const undoableImport = importSummary.undoable;
  /**
   * The upload the file card names (`equipment-page-undo-import`, R6): the live one the summary
   * speaks for, else the undoable one — a failed or partly undone upload has no summary, but the
   * card that offers to undo it must say which file it is.
   */
  const cardImport = latestImport ?? undoableImport?.record ?? null;

  /** Removed through the card: the notice, the card closes, and focus moves on to a row (R20). */
  const onAssetRemoved = useCallback(
    (notice: string) => {
      const rowsShown = Array.from(document.querySelectorAll<HTMLElement>(ASSET_BODY_ROWS));
      const at = openAssetRowEl.current ? rowsShown.indexOf(openAssetRowEl.current) : -1;
      setAssetWriteError(null);
      setAssetWriteNotice(notice);
      closeAssetCard();
      // The register's last row: the step lands on its start screen, where *Remove all*'s rule
      // puts focus on *Add an Asset* once it has rendered (seen on staging DEM-TST, 2026-10-21).
      if (useEquipmentCatalogStore.getState().registerRows.length <= 1) {
        focusAddAfterRemoveRef.current = true;
        return;
      }
      // Once the re-read has drawn the table without it.
      focusRowInPlaceOfOpen(at);
    },
    [closeAssetCard, focusRowInPlaceOfOpen],
  );

  /*
   * Save the plan when the re-match after a FILING changed its composite refids (epic ledger row
   * 164). `filedConfigIdRef` is set by Go Live, at step 6e.
   */
  const filedConfigIdRef = useRekeyedPlanSave({
    liveConfiguration,
    persistPlan,
    currentLayoutOf,
    planIdRef,
    sessionId,
  });

  // Opens the before/after model comparison from the summary. The "before" is the
  // pre-activation model captured in `activatedRef`; the "after" is read live here so it is the true
  // current model rather than the input-shaped tree we minted. Best-effort: a failed read surfaces
  // in the "after" pane rather than blocking the comparison. Only the latest opening's read lands:
  // one that settles after the comparison was left or reopened is dropped (R8).
  const openComparison = useCallback(async () => {
    const src = activatedRef.current;
    if (!src) return;
    const gen = ++comparisonGenRef.current;
    const current = () => gen === comparisonGenRef.current;
    setModalComparison({ before: src.prevModel, after: null, status: 'loading' });
    try {
      const token = accessToken ?? (await getAccessTokenSilently());
      const outcome = await dgqFetchTwinOutcome(
        Config.middlewareApi,
        token,
        src.orgBID,
        twinBid ?? '',
      );
      if (!current()) return;
      setModalComparison(c =>
        c === null
          ? c
          : outcome.status === 'failed'
            ? { ...c, status: 'error', error: 'Could not read the current model.' }
            : { ...c, after: outcome.status === 'found' ? outcome.twin : null, status: 'ready' },
      );
    } catch {
      if (!current()) return;
      setModalComparison(c =>
        c === null ? c : { ...c, status: 'error', error: 'Could not read the current model.' },
      );
    }
  }, [accessToken, getAccessTokenSilently, twinBid]);

  // The comparison was left: any read still out belongs to an opening that is over.
  const closeComparison = useCallback(() => {
    comparisonGenRef.current += 1;
    setModalComparison(null);
  }, []);

  /**
   * The live viewer's one "open this twin" (`onboarding-persistent-scene-open-twin`), the twin
   * picker's own sequence: the landing calls it, and writes no copy of it
   * (`onboarding-persistent-scene-launch-into-live`, R1.2).
   */
  const openTwin = useOpenTwin({
    middlewareApi: Config.middlewareApi,
    accessToken: accessToken ?? null,
  });

  /**
   * The landing (`onboarding-persistent-scene-launch-into-live`, R1): open the launched twin in Live,
   * by the picker's own open. Resolves whether it navigated. Never rejects: a failed open is warned
   * (R7) and answered `false`, and the caller offers it again.
   *
   * The twin row is the page's read of the launched twin; while that read has not answered, the BID
   * alone, which takes the picker's own defaults (Gym, `Europe/London`; spec, Inputs).
   */
  const landInLive = async (): Promise<boolean> => {
    if (!twinBid) return false;
    const from = requestArrivalFromStage(twinBid) ? 'plan-view' : 'opening-view';
    // eslint-disable-next-line no-console
    console.info('[launch] landing', { twinBid, from });
    const row =
      bidTwin && bidTwin.bid === twinBid
        ? { bid: bidTwin.bid, template: bidTwin.template, iana_timezone: bidTwin.iana_timezone }
        : { bid: twinBid, template: '', iana_timezone: '' };
    let opened = false;
    try {
      // A fresh token for each attempt: the page's own is read once at mount, and an onboarding
      // session can outlive it. A refresh that fails falls back to the page's.
      const token = await getAccessTokenSilently().catch(() => accessToken ?? null);
      opened = await openTwin(row, { accessToken: token });
    } catch {
      opened = false;
    }
    if (!opened) {
      // eslint-disable-next-line no-console
      console.warn('[launch] open twin failed', { twinBid });
    }
    return opened;
  };

  const landInLiveRef = useRef(landInLive);
  landInLiveRef.current = landInLive;

  // Go Live: convert the saved plan into the twin's live dGraph model and land
  // the user in the twin viewer. Reuses the admin plan-activation path
  // (convertPlanToTwin → activatePlan). Observers are created at activation in a
  // follow-up (PR-3); this step activates entities + their sensors.
  /**
   * The end of a launch: confirm what was deployed, then into the live twin. A single success modal
   * replaces the silent navigate (clean run) and the older inline notices (skipped/replaced
   * observers): every Go Live ends by telling the user exactly what went live, with skipped
   * observers, unreachable devices and a BI update that did not happen surfaced as caveats rather
   * than buried. The launched twin opens in Live behind it at once (launch-into-live R1); _Done_ only
   * dismisses it, and a failed open offers _Go to twin_ again.
   *
   * Called from Go Live itself, and — when the BI step failed — from the banner's Retry (once it
   * succeeds) or Continue. `isStale` says the person has left Launch since, so nothing is navigated.
   */
  const finishLaunch = async (
    inputs: LaunchSummaryInputs,
    bi: BiSummary,
    isStale: () => boolean,
    biOutcomeSave: BiOutcomeSave | null = null,
  ): Promise<void> => {
    const summary = buildDeploymentSummary({
      updating: inputs.updating,
      entitiesLive: inputs.entitiesLive,
      entitiesRemoved: inputs.entitiesRemoved,
      observers: inputs.observers,
      devices: inputs.devices,
      reused: inputs.reused,
      launchRecord: inputs.launchRecord,
      bi,
      assetNameByBid: inputs.assetNameByBid,
      biOutcomeSave,
    });
    if (inputs.draftMarkerUnsaved) {
      summary.rows.push({
        tone: 'warn',
        label: 'Your plan could not be saved after launch',
        detail:
          'Until it is, reopening the wizard may show these observers as a draft not yet ' +
          'launched. Save it from the Floor Plan step, which this opens.',
      });
    }

    /*
     * One acknowledgement, whatever was created.
     *
     * ⚠ This used to be a two-way choice, offering "Set up composite measures" beside "Go to
     * twin" whenever observers had just been created — because that stage came AFTER Launch and
     * was the operator's next step. It now comes BEFORE Launch and edits the plan, so offering it
     * here would walk the operator backwards into a stage whose edits only take effect at the
     * NEXT Go Live. The single acknowledgement the no-observers case always used is now the only
     * one.
     */
    const content = {
      title: summary.title,
      summary,
      diff: inputs.modelDiff,
      // UPDATE only (modelDiff is set iff UPDATE) — a first activation has no prior model.
      onCompare: inputs.modelDiff ? () => void openComparison() : undefined,
      onCompareClosed: closeComparison,
    };

    /*
     * No landing (`onboarding-persistent-scene-launch-into-live`, R1.6): on a plan that could not be
     * saved after launch, stay in the wizard: the page still holds the plan without its draft
     * marker, and arriving on Floor Plan saves it (its tag offers Retry if that fails too). A new
     * session would reload the old marker instead. A run the person has left, or a twin with no BID
     * (which Go Live refuses), ends as it always did.
     */
    if (inputs.draftMarkerUnsaved || isStale() || !twinBid) {
      if (!inputs.draftMarkerUnsaved && !isStale()) {
        // eslint-disable-next-line no-console
        console.warn('[launch] no twin to land on');
      }
      await showSummary({
        ...content,
        confirmLabel: inputs.draftMarkerUnsaved ? 'Open Floor Plan' : 'Go to twin',
      });
      closeComparison();
      if (isStale()) return;
      if (inputs.draftMarkerUnsaved) setSection(SECTION.plan);
      else navigate('/twin');
      return;
    }

    /*
     * The landing (R1): the summary opens carried, so it stays the same open Modal across the route
     * change, and the launched twin opens in Live behind it without waiting for the person. _Done_
     * only dismisses it (R1.4).
     *
     * A failed open leaves the person on Launch with the summary up, its button reading _Go to twin_,
     * which runs the open again (spec, Failure States). That holds however the summary went: one
     * dismissed while the open was still out, or a _Go to twin_ summary closed by Escape, the scrim
     * or its close button, is offered again, so a launched twin never strands the person on Launch.
     * It ends when the twin is open and the summary dismissed, when the summary is released unseen
     * (the page or the session went), or when the person leaves the run.
     */
    // The summary, or its comparison: one open Modal and one promise (`showComparison`), so a failed
    // open relabels it in place rather than replacing what the person is comparing.
    const summaryOpen = () => {
      const kind = getOnboardingModal().request?.kind;
      return kind === 'summary' || kind === 'comparison';
    };
    let landed = false;
    // A summary settled as cancel was released (the session changed or the protected layout went)
    // or replaced: it is never reopened, so it cannot show over another session (R2.3).
    let released = false;
    const track = (summary: Promise<Outcome>) => {
      void summary.then(outcome => {
        if (outcome === 'cancel') released = true;
      });
      return summary;
    };
    let attempt: Promise<void> | null = null;
    const offerAgain = {
      confirmLabel: 'Go to twin',
      onConfirmInstead: () => {
        updateSummary({ confirmLabel: 'Done', onConfirmInstead: undefined }, { carry: true });
        void land();
      },
    };
    let dismissed = track(showSummary({ ...content, confirmLabel: 'Done', carry: true }));
    const land = () =>
      (attempt ??= (async () => {
        // The latest render's open, which reads a fresh token for each attempt.
        if (await landInLiveRef.current()) {
          landed = true;
          return;
        }
        if (isStale() || released) return;
        if (summaryOpen()) updateSummary(offerAgain, { carry: false });
        else dismissed = track(showSummary({ ...content, ...offerAgain }));
      })().finally(() => {
        attempt = null;
      }));
    await land();
    for (;;) {
      const current = dismissed;
      const outcome = await current;
      if (attempt) await attempt;
      // An open that failed after the summary went has opened it again.
      if (dismissed !== current) continue;
      if (landed || outcome === 'cancel' || isStale()) break;
      // Dismissed without a landing: open it again, carried, and land.
      dismissed = track(showSummary({ ...content, confirmLabel: 'Done', carry: true }));
      await land();
    }
    closeComparison();
  };

  /**
   * Record what the launch's BI step did in its launch history (`twin-launch-history-bi-outcome`,
   * R3). Null when there is nothing to attach it to (R3.4). Never throws.
   */
  const saveBiOutcome = async (
    inputs: LaunchSummaryInputs,
    input: SetBiOutcomeInput,
    token: string,
  ): Promise<BiOutcomeSave | null> => {
    if (!inputs.launchUuid) return null;
    return launchRecord.saveBiOutcome({
      accessToken: token,
      twinId: inputs.twinId,
      launchUuid: inputs.launchUuid,
      input,
    });
  };

  /**
   * A fresh token for a save made after the person chose; the one Go Live held may have expired.
   * Falls back to that one if the refresh fails, and says so — a save that later fails 401 is
   * otherwise unexplained.
   */
  const freshToken = async (): Promise<string | null> => {
    try {
      return await getTokenBounded(getAccessTokenSilently);
    } catch (err) {
      console.warn('[go-live] token refresh failed; saving the BI result with the launch token', {
        message: err instanceof Error ? err.message : String(err),
      });
      return accessToken ?? null;
    }
  };

  /** Save with a token, or say why nothing was sent. Never throws. */
  const saveWithToken = async (
    inputs: LaunchSummaryInputs,
    input: SetBiOutcomeInput,
    token: string | null,
  ): Promise<BiOutcomeSave | null> => {
    if (token) return saveBiOutcome(inputs, input, token);
    console.warn('[go-live] BI outcome not saved', {
      twinId: inputs.twinId,
      launchUuid: inputs.launchUuid,
      status: 'no-token',
    });
    return 'not-saved';
  };

  /*
   * `failed` saves, IN ORDER. `failed` is the one outcome that can still change, and the database
   * applies the last one to arrive — so a slow first failure (or its retry) landing after a Retry's
   * newer one would leave the older reason on a launch someone walked away from. Each failed save
   * waits for the one before it. Final outcomes need no such care: the database keeps them final.
   */
  const failedSaveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const saveFailedInOrder = (
    inputs: LaunchSummaryInputs,
    failure: BiImportFailure,
    token: () => Promise<string | null>,
  ): void => {
    failedSaveChainRef.current = failedSaveChainRef.current.then(async () =>
      saveWithToken(inputs, toFailedBiOutcome(failure), await token()),
    );
  };

  /**
   * The BI banner's Retry: the BI step alone, then — once it succeeds — its outcome and the summary.
   * A retry that fails again is recorded as `failed` with its new reason, without waiting.
   */
  const handleRetryBi = async () => {
    const inputs = pendingSummaryRef.current;
    if (!inputs) return;
    const runAtPress = activationRunRef.current;
    const isStale = () =>
      activationRunRef.current !== runAtPress || pendingSummaryRef.current !== inputs;
    const shownFailure = biPush.state.failure ?? null;
    const next = await biPush.retry();
    if (next.status === 'failed' && next.failure) {
      saveFailedInOrder(inputs, next.failure, freshToken);
    }
    if (next.status !== 'done') return;
    /*
     * BI WAS updated, so that is recorded whatever happened to the page meanwhile — the nav is not
     * locked during a Retry, and a person who left Launch has not un-happened the update (R3.5).
     * Only the summary is skipped for a run they left.
     */
    if (isStale()) {
      void freshToken().then(token =>
        saveWithToken(inputs, toBiOutcome(next, inputs.assetsLive), token),
      );
      return;
    }
    // Held until the summary opens: the banner would otherwise vanish the moment BI is done.
    setBiSavingFailure(shownFailure);
    setBiOutcomeSaving('retry');
    try {
      const saved = await saveWithToken(
        inputs,
        toBiOutcome(next, inputs.assetsLive),
        await freshToken(),
      );
      if (isStale()) return;
      pendingSummaryRef.current = null;
      setBiOutcomeSaving(null);
      await finishLaunch(
        inputs,
        toBiSummary(next, inputs.assetsLive),
        () => activationRunRef.current !== runAtPress,
        saved,
      );
    } finally {
      setBiOutcomeSaving(null);
    }
  };

  /** The BI banner's Continue: its outcome, then the summary, with BI marked not updated and why. */
  const handleContinueWithoutBi = async () => {
    const inputs = pendingSummaryRef.current;
    const failure = biPush.state.failure;
    if (!inputs) return;
    const runAtPress = activationRunRef.current;
    // Cleared first: it is what makes a second press do nothing.
    pendingSummaryRef.current = null;
    setBiSavingFailure(failure ?? null);
    setBiOutcomeSaving('continue');
    let saved: BiOutcomeSave | null = null;
    try {
      saved = await saveWithToken(inputs, toNotUpdatedBiOutcome(failure), await freshToken());
    } finally {
      setBiOutcomeSaving(null);
    }
    if (activationRunRef.current !== runAtPress) return;
    biPush.reset();
    await finishLaunch(
      inputs,
      {
        kind: 'not-updated',
        reasonText: failure ? biFailureReason(failure) : 'BI was not updated.',
        uncertain: failure?.outcomeUnknown ?? false,
      },
      () => activationRunRef.current !== runAtPress,
      saved,
    );
  };

  const handleGoLive = async () => {
    /*
     * Claim the run FIRST, synchronously, before any await.
     *
     * ⚠ Swept in by `camera-observer-setup-golive-config`'s review (E8 — same defect, same
     * function). The claim used to sit after `await resolveActivationOrg`, and the button's only
     * lock against a second press is `activating` (its other lock, a duplicated composite target,
     * reflects the plan and does nothing about a double press), which is set in the same place — so for one network
     * round trip the button was live and the ref was unset, and two presses ~150 ms apart both
     * passed this check. Both runs then shared one `runId`, which is exactly what `superseded()`
     * cannot separate: on the silent path both reached `activatePlan`, both minted a configuration
     * version, both filled, and both activated. Claiming before the first await closes it.
     *
     * ⚠ THE RELEASE IS NOT UNIFORM, and a new early return must not assume it is. Five returns
     * sit between this claim and the `try` — missing context, duplicate entity codes, a duplicated
     * composite target, an unread Asset Register, and an unresolved organisation — and each releases the ref EXPLICITLY,
     * because the `finally` that covers everything inside the `try` cannot reach them. Anything
     * added in that window needs its own release, or the button is dead until a page reload.
     */
    if (goLiveInFlightRef.current) return;
    goLiveInFlightRef.current = true;

    /*
     * THE REGISTER'S NAMES AND 3D MODELS, before the layout is read for anything below
     * (`onboarding-polish-plan-labels`, epic ledger row 26; Rob, 2026-10-05 — and models since
     * `onboarding-polish-model-source`, ledger row 28; Rob, 2026-10-06).
     *
     * A matched machine's name reaches dGraph from the plan (`convertPlanToTwin` names each entity
     * after its item). The Floor Plan step keeps those names in step with the register while it is
     * open, but Equipment → Observers → Launch never opens it — so a rename made on Your Assets
     * went live under the old name. This applies the register's NAME and 3D MODEL to every matched
     * item, through the same function and projection the Floor Plan step uses, and writes back only
     * those two fields — never the link. The model is the one the register serves: the catalogue
     * product's unless the row records an override, and a register with no model never blanks the
     * plan's (Rob, 2026-10-06). Placed BEFORE `liveLayout` is
     * captured, so the claim, the save, the tracking, the converter and the delta all read the
     * result.
     *
     * Only on a `ready` read — on any other the refusal below stops Go live anyway, and a register
     * nobody successfully read must not rename anything. Only on a fresh run: a resumed launch has
     * already converted and filed, and its plan was synced by the run that did. A refusal further
     * down leaves the renamed plan in the store, which is the Floor Plan step's own rule: the
     * register wins. Only where `registerNameScope` vouches that the plan and the register are the
     * same twin, and the store holds that session's plan.
     *
     * ⚠ This sits between the in-flight claim and the `try`, so it must not throw past here: a
     * throw would leave `goLiveInFlightRef` claimed and the button dead. A failure is logged and Go
     * live carries on with the plan's own names and models, which is what it sent before this existed.
     */
    if (registerRead === 'ready' && !activatedRef.current?.refidToBid && registerNameScope) {
      try {
        const { layout: unsynced, twinBID: storeScope } = useFloorplanStore.getState();
        if (storeScope === registerNameScope) {
          const synced = mirrorRegisterThroughJoin(
            unsynced,
            registerRows,
            registerRowsForMirror(registerRows),
            twinBid,
            { repairStaleLinks: true },
          );
          if (synced && synced !== unsynced) useFloorplanStore.getState().replaceLayout(synced);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[go-live] could not apply the register names and models; going live with the plan’s',
          {
            twinBid,
            twinId,
            error: String(error),
          },
        );
      }
    }

    const { layout: liveLayout } = useFloorplanStore.getState();
    if (!liveLayout || !accessToken || !twinBid || !selectedOrgBid || twinId == null) {
      setActivationError('Missing twin context — cannot activate. Try reloading.');
      goLiveInFlightRef.current = false;
      return;
    }
    if (hasDuplicateCodes) {
      setActivationError(duplicateCodeHint!);
      goLiveInFlightRef.current = false;
      return;
    }

    /*
     * A composite measure the twin would refuse — a duplicated target, a cycle, a double count, a
     * reference the plan lost, and the rest (`composites-page-launch-refusals`, R7). Filing or the
     * statement builder refuses the whole statement on most of them; the two containment refusals
     * are the wizard's own, because nothing downstream can see plan geometry, so for those this and
     * Launch's hold are the ONLY stop. Either way, stop here, before anything is saved or resolved.
     *
     * ⚠ **Not reachable through today's UI, and knowingly so.** Launch disables this button from
     * the SAME verdicts over the same layout (`coverage.refused`), and a store change re-renders it
     * disabled before it can be pressed — unlike the duplicate-code guard above, whose button is not
     * gated. This is for any caller that does not come through that button; its sentence is pinned
     * on `goLiveCompositeRefusal` itself.
     */
    const compositeRefusal = goLiveCompositeRefusal({
      layout: liveLayout,
      twinName: twinDisplayName,
      twinBid,
      refTwinName: bidTwin?.name,
      actionLabel: goLiveButtonLabel,
    });
    if (compositeRefusal && 'checkFailed' in compositeRefusal) {
      /*
       * The check itself threw — a defect in it, since it does no I/O. Carry on: the server and the
       * statement builder still refuse a bad total, and a broken check must not block every launch
       * (spec R6a). Logged so the defect is found.
       */
      // eslint-disable-next-line no-console
      console.error('[go-live] composite refusal check failed', {
        twinBid,
        twinId,
        error: compositeRefusal.error,
      });
    } else if (compositeRefusal) {
      // The refids, targets and reasons, not only the twin: display names are not unique, and
      // these are what someone fixes or deletes to clear it.
      // eslint-disable-next-line no-console
      console.error('[go-live] composite measure would be refused — activation refused', {
        twinBid,
        twinId,
        refused: compositeRefusal.refused,
      });
      setActivationError(compositeRefusal.message);
      // Released explicitly: this return is BEFORE the `try`, so the `finally` cannot do it.
      goLiveInFlightRef.current = false;
      return;
    }

    /*
     * The register must have been READ before anything is minted (`plan-register-bid-match` FR10).
     *
     * `registerRows` is `[]` both when the twin genuinely has no register and when the read failed
     * or has not landed — the conflation this page already keeps `registerRead` around to resolve.
     * Fed to `reservedRegisterIdentities` unchecked it yields an empty reserved set, and
     * `nextFreeCode` then mints straight over the identities the register holds: on UGG-FIN the
     * first free number is `E002`, the customer's "BikeErg 1". The join would afterwards bind the
     * new item to the BikeErg's row, so a wrong BID quietly becomes a wrong register link.
     *
     * The admin activate path refuses in exactly this situation; this is the busier path and was
     * the asymmetric one. Nothing has been touched at this point, so refusing here is free — and
     * `'loading'` is refused as well as `'error'`, because an operator who presses Launch before
     * the read settles gets the same empty set.
     */
    if (registerRead !== 'ready') {
      // eslint-disable-next-line no-console
      console.error('[go-live] register not read — activation refused', {
        twinBid,
        twinId,
        registerRead,
      });
      setActivationError(
        'The Asset Register could not be read, so activation was stopped before anything was ' +
          'changed — activating now could mint an entity BID the register already reserves. Your ' +
          'session may have expired: reload the page and sign in, then try again.',
      );
      // Released explicitly: this return is BEFORE the `try`, so the `finally` cannot do it.
      goLiveInFlightRef.current = false;
      return;
    }

    // 0. Whose namespace? Resolve it from the TWIN, never from the operator's selection.
    //    This runs before every other check because getting it wrong is the one remaining
    //    path that writes cross-tenant and destroys a live model with no backup — and the
    //    delta guard below cannot see it, since in the wrong namespace "nothing is live"
    //    is the correct answer. Nothing has been touched at this point, so refusing here
    //    is free.
    const orgOutcome = await resolveActivationOrg({
      middlewareApi: Config.middlewareApi,
      accessToken,
      twinBid,
      selectedOrgBid,
    });
    if (orgOutcome.status !== 'ok') {
      if (orgOutcome.status === 'failed') {
        console.error('[go-live] could not resolve the twin’s organisation:', orgOutcome.reason);
      }
      setActivationError(activationOrgErrorMessage(orgOutcome)!);
      // Released explicitly: this return is BEFORE the `try`, so the `finally` cannot do it.
      goLiveInFlightRef.current = false;
      return;
    }
    const orgBID = orgOutcome.orgBid;
    // The twin record the org resolution already read. Carried forward so the origin resolution
    // in step 2 costs no second fetch, and so both answers come from one read of one row.
    const activationTwin = orgOutcome.twin;

    setActivationError(null);
    setActivationNotice(null);
    setActivating(true);
    // Claim this run. Any exit from Launch bumps the counter, after which this
    // run must not write activation state or `activatedRef`.
    const runId = activationRunRef.current;
    const superseded = () => activationRunRef.current !== runId;
    /*
     * This launch's history key: minted when the launch starts, carried across a resume (a retry
     * after a failed filing is the SAME launch, holding the same claim).
     */
    const runLaunchUuid = activatedRef.current?.launchUuid ?? crypto.randomUUID();
    /** Whether this run holds the twin's claim, and whether anything has gone live under it. */
    let claimed = false;
    let modelLive = Boolean(activatedRef.current?.refidToBid);
    /*
     * The phase follows the live twin, not the button (`onboarding-persistent-scene-spinner`, R4).
     * A fresh run checks first, and nothing it does before `activatePlan` touches the live twin. A
     * resumed run's model is already live, so it starts changing. Synchronous with the run's claim,
     * so this run cannot yet be superseded here.
     */
    if (modelLive) {
      setActivationPhase('changing');
      console.info('[go-live] changing the live twin', { twinBid, twinId, resume: true });
    } else {
      setActivationPhase('checking');
      console.info('[go-live] pre-flight starting', { twinBid, twinId });
    }
    try {
      /*
       * CLAIM THE TWIN FIRST (spec twin-launch-history-record, R6; ruled by maria@twinlabs.ai
       * 2026-10-05). Before anything is written, the server serialises launches of this twin: a
       * second person — or this page loaded before someone else's launch — is told the twin is
       * locked, and nothing happens. "Could not find out" stops too; it is not permission.
       */
      const claim = await launchRecord.claim({
        accessToken,
        twinId,
        launchUuid: runLaunchUuid,
        plan: liveLayout,
      });
      /*
       * Every outcome by name, so one added later cannot fall through to "claimed". The two
       * opening-hours refusals (spec twin-details-page-launch-gate, R6) wrote nothing and claimed
       * nothing: the step shows the server's own answer, as a lock adopted from `locked` does.
       */
      switch (claim.status) {
        case 'locked':
          launchLock.applyLock(claim.response.lock, claim.response.launch.launchedAt);
          return;
        case 'failed':
          if (!superseded()) {
            setActivationError(
              "Couldn't check whether this twin can be launched now. Nothing was changed.",
            );
          }
          return;
        case 'refused':
          if (!superseded()) launchHours.adopt(claim.launchHours);
          return;
        case 'unchecked':
          if (!superseded()) launchHours.markUnchecked();
          return;
        case 'claimed':
          break;
        default: {
          const unreachable: never = claim;
          return unreachable;
        }
      }
      claimed = true;
      // Superseded while the claim was in flight: stop before writing anything, as after every other
      // await here. (The nav is locked while `activating`, so today nothing can supersede a run at
      // this point; the check keeps that true if the lock ever loosens.) The `finally` below gives
      // the claim back, because nothing went live under it.
      if (superseded()) return;

      /*
       * A waiting draft save is DROPPED, not fired (`observers-page-draft`, FR7): its work is
       * already in the store, and the pre-flight save below persists it. One already in flight is
       * waited for by `persistPlan`'s chaining. Here, inside the `try`, rather than beside the
       * claim: the early returns above release the claim themselves, and a cancel before them
       * would drop a request no `finally` puts back.
       */
      draft.cancel();
      // Steps 1–5 are the destructive half: they replace the twin's whole dGraph
      // tree. Skip them entirely when a previous press already got that far and
      // only observer creation failed (see `activatedRef`).
      let refidToBid = activatedRef.current?.refidToBid;
      /**
       * The renumberings this launch carries (`floor-plan-page-launch-bid-changes`, R11, R15). Set at
       * step 2b when the destructive half runs. On a RESUME it did not run this press — the model was
       * replaced by the press that failed at filing — so it is the value THAT press captured, carried
       * in `activatedRef`. (A reload loses `activatedRef`; the next press then takes the full path,
       * where each `from` is accepted because step 4a retired it before the model was touched.)
       */
      let codeChanges: EntityCodeChange[] = activatedRef.current?.codeChanges ?? [];
      /**
       * The twin's TwinSense configurations, read ONCE by the observer pre-flight below.
       *
       * Hoisted out of the destructive-half block so step 6 can reuse it instead of repeating the
       * GET — the pre-flight and step 6 sit on opposite sides of the `if (!refidToBid)` resume
       * guard. Stays `undefined` on a resume (where the pre-flight is skipped because the
       * destructive half already ran), and step 6 performs the read itself in that case.
       */
      let preflightConfigs: NolaConfig[] | undefined;
      /**
       * Observers in the configuration the twin is measuring RIGHT NOW, per the pre-flight.
       *
       * Empty means either "nothing is running" or "no active configuration" — never "we could not
       * find out", which the pre-flight refuses on rather than continuing past.
       */
      let runningObservers: RunningObserver[] = [];
      /**
       * The configuration the twin is measuring right now, read as a statement — the source of the
       * ordinals and per-camera tuning a filing has to carry across.
       *
       * Three states, and the difference between two of them is what stops a gym being re-numbered:
       *
       *  - a statement — established, and what `buildConfigurationStatement` carries forward;
       *  - `null` — this twin has NO active configuration, so there is nothing to carry;
       *  - `undefined` — never established, which happens only on a RESUME (the pre-flight lives
       *    inside the destructive-half block and is skipped). Step 6 reads it itself in that case,
       *    exactly as it already does for `preflightConfigs`.
       *
       * ⚠ "Could not read it" is never one of the three: the pre-flight refuses on a failed read
       * rather than continuing with `null`, because filing an existing gym as though it were a new
       * one re-allocates every ordinal and looks like a clean run.
       */
      let liveStatement: ConfigurationStatement | null | undefined;
      // Model counts + the UPDATE diff for the success summary. Seeded from the ref so an
      // observer-step retry keeps them; (re)computed from `parsed`/`delta` inside the activation
      // block on a fresh run.
      let entitiesLive = activatedRef.current?.entitiesLive ?? 0;
      let entitiesRemoved = activatedRef.current?.entitiesRemoved ?? 0;
      let assetsLive = activatedRef.current?.assetsLive ?? 0;
      let assetNameByBid: ReadonlyMap<string, string> =
        activatedRef.current?.assetNameByBid ?? new Map();
      let modelDiff = activatedRef.current?.modelDiff ?? null;
      /**
       * Which configuration the operator's consent was given against, `null` for "nothing was
       * running", `undefined` for "we never established it" (a resume from before this field
       * existed). Read by the guard at step 6c.
       */
      let statedAgainstConfigId: number | null | undefined =
        activatedRef.current?.statedAgainstConfigId;
      /** This launch's history key and copies — minted at activation, carried across a resume. */
      let launchUuid = activatedRef.current?.launchUuid;
      let modelCopySuffix = activatedRef.current?.modelCopySuffix ?? null;
      let sensorCopySuffix = activatedRef.current?.sensorCopySuffix ?? null;

      if (!refidToBid) {
        // 1. Persist the latest plan (incl. saved observers) before activating. A save that did
        //    not reach the server is a hard stop: everything below reads the plan as authoritative,
        //    and activating from a layout the server does not have is exactly how the persisted
        //    plan and the live model silently diverge.
        // An EDIT save (it stamps): it may carry floor-plan or camera work no autosave has sent
        // yet, and if this launch then fails that work must still read as unlaunched. A launch
        // that succeeds resets the marker anyway.
        const preflight = await persistPlan();
        if (!preflight.saved) {
          setActivationError(
            describeFailedSave(GO_LIVE_SAVE_LEAD, preflight.reason, 'Please try again.'),
          );
          return;
        }

        // 2. Fetch the current model — versioning source, established geo-origin, and the left-hand
        //    side of the delta below. A failed read must NOT be read as "this twin has no model":
        //    `activatePlan` derives `updating` from it, so a null would skip both backups and then
        //    delete the tree anyway, and the delta would see nothing to lose. Both guarantees turn
        //    on this one distinction.
        const outcome = await dgqFetchTwinOutcome(
          Config.middlewareApi,
          accessToken,
          orgBID,
          twinBid,
        );
        if (outcome.status === 'failed') {
          console.error('[go-live] could not read the twin model:', outcome.error);
          setActivationError(
            "Could not read this twin's current model, so activation was stopped before anything " +
              'was changed. Your session may have expired — reload the page and sign in, then try ' +
              'again.',
          );
          return;
        }
        // One value, two generated types: `TwinEntity` (backend-types) is what the fetch returns
        // and what `convertTwinToPlan` takes; `Entity` (frontend-types) is what `activatePlan` and
        // the geo-origin read expect. The two describe the same dGraph node and do not line up
        // nominally, hence the single bridging cast here rather than one at each use site.
        let prevTwin: TwinEntity | null = outcome.status === 'found' ? outcome.twin : null;
        let prevModel = prevTwin as unknown as Entity | null;

        /*
         * 2b. THE RENUMBERINGS, captured ONCE, here (`floor-plan-page-launch-bid-changes`, R11). Live
         *     spaces re-coded in this draft (*Change Kind*) whose old BID is in the model just read —
         *     or already retired, on a retried launch. Every later step uses these constants and never
         *     reads the record from the store again: the layout is stamped (`activatedLayoutFor`)
         *     BEFORE the filing, and a record read after that would be the wrong one.
         */
        const liveBidSet = liveEntityBidsOf(prevModel as unknown as IdentifiedNode | null);
        codeChanges = effectiveCodeChanges(
          liveLayout as unknown as FloorPlannerLayout,
          liveBidSet,
          twinBid,
        );
        const renames = renamesOf(codeChanges);

        // 2a. WHERE ON EARTH? The chain is prior-model origin → the twin record's own lat/lng →
        //     block, and it never falls back to 0,0 (which is what a wizard-built twin used to
        //     get, stranding it at null island with Edit mode as the only cure).
        //
        //     Resolved HERE rather than beside the org guard because the first branch needs
        //     `prevModel`, which the read above has just produced — one decision point, with both
        //     inputs in hand, instead of a provisional verdict and a final one. Nothing destructive
        //     has run at this point: steps 1–3a save the user's own plan, read the model and
        //     convert it. `activatePlan` is step 5.
        //
        //     `prevModel?.mapOrigin ? … : {0,0}` was the old rule, and a presence check is not
        //     enough — see `isUsableOrigin`. A twin already activated by the wizard HAS a
        //     `mapOrigin`, and it is (0,0), so a presence check would keep it at null island for
        //     ever instead of re-siting it from its address.
        const originOutcome = resolveActivationOrigin({ prevModel, twin: activationTwin });
        if (originOutcome.status === 'blocked') {
          setActivationError(activationOriginErrorMessage());
          return;
        }
        const mapOrigin = originOutcome.origin;

        // What is tracked is DERIVED, never ticked (epic ledger row 133): a zone or machine is
        // tracked when a live observer targets it or a live complex measure that is not excluded
        // totals it. The one rule, from the plan's own observers and measures on every floor, handed
        // to the converter, which stamps the resulting `tracked` flag onto each entity. Before row
        // 133 this counted observers alone, so a zone only a total measures reached dGraph as
        // untracked — greyed out in the twin viewer and `is_tracked = false` in BI.
        const tracked = trackedPlanEntities(liveLayout as unknown as FloorPlannerLayout);

        // 3. Convert the plan layout into a dGraph entity tree.
        /*
         * Convert the REGISTER-JOINED layout, not the raw one.
         *
         * The planner's sync hook persists the join into the store, but only while the Floor Plan
         * step is open — and Equipment → Observers → Launch is a complete path that never mounts it.
         * Converting `liveLayout` therefore made `definitions.asset_uid` present on the 143 matched
         * entities if the operator had visited that step and absent if they had not: the same
         * order-dependence FR8 removed from the coverage counts, one field further along. The join
         * returns the identical reference when nothing changes, so this costs nothing on a plan with
         * no adopted items, and `persistPlan` and the observer derivation deliberately keep reading
         * `liveLayout`.
         */
        const joinedLayout = joinRegisterOntoPlan(
          liveLayout as unknown as FloorPlannerLayout,
          registerRows,
          twinBid,
          {
            /*
             * The repair matters MORE here than anywhere else on this page, because this is the
             * only join whose result leaves the browser. `convertPlanToTwin` carries `assetUid`
             * into the minted entity's `definitions` blob as `asset_uid`, so without this a link
             * naming a deleted register row is persisted into dGraph and outlives the plan.
             *
             * Gated on the read like every other site: activating against a register we could not
             * read is exactly when a re-point would be a guess.
             */
            repairStaleLinks: registerRead === 'ready',
          },
        );

        const parsed = await convertPlanToTwin({
          fml: joinedLayout,
          siteName: prevModel?.name || 'Site',
          twinBID: twinBid,
          mapOrigin,
          tracking: { by: 'plan', tracked },
          /*
           * Identities the register has already reserved, so minting cannot take one
           * (`plan-register-bid-match` FR9). UGG-FIN's register holds six identities with no plan
           * item and the counter's first free number is the customer's "BikeErg 1" — without this
           * the next Library-placed item activates as that BID and then inherits its register row.
           *
           * `registerRows` is this page's own read of the twin's register, so there is nothing to
           * fetch: the set is the same rows the coverage index and the Equipment list use.
           */
          /*
           * ALSO every BID this twin has retired (`floor-plan-page-launch-bid-changes`, R9): the
           * filler fills the LOWEST free gap, so without them a blank space could take a BID launched
           * away before, and new data would land under someone else's history.
           *
           * ⚠ NOT the live model's BIDs (Copilot on #2115). A space with no stored code — one last
           * activated from the admin plans page, which never stamps its codes — keeps its live BID only
           * because the gap fill finds it again; reserving live BIDs would renumber it on every
           * launch. The cost: a BID deleted IN this launch can still be taken by a blank space in the
           * same launch, as before — the filler cannot tell that BID from a blank space's own.
           */
          reservedIdentities: new Set([
            ...Array.from(reservedRegisterIdentities(registerRows, twinBid)),
            ...((liveLayout as unknown as FloorPlannerLayout).retiredEntityCodes ?? []),
          ]),
          /*
           * Every capacity Launch writes, resolved from the same rows (`floor-plan-page-capacity-source`):
           * a machine takes the register's number, a space, floor or the site its hand-set number or
           * its sum. Known by construction — the guard above has refused unless the read is `ready`.
           */
          registerCapacityOf: registerCapacityLookup(registerRows, true),
          /*
           * Each linked item's catalogue reference, written into its `definitions.equipment_id` —
           * the field the BI manufacturer-hierarchy import reads (`twin-launch-history-bi-push`).
           * The same register read as above; the pre-flight guard has already refused Go Live
           * unless that read is `ready`.
           */
          referenceIdByAssetUid: referenceIdsByAssetUid(registerRows),
        });

        // 3a. Stamp the codes this conversion minted back onto the plan, so the next one reuses
        //     them instead of recounting. Without it entity bIDs are positional and a single
        //     deletion renumbers everything after it — which would make the delta below report a
        //     cascade of phantom removals on the ordinary iterative path. Only blanks are filled,
        //     so this is a one-time cost per entity and a no-op on every later Go Live.
        const stamped = stampEntityCodes(
          liveLayout as unknown as FloorPlannerLayout,
          parsed.refidToEntityCode,
          // The twin BID, so the stamp compares stored identities on ONE form (ruling R18) — a
          // stamped `UGG-FIN-E001` and a hand-typed `E001` are the same identity and must not both
          // be written.
          twinBid,
        );
        if (stamped.collisions.length > 0) {
          console.warn(
            '[go-live] entity codes already in use, left blank for now:',
            stamped.collisions,
          );
        }
        // `stamped.layout` is nullable in the type because the input is; `changed` can only be true
        // when a real layout came back, and the guard makes that legible rather than asserted.
        if (stamped.changed && stamped.layout) {
          const stampedSave = await saveStampedCodes(stamped.layout);
          if (!stampedSave.saved) {
            setActivationError(
              describeFailedSave(GO_LIVE_SAVE_LEAD, stampedSave.reason, 'Please try again.'),
            );
            return;
          }
        }

        /*
         * Did we stop and ask a person anything? Set only when Launch's one confirmation (after 3d)
         * was accepted, and read by the `if (askedAHuman)` re-read at 3e.
         *
         * A dialog waits on a human for an unbounded time, so the model and the running observers
         * both have to be re-read before anything destructive runs. Where NOTHING was asked — the
         * ordinary iterative case, no removals and no observers lost — no time passed, and the
         * silent path pays for no extra reads.
         */
        let askedAHuman = false;

        // 3b. The pre-flight: does this plan account for what is already live? `activatePlan`
        //     deletes the twin's whole tree and inserts the plan's, so every live entity the plan
        //     does not contain is destroyed. Only removed AREAS are asked about, and not until every
        //     refusal below has had its chance (`launch-single-confirmation`, R1a): a removed machine
        //     is asked about only if it stops something being measured (3d), and walls and floors
        //     never on their own.
        // A recorded renumbering is not a removal (R12): its observers and measures are carried.
        const delta = withoutRenumbered(
          computeActivationDelta(prevModel, parsed.twin),
          codeChanges,
        );
        const areasRemoved = areasRemovedBy(delta);
        // What activation will actually remove: this delta, or the re-read's once 3e has run.
        let removalDelta = delta;

        /*
         * 3c. THE OBSERVER PRE-FLIGHT — what is this twin measuring right now, and would going
         *     live stop any of it? (`camera-observer-setup-golive-config`, requirements F6/F7.)
         *
         * It sits HERE, beside the entity delta and before anything destructive, so cancelling
         * means nothing happened at all. It used to sit at step 6, after `activatePlan` had already
         * replaced the tree — at which point "we could not find out" was reported to someone whose
         * twin was already half-changed.
         *
         * Both reads BLOCK Go Live, deliberately (spec decision D2). The cost is that a transient
         * TwinSense failure now stops the whole activation rather than only its observer half; the
         * gain is that it stops it before the destructive step rather than after. Both are bounded
         * at 30s, so blocking HERE cannot mean hanging — they were unbounded until this change.
         *
         * ⚠ That is a claim about these two reads and nothing else. `activatePlan` and
         * `dgqFetchTwinOutcome` (through `callExternalApi`, which passes no timeout to its 111 call
         * sites) are still unbounded, and the only exit from `activating` is the `finally` — which
         * cannot run while an await is pending. So a stalled model write still parks the wizard on a
         * disabled "Activating…" with no cancel. That is the backlog's "no request in the
         * activation path has a timeout" row, narrowed rather than closed.
         *
         * The measurement write is no longer in that class: the filing endpoint is bounded by
         * `MIDDLEWARE_WRITE_TIMEOUT_MS`, sized for a write rather than a read.
         */
        const cfgOutcome = await getTwinSenseConfigsOutcome(
          Config.middlewareApi,
          accessToken,
          twinId,
        );
        if (superseded()) return;
        if (cfgOutcome.status === 'failed') {
          // Logged with identifiers, matching the three refusals already above in this function.
          // The operator's sentence is deliberately generic, so this is the only forensic record.
          console.error('[go-live] TwinSense configuration read failed — activation refused', {
            twinBid,
            twinId,
            error: cfgOutcome.error,
          });
          setActivationError(
            "Could not check this twin's TwinSense configuration, so nothing was changed. " +
              'Please try again.',
          );
          return;
        }
        preflightConfigs = cfgOutcome.status === 'found' ? cfgOutcome.configs : [];

        /*
         * More than one active configuration means nothing can honestly say what is running, so
         * this refuses rather than picking one.
         *
         * Not defensive programming — staging twin 532 (`PUL2-COL`) carries two today, ids
         * 10000001 and 27. `setActiveConfig` clears `is_active` across the twin and then sets it,
         * in two statements with no transaction, and a row inserted with `is_active` already true
         * bypasses it entirely. A `.find(c => c.is_active)` here would silently choose one of the
         * two and understate or overstate the loss depending on which.
         */
        const activeConfigs = preflightConfigs.filter(c => c.is_active);
        if (activeConfigs.length > 1) {
          console.error('[go-live] twin has several active configurations — activation refused', {
            twinBid,
            twinId,
            activeConfigIds: activeConfigs.map(c => c.id),
          });
          setActivationError(
            `This twin has ${activeConfigs.length} active TwinSense configurations ` +
              `(${activeConfigs.map(c => c.id).join(', ')}), so we cannot tell what it is ` +
              'currently measuring. Nothing was changed. Ask TwinLabs support to deactivate all ' +
              'but one, then press "Go live" again.',
          );
          return;
        }

        const activeConfig = activeConfigs[0];
        /*
         * Established on this path either way. A twin with no active configuration has nothing to
         * carry forward, which is `null` — deliberately not `undefined`, which means "never
         * established" and is what a resume produces.
         */
        liveStatement = null;
        if (activeConfig) {
          /*
           * ONE read, TWO uses (`go-live-statement`, epic ledger row 16).
           *
           * This used to call the observers route. It now reads the configuration as a STATEMENT,
           * which carries the same rows plus the two things about each camera that only the
           * database knows: the ordinal it is already keyed on, and the confidence somebody tuned
           * it to. The loss computation below needs the first; `buildConfigurationStatement` needs
           * both, and filing without them would re-allocate ordinals from row order and reset the
           * tuning. Reading the same rows twice in two shapes was the alternative.
           *
           * It also retires the wizard's last direct call to the observers route, which is what
           * "the statement is the contract" means in practice.
           */
          const statementOutcome = await getConfigurationStatementOutcome(
            Config.middlewareApi,
            accessToken,
            twinId,
            activeConfig.id,
          );
          if (superseded()) return;
          /*
           * A failed read is NOT an empty configuration, and this is the whole point of the
           * outcome type. Coerced to `[]`, a twin measuring 149 observers reads as measuring none,
           * the loss below computes as zero, no confirmation appears, and Go Live files an empty
           * version over a working one — silently, and with an affirmative false claim behind it.
           *
           * ⚠ It is now load-bearing in a SECOND way, and the second is quieter. Without this
           * statement there are no ordinals to carry, so a filing would re-number every camera on a
           * gym that is already running — and that failure looks like a completely clean Go Live.
           * Refusing here is what makes `liveStatement` safe to read as "this twin has never been
           * filed" further down.
           */
          if (statementOutcome.status === 'failed') {
            console.error('[go-live] running-configuration read failed — activation refused', {
              twinBid,
              twinId,
              configId: activeConfig.id,
              error: statementOutcome.error,
            });
            setActivationError(
              'Could not read what this twin is currently measuring, so nothing was changed. ' +
                'Please try again.',
            );
            return;
          }
          liveStatement = statementOutcome.statement;
          runningObservers = statementObserversAsRunning(liveStatement);
        }

        /*
         * 3d. STATE THE DIFFERENCE, AND ASK (epic ledger row 9, `launch-single-confirmation`).
         *
         * Asks once, and only when an area is removed, something stops being measured or a written
         * total loses a member. Silent otherwise; the whole iterative workflow depends on that.
         */
        /*
         * Entity display name by bID, composed from the two maps that already exist:
         * `entityNamesByRefid` (the plan's CURRENT names, which the register can change) and the
         * converter's `refidToBid`. Deliberately not taken from the running observers' own `name`
         * column — that string was snapshotted when the observer was drawn and can be stale, which
         * is the defect `plan-item-register-sync` (epic ledger row 61) exists to close.
         */
        /*
         * ⛔ **Floors included** (`refiling-preserves-configuration`): the converter mints BIDs for
         * surfaces and items only, so a floor-wide observer resolves only through this map. The same
         * map `buildConfigurationStatement` builds for itself — so the loss the operator is asked to
         * accept and what is then filed cannot disagree about a floor's cameras.
         */
        /*
         * Rebuilt from the store's CURRENT layout on every call — the pre-flight's and the re-check's
         * after the operator's wait — so the loss is always computed against the floors the filing
         * will read, never against a snapshot taken before the dialog opened.
         */
        const refidToBidWithFloors = () =>
          withFloorTargetBids(parsed.refidToBid, useFloorplanStore.getState().layout, twinBid);
        const namesByBid = new Map<string, string>();
        const planNamesByRefid = observerTargetNamesByRefid(useFloorplanStore.getState().layout);
        const namesMap = refidToBidWithFloors();
        planNamesByRefid.forEach((name, refid) => {
          const bid = namesMap[refid];
          // Keyed through `baseEntityBid`, the SAME fold `computeObserverLoss` looks up with.
          // Keying on the raw value works only while both sides happen to agree on case; the
          // moment the fold normalises anything the map would miss and the dialog would show
          // bare identifiers instead of names, in the one place a person is deciding what to
          // stop measuring.
          if (bid) namesByBid.set(baseEntityBid(bid), name);
        });
        // A removed machine is no longer on the plan, so its name comes from the live model, or the
        // dialog would list it by its bare identifier.
        delta.removed.forEach(e => {
          const k = baseEntityBid(e.bID);
          if (!namesByBid.has(k)) namesByBid.set(k, e.name);
        });

        /** The plan's live observers, from the store — the same read step 6 makes. */
        const lossInputs = () => {
          const refidToBid = refidToBidWithFloors();
          return {
            fileable: selectFileableObservers({
              observers: layoutCurrentDesigns(useFloorplanStore.getState().layout)
                .flatMap(d => asArray(d?.observers))
                .filter(isLive),
              refidToBid,
            }),
            refidToBid,
            namesByBid,
            // Both loss checks (and the re-check after a person was asked) compare under the same
            // renumbering, or the re-check reports a renumbered space as lost and aborts (#2115).
            renames,
          };
        };

        /*
         * Only the removed AREAS are passed as deleted: they are listed in their own right. A removed
         * machine that a camera measures then counts as something that stops being measured (R1b),
         * and one with no camera appears nowhere (R2a).
         */
        const observerLoss = computeObserverLoss({
          ...lossInputs(),
          running: runningObservers,
          removedEntityBids: areasRemoved.map(e => e.bID),
        });

        /*
         * A running list at PostgREST's silent 1,000-row cap may be truncated, and a truncated
         * running set UNDERSTATES the loss — the one failure that would quietly defeat this gate.
         * Refusing is one of the three sanctioned answers (`docs/coding-standards.md`, "Behaviour
         * at the maximum"); production's largest configuration holds 241, so it cannot bite today.
         */
        if (observerLoss.runningMayBeTruncated) {
          console.error('[go-live] running-observer read hit the row cap — activation refused', {
            twinBid,
            twinId,
            configId: activeConfig?.id,
            rowsRead: observerLoss.runningRowCount,
            cap: POSTGREST_MAX_ROWS,
          });
          // The RAW row count, not the measuring subset: `runningMayBeTruncated` is a fact about
          // the read, and quoting the filtered count while calling it the read's maximum produces
          // a sentence that is false on its face.
          setActivationError(
            `This twin's observer list came back at ${observerLoss.runningRowCount} rows, which ` +
              'is the maximum a single read returns — so it may be incomplete and we cannot say ' +
              'what would stop being measured. Nothing was changed.',
          );
          return;
        }

        /*
         * ⛔ **The TOTALS, as well as the entities** (`refiling-preserves-configuration`, epic
         * ledger row 121). On staging PUL-CON, 2026-09-24, a no-edit Go Live lost both floor totals
         * and filed the whole-gym total with no members, and this dialog never appeared — because no
         * ENTITY lost its last observer. The statement Go Live would file is built here (it is pure,
         * and step 6 builds the same one) and compared, total by total, with what is running.
         */
        /*
         * ⛔ **A plan that cannot be filed is refused HERE, before anything destructive.** Step 6
         * builds the same statement from the same inputs (`withFloorTargetBids` is idempotent), so
         * a refusal here is the refusal step 6 would give — but by then `activatePlan` has already
         * replaced the twin's model, leaving a new model live beside the old measurement setup.
         * Reading a refusal as "no totals lost" would also hide the loss dialog on exactly the run
         * that is about to fail. So it is a third answer, never an empty loss.
         */
        const compositeLossAgainst = (
          live: ConfigurationStatement | null | undefined,
        ): CompositeLoss | { refused: string[] } => {
          if (!live) return NO_COMPOSITE_LOSS;
          const wouldFile = buildConfigurationStatement({
            layout: useFloorplanStore.getState().layout,
            refidToBid: refidToBidWithFloors(),
            twinBid,
            configName: liveLayout.name || PLAN_NAME,
            live,
          });
          if (!wouldFile.ok) return { refused: wouldFile.reasons };
          // Read the running totals under the renumbering (R13), as the filing will carry them.
          return computeCompositeLoss({
            running: renumberStatement(live, renames),
            filing: wouldFile.statement,
            twinBid,
          });
        };
        const compositeLossOrRefusal = compositeLossAgainst(liveStatement);
        if ('refused' in compositeLossOrRefusal) {
          console.error('[go-live] the plan cannot be filed — refused before activation', {
            twinBid,
            twinId,
            reasons: compositeLossOrRefusal.refused,
          });
          setActivationError(
            `This plan's measurement setup cannot be filed, so nothing was changed. ` +
              compositeLossOrRefusal.refused.join(' '),
          );
          return;
        }
        const compositeLoss = compositeLossOrRefusal;

        /*
         * ONE confirmation, and only when the launch removes an area or stops something being
         * measured (`launch-single-confirmation`, Rob 2026-10-28). It replaced three dialogs in a
         * row, the first recommending an import that threw the work away and the last painted red.
         * Auto-created observers with no camera or region are still not carried forward
         * (`observerLoss.unconfiguredDropped`), and no longer asked about (R2d).
         */
        const asksAboutChanges =
          areasRemoved.length > 0 ||
          observerLoss.entitiesLosing.length > 0 ||
          compositeLoss.removed.length > 0 ||
          compositeLoss.losingMembers.length > 0;
        if (asksAboutChanges) {
          // Nothing destructive has run, so unlock the wizard while we ask. Re-locked on accept.
          setActivating(false);
          const launchConfirmed = await confirmChanges(
            buildLaunchChangesMessage({
              twinName: twinDisplayName,
              areasRemoved,
              entitiesLosing: observerLoss.entitiesLosing,
              totals: compositeLoss,
            }),
          );
          // The dialog waits on a person, so an unbounded amount of time passed here.
          if (!launchConfirmed) {
            /*
             * Back to the button that asked (criterion 10). It was disabled while Go Live ran, so
             * focus had already fallen to the page when the dialog opened, and the dialog can only
             * give back what it found. After a tick, once the dialog has closed and the button is
             * enabled again.
             */
            window.setTimeout(() => goLiveButtonRef.current?.focus(), 0);
            return;
          }
          if (superseded()) return;
          askedAHuman = true;
          setActivating(true);
        }

        /*
         * 3e. Re-read before anything destructive, but only if a dialog made us wait on a person.
         *
         * `activatePlan` versions the model it is HANDED while `dgqDeleteTwinEntity` re-reads and
         * deletes the CURRENT tree, so a stale `prevModel` destroys anything added in the meantime
         * with no versioned backup — and the changes the operator just agreed to would be out of
         * date.
         */
        if (askedAHuman) {
          const fresh = await dgqFetchTwinOutcome(
            Config.middlewareApi,
            accessToken,
            orgBID,
            twinBid,
          );
          if (superseded()) return;
          if (fresh.status === 'failed') {
            setActivationError(
              'Could not re-check this twin before changing it, so nothing was changed. Please try again.',
            );
            return;
          }
          prevTwin = fresh.status === 'found' ? fresh.twin : null;
          prevModel = prevTwin as unknown as Entity | null;

          const freshDelta = withoutRenumbered(
            computeActivationDelta(prevModel, parsed.twin),
            codeChanges,
          );
          if (!sameRemovals(delta, freshDelta)) {
            setActivationError(
              'This twin changed while you were deciding, so nothing was changed. Press ' +
                `"${goLiveButtonLabel}" again to see what would happen now.`,
            );
            return;
          }
          // The same AREAS, but a camera-less machine, wall or floor may have come or gone during
          // the wait (never asked about, R6), and activation removes what is live NOW: count that.
          removalDelta = freshDelta;

          /*
           * And what the twin is measuring — WHICH configuration, not just its rows.
           *
           * Re-reading only `activeConfig.id`'s observers would miss somebody activating a
           * DIFFERENT version from admin during the wait: the rows under the old id come back
           * unchanged, the comparison passes, and Go Live supersedes a configuration the operator
           * was never shown. This also re-checks the multiply-active condition, which can appear
           * in the same window.
           */
          const freshCfg = await getTwinSenseConfigsOutcome(
            Config.middlewareApi,
            accessToken,
            twinId,
          );
          if (superseded()) return;
          if (freshCfg.status === 'failed') {
            setActivationError(
              "Could not re-check this twin's TwinSense configuration before changing it, so " +
                'nothing was changed. Please try again.',
            );
            return;
          }
          const freshConfigs = freshCfg.status === 'found' ? freshCfg.configs : [];
          const freshActive = freshConfigs.filter(c => c.is_active);
          if (freshActive.length > 1 || freshActive[0]?.id !== activeConfig?.id) {
            console.error('[go-live] active configuration changed during the confirmation', {
              twinBid,
              twinId,
              wasActive: activeConfig?.id,
              nowActive: freshActive.map(c => c.id),
            });
            setActivationError(
              'Which configuration this twin is measuring changed while you were deciding, so ' +
                `nothing was changed. Press "${goLiveButtonLabel}" again to see what would happen now.`,
            );
            return;
          }
          preflightConfigs = freshConfigs;

          // And its rows, for the same reason: the operator agreed to a SPECIFIC loss.
          if (activeConfig) {
            const freshStatement = await getConfigurationStatementOutcome(
              Config.middlewareApi,
              accessToken,
              twinId,
              activeConfig.id,
            );
            if (superseded()) return;
            if (freshStatement.status === 'failed') {
              setActivationError(
                'Could not re-check what this twin is measuring before changing it, so nothing ' +
                  'was changed. Please try again.',
              );
              return;
            }
            /*
             * The FRESH statement replaces the pre-flight's, not just for the comparison below but
             * as what gets filed. The operator waited; a camera may have been re-tuned in admin in
             * that window, and carrying the stale reading forward would file the old value back
             * over the new one.
             */
            liveStatement = freshStatement.statement;
            const freshLoss = computeObserverLoss({
              ...lossInputs(),
              running: statementObserversAsRunning(freshStatement.statement),
              removedEntityBids: areasRemovedBy(freshDelta).map(e => e.bID),
            });
            /*
             * Everything the dialog listed, compared as one string: what stops being measured and
             * the totals (the areas were compared above). A total that started losing a member
             * while they read the dialog is as much a change as an entity that did. Never-set-up
             * observers were not shown, so a change in them does not abort (R6).
             */
            /*
             * Every line of the dialog is in here — `from` and `kind` as well as `to` — so a total
             * whose running size changed while the operator read "3 → 1" cannot pass as the same
             * loss because it still ends at 1. A plan that has become unfileable in the meantime is
             * a change too.
             */
            const agreedTo = (
              loss: typeof observerLoss,
              totals: ReturnType<typeof compositeLossAgainst>,
            ) => {
              const line = (t: CompositeLossEntry) => `${t.kind}:${t.target}:${t.from}>${t.to}`;
              return [
                loss.entitiesLosing.map(e => e.bID).join('|'),
                'refused' in totals
                  ? `refused:${totals.refused.join('|')}`
                  : [
                      totals.removed.map(line).join('|'),
                      totals.losingMembers.map(line).join('|'),
                    ].join('#'),
              ].join('#');
            };
            const before = agreedTo(observerLoss, compositeLoss);
            const after = agreedTo(freshLoss, compositeLossAgainst(freshStatement.statement));
            if (before !== after) {
              // Both fingerprints, so the log says WHAT changed — the entity lists alone are often
              // identical when the change was a total.
              console.error('[go-live] running observers changed during the confirmation', {
                twinBid,
                twinId,
                configId: activeConfig.id,
                agreedLosing: observerLoss.entitiesLosing.map(e => e.bID),
                nowLosing: freshLoss.entitiesLosing.map(e => e.bID),
                agreedTo: before,
                nowWouldBe: after,
              });
              setActivationError(
                'What this twin is measuring changed while you were deciding, so nothing was ' +
                  `changed. Press "${goLiveButtonLabel}" again to see what would happen now.`,
              );
              return;
            }
          }
        }

        // 4. One PeopleCountLive sensor per entity.
        const sensorInputs: AddSensorInput[] = (parsed.entities ?? []).map(entity => ({
          bID: entity.bID,
          name: `PeopleCountLive (${entity.bID})`,
          metricType: PEOPLE_COUNT_METRIC,
          maker: 'Nola',
          modelName: 'PeopleCountLive',
          entity: { bID: entity.bID },
        }));

        /*
         * ⛔ **The composite refusal check, AGAIN, on the plan as it stands NOW** (review of
         * `composites-page-launch-refusals`, 2026-10-05). The press-time guard judged `liveLayout`,
         * but step 6 files a fresh read of the store after every await above — the org resolution,
         * the pre-flight save, the configuration reads, a confirmation dialog — and the generated-
         * totals reconciliation can still write composites in that window. A refusal that arrives
         * there would otherwise reach `statement-file` only AFTER `activatePlan` has replaced the
         * twin's model. Inside the `try`, so the `finally` releases the run.
         */
        const lateRefusal = goLiveCompositeRefusal({
          layout: useFloorplanStore.getState().layout,
          twinName: twinDisplayName,
          twinBid,
          refTwinName: bidTwin?.name,
          actionLabel: goLiveButtonLabel,
        });
        if (lateRefusal && 'checkFailed' in lateRefusal) {
          // The plan changed since the press-time check and the check now throws: carry on, as
          // there (spec R6a), but say so — this is the one log that would find it (Copilot review).
          // eslint-disable-next-line no-console
          console.error('[go-live] composite refusal check failed', {
            twinBid,
            twinId,
            error: lateRefusal.error,
            stage: 'before activation',
          });
        } else if (lateRefusal) {
          // eslint-disable-next-line no-console
          console.error('[go-live] composite measure would be refused — activation refused', {
            twinBid,
            twinId,
            refused: lateRefusal.refused,
          });
          setActivationError(lateRefusal.message);
          return;
        }

        // Last chance to bail before anything irreversible. Three awaits sit
        // between claiming this run and here, and `activatePlan` below versions,
        // DELETES and re-inserts the twin's whole dGraph tree — so a run the user
        // has already navigated away from must stop here rather than replace a
        // live model from a plan snapshot they may since have edited.
        if (superseded()) return;

        /*
         * 4a. RETIRE BEFORE ANYTHING IS DESTROYED (`floor-plan-page-launch-bid-changes`, R8; review
         *     2026-10-17). `activatePlan` deletes the old tree and inserts the new one, and it can fail
         *     between the two, or succeed with its answer lost. If the codes it takes out of the live
         *     twin were retired only after a successful activation, such a failure would leave them
         *     neither live nor retired: the retry would drop every renumbering, and the Launch-time
         *     filler could hand an old BID out again. Retiring early is harmless — a retired code is
         *     only ever skipped by mints — so it is stored and saved here, before step 5. A failed save
         *     stops the launch here, with nothing changed.
         */
        const { retired, save: retiredSave } = saveRetiredCodes({
          live: prevModel as unknown as IdentifiedNode | null,
          tree: parsed.twin as unknown as IdentifiedNode,
        });
        if (retiredSave && !(await retiredSave).saved) {
          console.error('[go-live] retired codes could not be saved — activation stopped', {
            twinBid,
            retired,
          });
          setActivationError(
            'Could not save the plan before updating the twin, so nothing was changed. Please try again.',
          );
          return;
        }
        if (superseded()) return;

        // The live twin starts changing here, and not before (R4.2).
        setActivationPhase('changing');
        console.info('[go-live] changing the live twin', { twinBid, twinId, resume: false });

        // 5. Version the old model/sensors and insert the new tree.
        const result = await activatePlan({
          baseUrl: Config.middlewareApi,
          accessToken,
          orgBID,
          tree: parsed.twin,
          prevModel,
          sensorInputs,
        });

        if (!result.success) {
          if (!superseded()) {
            setActivationError(result.error || 'Activation failed. Please try again.');
          }
          return;
        }

        // The model is live: this is now a launch. Its history key, and the copies activation took
        // of the outgoing model and sensors, which the launch record owns (and the prune deletes
        // with it). `activatePlan` has always returned both suffixes; they used to be dropped.
        launchUuid = runLaunchUuid;
        modelLive = true;
        modelCopySuffix = result.modelVersionSuffix ?? null;
        sensorCopySuffix = result.sensorBackup?.versionSuffix ?? null;

        // The model is now in dGraph, so the twin has reached `graphSaved`. Written here rather
        // than at the end of handleGoLive on purpose: every observer branch below returns early,
        // so a write at the end would be skipped on exactly the partial-success paths where the
        // graph *is* saved. `graphSaved` means the graph was saved; observers are a separate
        // concern with their own messaging.
        //
        // Fire-and-forget, and this one is the wizard's LAST milestone — so unlike the `3dScene`
        // write there is no later write to correct a drop. If it fails, the twin stays at
        // `3dScene` until another Go Live.
        //
        // NOT gated on `superseded()`: the graph genuinely was saved, so recording it is correct
        // regardless of where the user has navigated to. Only the in-memory retry state below is
        // run-scoped.
        void persistTwinLifecycleStage(
          Config.middlewareApi,
          accessToken,
          twinBid,
          TwinLifecycleStageValues.GraphSaved,
        );
        // The model is live whether or not that write lands, so the route says so from here on.
        setGraphSavedFor(twinBid ?? null);

        // Stamp the plan as the source of what is now live (`activatedAt`; absent means this plan
        // has never produced a live model). Nothing in the viewer branches on it today; it is the
        // saved plan's record of its last launch. The same layout also carries this launch's
        // retired space and machine codes, the floor-id high-water mark and the cleared removed
        // floors.
        //
        // Awaited, but never fatal: the graph genuinely was saved, and a bookkeeping field must not
        // be able to fail a Go Live that worked.
        const activatedSave = saveActivatedPlan({
          live: prevModel as unknown as IdentifiedNode | null,
          tree: parsed.twin as unknown as IdentifiedNode,
        });
        if (activatedSave && !(await activatedSave).saved) {
          console.warn(
            '[go-live] activated, but the plan could not be stamped as activated — the saved ' +
              'plan does not record this launch, and the codes it retired were not saved with it ' +
              '(they were saved before activation where that save landed).',
          );
        }

        // The destructive half is done for this plan. Record it so a retry after an
        // observer-only failure resumes at step 6 — but only if the user is still on
        // Launch: if they navigated away mid-flight the plan may already have changed,
        // and a stale `refidToBid` would link observers to entities it no longer mints.
        if (superseded()) return;
        refidToBid = parsed.refidToBid;
        entitiesLive = parsed.entities?.length ?? 0;
        entitiesRemoved = removalDelta.removed.length;
        assetsLive = (parsed.entities ?? []).filter(
          entity => entity.type?.name?.toLowerCase() === 'asset',
        ).length;
        assetNameByBid = new Map(
          (parsed.entities ?? []).flatMap(entity =>
            entity.bID && entity.name ? [[entity.bID, entity.name] as const] : [],
          ),
        );
        // UPDATE only: compare the model that was live (read at step 2, before the delete) against
        // the one just activated. CREATE has no prior model, so there is nothing to diff.
        modelDiff = onboardingMode === 'UPDATE' ? computeModelDiff(prevModel, parsed.twin) : null;
        statedAgainstConfigId = activeConfig?.id ?? null;
        activatedRef.current = {
          refidToBid,
          entitiesLive,
          entitiesRemoved,
          assetsLive,
          assetNameByBid,
          modelDiff,
          prevModel,
          codeChanges,
          orgBID,
          statedAgainstConfigId,
          launchUuid,
          modelCopySuffix,
          sensorCopySuffix,
        };
      }

      /*
       * 6. FILE THE WHOLE MEASUREMENT CONFIGURATION, IN ONE CALL.
       *
       * ⚠ This used to be three calls and a guard: mint an empty version, fill it camera by camera
       * (capturing a fresh picture per feed and polling up to 90 seconds for each, serially), then
       * switch the twin over. That sequence had four properties this one does not:
       *
       *  - it LOST what only the database knew. The plan knows a camera watches a treadmill; it does
       *    not know this is treadmill camera 2, nor that somebody tuned one camera to a lower
       *    threshold. Ordinals were derived from row order, so re-launching a running gym handed
       *    `.1`'s region to `.2` — six of the eight multi-camera entities on the staging gym are
       *    stored out of ordinal order — and reset the tuning;
       *  - it could HALF-FILL. A failed batch left an inactive, partly-written version behind;
       *  - it had an UNKNOWN outcome. `setActiveConfig` clears then sets, so a lost response left
       *    nobody able to say what the twin was measuring;
       *  - it was SLOW in proportion to the number of cameras, because of the picture capture.
       *
       * The endpoint writes the version, every observer, the derived total for any entity watched
       * by more than one camera, the totals the operator asked for, and the switch — and undoes all
       * of it if any part fails. The previous version measures throughout.
       *
       * The pictures are gone with the fill (Rob, 2026-09-15). They were a convenience backdrop for
       * the admin single-observer editor, which offers a live capture and an upload instead, and
       * the ones already stored survive because blob storage keys them by entity rather than by
       * configuration.
       */

      // The twin's configurations. Reused from the pre-flight where this run made that read; absent
      // only on a RESUME, where the destructive half already ran and the pre-flight was skipped.
      let twinConfigs: NolaConfig[];
      if (preflightConfigs) {
        twinConfigs = preflightConfigs;
      } else {
        const cfgOutcome = await getTwinSenseConfigsOutcome(
          Config.middlewareApi,
          accessToken,
          twinId,
        );
        if (cfgOutcome.status === 'failed') {
          if (!superseded()) {
            setActivationError(
              'Twin activated, but its measurement setup could not be filed — we could not check ' +
                `this twin's TwinSense configuration. Press "${goLiveButtonLabel}" again to ` +
                'retry — the twin is still measuring the configuration it was.',
            );
          }
          return;
        }
        twinConfigs = cfgOutcome.status === 'found' ? cfgOutcome.configs : [];
      }

      const activeBeforeFiling = twinConfigs.find(c => c.is_active)?.id ?? null;

      /*
       * The plan's live observers, only to decide whether there is anything to file at all.
       *
       * Filing even when the plan has NO observers but the twin already has a configuration, so
       * deleting every observer and re-launching supersedes the running version with an empty one.
       * A twin with neither is a twin with nothing to say, and files nothing.
       */
      const planObserverCount = layoutCurrentDesigns(useFloorplanStore.getState().layout)
        .flatMap(d => asArray(d?.observers))
        .filter(isLive).length;

      // Outcomes collected for the success confirmation below. Null when the twin has no TwinSense
      // configuration at all (no observers drawn and none previously live), in which case the modal
      // omits the observer/device rows entirely.
      let observerResult: { created: number; skipped: number } | null = null;
      /** The plan could not be saved as launched (its `draftBasis` reset) after the launch. */
      let draftMarkerUnsaved = false;
      /** Whether this run has reset the plan's `draftBasis` yet — the filing does, when it files. */
      let draftMarkerReset = false;
      let framePush: SendFrameSplitConfigResult | null = null;
      let configId: number | undefined;
      /** The plan exactly as filed — what the launch record keeps (spec twin-launch-history-record). */
      let planAsFiled: FloorPlannerLayout | null = null;
      /** Set when filing found nothing changed and kept the live configuration. */
      let filingReused: { versionNumber: number } | null = null;

      if (planObserverCount > 0 || twinConfigs.length > 0) {
        /*
         * 6a. What the twin is measuring right now — the ordinals and tuning to carry across.
         *
         * `undefined` means this is a RESUME and the pre-flight never ran, so the read happens here
         * for the same reason `twinConfigs` is read here. A FAILED read is refused rather than
         * treated as "nothing to carry": filing an existing gym as though it were a new one
         * re-numbers every camera, and it looks like a completely clean Go Live.
         */
        if (liveStatement === undefined) {
          /*
           * ⚠ The same refusal the pre-flight makes, because the resume skips the pre-flight.
           *
           * `.find` on a twin carrying two active configurations picks whichever row came back
           * first — staging twin 532 has carried two for months — and then the identities carried
           * forward come from an arbitrary one. The operator would get the stale-identity refusal
           * telling them to reload, which cannot help, instead of the accurate one telling them to
           * deactivate all but one.
           */
          const activeOnResume = twinConfigs.filter(c => c.is_active);
          if (activeOnResume.length > 1) {
            console.error('[go-live] twin has several active configurations on resume — refused', {
              twinBid,
              twinId,
              activeConfigIds: activeOnResume.map(c => c.id),
            });
            setActivationError(
              `This twin has ${activeOnResume.length} active TwinSense configurations ` +
                `(${activeOnResume.map(c => c.id).join(', ')}), so we cannot tell what it is ` +
                'currently measuring. Nothing was changed. Ask TwinLabs support to deactivate all ' +
                `but one, then press "${goLiveButtonLabel}" again.`,
            );
            return;
          }
          const activeNow = activeOnResume[0];
          if (!activeNow) {
            liveStatement = null;
          } else {
            const resumed = await getConfigurationStatementOutcome(
              Config.middlewareApi,
              accessToken,
              twinId,
              activeNow.id,
            );
            if (superseded()) return;
            if (resumed.status === 'failed') {
              console.error('[go-live] could not re-read what the twin measures before filing', {
                twinBid,
                twinId,
                configId: activeNow.id,
                error: resumed.error,
              });
              setActivationError(
                'Twin activated, but we could not read what it is currently measuring, so its ' +
                  'measurement setup has NOT been filed — the twin is still measuring the ' +
                  `configuration it was. Press "${goLiveButtonLabel}" again to retry.`,
              );
              return;
            }
            liveStatement = resumed.statement;
          }
        }

        /*
         * 6b. Build the statement. Pure, and it refuses rather than filing something lesser: a
         * total whose target or member is not on the plan would otherwise be filed without that
         * member, reporting a smaller number with nothing saying so.
         */
        /*
         * ⛔ **The composite refusal check, one last time, on the EXACT plan about to be filed**
         * (Copilot review of PR #1897). The checks before activation cannot see a composite that
         * arrives during `activatePlan` or the activated-plan save, and a retry after a failed
         * filing skips the whole pre-flight. The server refuses most kinds itself, but not the two
         * containment refusals, which only the wizard can see — so without this a contained or
         * undecided double count could be filed. One read of the store, judged and then built from:
         * the plan checked is the plan filed. The model has already been replaced here, so a refusal
         * says so in the same words a builder refusal does.
         */
        const planToFile = useFloorplanStore.getState().layout;
        planAsFiled = planToFile;
        const filingRefusal = goLiveCompositeRefusal({
          layout: planToFile,
          twinName: twinDisplayName,
          twinBid,
          refTwinName: bidTwin?.name,
          actionLabel: goLiveButtonLabel,
        });
        if (filingRefusal && 'checkFailed' in filingRefusal) {
          // eslint-disable-next-line no-console
          console.error('[go-live] composite refusal check failed', {
            twinBid,
            twinId,
            error: filingRefusal.error,
            stage: 'before filing',
          });
        } else if (filingRefusal) {
          const names = refusedTargetNames(filingRefusal.refused);
          // eslint-disable-next-line no-console
          console.error('[go-live] composite measure would be refused — filing refused', {
            twinBid,
            twinId,
            refused: filingRefusal.refused,
          });
          setActivationError(
            'Twin activated, but its measurement setup could not be filed, so the twin is still ' +
              `measuring the configuration it was. ${names.join(', ')} would be refused. Fix ` +
              `${names.length === 1 ? 'it' : 'them'} in the Composite measures step, then press ` +
              `"${goLiveButtonLabel}" again.`,
          );
          return;
        }

        // Only renumberings the active configuration still holds anything under: a completed one (its
        // clearing save lost) would otherwise claim the space's own new key (Copilot on #2115).
        const filedCodeChanges = renumberingsTheConfigurationHolds(
          codeChanges,
          liveStatement ?? null,
        );
        const built = buildConfigurationStatement({
          layout: planToFile,
          refidToBid,
          twinBid,
          configName: liveLayout.name || PLAN_NAME,
          // `?? null` rather than leaning on narrowing: the block above establishes it on every
          // path, but the distinction that matters here is only two-valued — a statement to carry
          // forward, or nothing to carry. "Never established" cannot reach this line.
          live: liveStatement ?? null,
          // The record captured at step 2b, never the store's (R11, R14).
          entityCodeChanges: filedCodeChanges,
        });
        if (!built.ok) {
          console.error('[go-live] the plan cannot be filed', {
            twinBid,
            twinId,
            reasons: built.reasons,
          });
          setActivationError(
            `Twin activated, but its measurement setup could not be filed, so the twin is still ` +
              `measuring the configuration it was. ${built.reasons.join(' ')}`,
          );
          return;
        }

        /*
         * 6c. THE LAST GUARD: is the twin still measuring what the operator was shown?
         *
         * One bounded GET immediately before the write. It used to sit between the fill and the
         * switch, because the fill could run for an hour on a large twin — that gap is gone, but
         * the other gap it covered is not: after a failed filing the whole pre-flight is skipped on
         * the retry (it lives inside `if (!refidToBid)`), so a retry minutes or hours later has no
         * memory of what was agreed. `statedAgainstConfigId` is what gives it one.
         *
         * Refusing rather than re-asking is deliberate: the operator's answer is only valid for the
         * configuration it was given against, and pressing Go live again re-runs the pre-flight
         * from scratch, which is the honest way to get a fresh answer.
         */
        const preFile = await getTwinSenseConfigsOutcome(Config.middlewareApi, accessToken, twinId);
        if (superseded()) return;
        if (preFile.status === 'failed') {
          console.error('[go-live] pre-filing configuration re-read failed', {
            twinBid,
            twinId,
            error: preFile.error,
          });
          setActivationError(
            "Twin activated, but we could not re-check this twin's active configuration before " +
              'filing its measurement setup, so nothing was filed — it is still measuring the ' +
              `configuration it was. Press "${goLiveButtonLabel}" again to retry.`,
          );
          return;
        }
        const preFileActive = (preFile.status === 'found' ? preFile.configs : []).filter(
          c => c.is_active,
        );
        const stillAsStated =
          statedAgainstConfigId === undefined ||
          (preFileActive.length <= 1 && (preFileActive[0]?.id ?? null) === statedAgainstConfigId);
        if (!stillAsStated) {
          console.error('[go-live] active configuration changed before filing — refused', {
            twinBid,
            twinId,
            statedAgainst: statedAgainstConfigId,
            nowActive: preFileActive.map(c => c.id),
          });
          setActivationError(
            'Twin activated, but what this twin is measuring changed while that was happening — ' +
              'so its measurement setup has NOT been filed. Press ' +
              `"${goLiveButtonLabel}" again to see what would happen now.`,
          );
          return;
        }

        // 6d. File it. One call: written whole, activated last, rolled back on any failure before
        //     that point.
        const filing = await fileConfigurationStatementOutcome(
          Config.middlewareApi,
          accessToken,
          twinId,
          built.statement,
        );

        /*
         * ⚠ THE RECORD IS WRITTEN BEFORE THE RUN-GUARD, and that ordering is the point.
         *
         * `superseded()` asks whether this run still owns the wizard — whether the operator has
         * navigated away. It says nothing about whether the SERVER did something, and the server
         * has. Gating the record on it meant a filing that landed while the operator was clicking
         * back to the Cameras stage changed the gym's live measurement configuration and left not
         * one line anywhere: no version id, no counts, nothing to reconstruct from.
         *
         * Same reasoning as the `persistTwinLifecycleStage` write further up, which is deliberately
         * not gated for exactly this reason. From here the run-guard covers the UI — the summary
         * modal and the navigate — and nothing else.
         */
        if (filing.status === 'filed') {
          console.info('[go-live] configuration filed', {
            twinBid,
            twinId,
            newConfigId: filing.result.configId,
            versionNumber: filing.result.versionNumber,
            observersWritten: filing.result.observersWritten,
            compositesWritten: filing.result.compositesWritten,
            previousActiveConfigId: activeBeforeFiling,
            // The invariant the whole feature turns on, made visible: how many cameras kept an
            // identity they already had, against how many were filed as new.
            identitiesCarried: built.identitiesCarried,
            observersFiledAsNew: built.statement.observers.length - built.identitiesCarried,
            supersededRun: superseded(),
          });
        }

        if (filing.status === 'refused') {
          /*
           * The server named every fault. They are shown as it worded them — each names the thing
           * by the identifier that was supplied — rather than summarised into a sentence of our
           * own, which would be a second rendering of the same facts to keep true.
           *
           * Capped so a gym with fifty faults does not produce a dialog nobody can read, with the
           * remainder counted rather than dropped.
           */
          const shown = filing.faults.slice(0, MAX_FILING_FAULTS_SHOWN);
          const remainder = filing.faults.length - shown.length;
          /*
           * The server's own sentence is the fallback, not an empty string. A 400 carrying neither
           * a readable fault list nor a message would otherwise render a banner that stops after
           * "still measuring the configuration it was. " with no reason and — because a refusal is
           * permanent by nature — no retry advice either. A dead end.
           */
          const reason = shown.length > 0 ? shown.map(f => f.detail).join(' ') : filing.message;
          if (!superseded())
            setActivationError(
              'Twin activated, but its measurement setup could not be filed, so nothing was ' +
                'changed and the twin is still measuring the configuration it was. ' +
                reason +
                (remainder > 0 ? ` (and ${remainder} more — see the console.)` : ''),
            );
          return;
        }

        if (filing.status === 'written-not-switched') {
          console.error('[go-live] a version was written but the twin was not switched to it', {
            twinBid,
            twinId,
            previousActiveConfigId: activeBeforeFiling,
            serverMessage: filing.message,
          });
          /*
           * The one failure the server does NOT roll back, because the version it wrote is correct
           * and only the switch needs retrying. Its sentence is already true whether or not the
           * switch landed, and names the version so it can be activated by hand — so it is passed
           * through rather than replaced.
           *
           * The window itself closes when `activation-never-none` makes activation one
           * transactional statement (epic ledger row 50). It is not this feature's to close.
           */
          if (!superseded()) setActivationError(`Twin activated, but: ${filing.message}`);
          return;
        }

        if (filing.status === 'rollback-incomplete') {
          console.error(
            '[go-live] MANUAL CLEANUP REQUIRED — a partial configuration version is left on this twin',
            {
              twinBid,
              twinId,
              previousActiveConfigId: activeBeforeFiling,
              serverMessage: filing.message,
            },
          );
          /*
           * The filing failed AND the endpoint could not remove what it had written, so a partial
           * version is sitting on this twin and somebody has to delete it.
           *
           * ⚠ This is why it is not folded into the generic failure below: that one says "Nothing
           * was written", which is true on every other failure path and FALSE on this one. Saying
           * it here would be an affirmative false statement on the single path that asks for a
           * human to clean up — and the request to clean up would go with it.
           */
          if (!superseded()) setActivationError(`Twin activated, but: ${filing.message}`);
          return;
        }

        if (filing.status === 'failed') {
          /*
           * The server's OWN sentence is what says why — a role refusal, a twin that is not there,
           * a read that failed. The operator's message is fixed, so without this line the reason
           * exists nowhere at all: the client's log carries axios's "Request failed with status
           * code 403", which is not a reason.
           */
          console.error('[go-live] filing failed — nothing was written', {
            twinBid,
            twinId,
            httpStatus: filing.httpStatus,
            serverMessage: filing.serverMessage,
            previousActiveConfigId: activeBeforeFiling,
          });
          /*
           * The server ANSWERED. That is what makes "nothing was written" safe to say: the
           * endpoint ran, so its compensating rollback ran, and the twin is measuring exactly what
           * it was. A retry costs nothing.
           */
          /*
           * 401/403/404 will fail identically for ever, so "press Go live again" sends the operator
           * round a loop that cannot end. Every other refusal on this page already says what to do
           * about an expired session; this one now matches them.
           */
          const permanent = filing.httpStatus === 401 || filing.httpStatus === 403;
          /*
           * ⛔ **A CLEAN FAILURE IS NOT AUTOMATICALLY A RETRYABLE ONE, AND THIS IS THE CASE THAT
           * SEPARATES THEM.**
           *
           * `bid-collision` means two rows of one configuration were handed the same permanent
           * identifier. Nothing was written and the rollback ran — so every other clause of the
           * sentence below is true — but the cause is a defect in the filing CODE, not in what the
           * operator set up, so the next press reproduces it exactly. The server says so in its own
           * words ("Do not simply retry — this needs looking at"), and telling them to press the
           * button again both contradicts it and starts a loop that cannot end.
           *
           * ⚠ Branching on the CODE, never on the server's prose. That sentence also contains
           * "nothing was activated and the twin is still measuring", which is what the client's
           * clean-failure patterns match — so removing the code from that set would not reach this,
           * and matching the prose here would break the first time anyone rewords it.
           *
           * `undefined` (a middleware older than the discriminator) keeps the retry wording, which
           * is the prior behaviour and the safe direction: it is wrong only on this one defect.
           */
          const doNotRetry = filing.code === 'bid-collision';
          if (!superseded())
            setActivationError(
              permanent
                ? 'Twin activated, but its measurement setup could not be filed because this ' +
                    'session is not allowed to file it. Nothing was written — the twin is still ' +
                    'measuring the configuration it was. Your session may have expired: reload the ' +
                    'page and sign in, then try again.'
                : doNotRetry
                  ? 'Twin activated, but its measurement setup could not be filed. Nothing was ' +
                    'written — the twin is still measuring the configuration it was. This is a ' +
                    'fault in the filing itself rather than in what you set up, so pressing ' +
                    `"${goLiveButtonLabel}" again will hit it again. Please report it.`
                  : 'Twin activated, but its measurement setup could not be filed. Nothing was ' +
                    'written — the twin is still measuring the configuration it was. Press ' +
                    `"${goLiveButtonLabel}" again to retry.`,
            );
          return;
        }

        if (filing.status === 'unknown') {
          /*
           * ⚠ NOBODY KNOWS, and the whole point of this branch is not to pretend otherwise.
           *
           * No answer came back — a timeout, an aborted connection, a gateway that ate the
           * response. The filing may have landed in full. Borrowing the sentence above would be an
           * affirmative false statement, and its "try again" is the specific harm: a retry after a
           * filing that actually succeeded writes a second equivalent version over a correct one.
           *
           * So this says what is true (the model is live, the measurement setup is not confirmed),
           * points at where the answer is, and asks rather than instructs.
           */
          console.error('[go-live] filing outcome UNKNOWN — it may or may not have landed', {
            twinBid,
            twinId,
            error: filing.error,
            /*
             * "Ask TwinLabs support to check which configuration is active" only answers the question if
             * support knows what was active BEFORE, and what this attempt was trying to write. Both are in
             * scope here and neither was recorded.
             */
            previousActiveConfigId: activeBeforeFiling,
            observersIntended: built.statement.observers.length,
            compositesIntended: built.statement.composites.length,
          });
          if (!superseded())
            setActivationError(
              'Twin activated, but we got no answer when filing its measurement setup, so we ' +
                'cannot say whether it was filed. It may be measuring the new configuration or the ' +
                'previous one. Ask TwinLabs support to check which configuration is active before pressing ' +
                `"${goLiveButtonLabel}" again — retrying now could file a second copy.`,
            );
          return;
        }

        configId = filing.result.configId;
        observerResult = { created: filing.result.observersWritten, skipped: built.skipped };
        // A server that predates renumbering strips the field and re-mints those entities'
        // identifiers; its result then carries no `renumbered` count. Said here, because after this
        // the record is cleared and nothing else would tie the re-mint to the renumbering
        // (`floor-plan-page-launch-bid-changes`, review 2026-10-17).
        if (filedCodeChanges.length > 0 && filing.result.renumbered === undefined) {
          console.warn(
            '[go-live] the middleware did not acknowledge the renumbering — it predates the field, ' +
              'so these spaces were given NEW observer and composite identifiers:',
            filedCodeChanges.map(c => `${c.from} → ${c.to}`),
          );
        }
        if (filing.result.reused === true) {
          filingReused = { versionNumber: filing.result.versionNumber };
          // Nothing was written, so "0 observers deployed" would misreport a live configuration
          // that is still measuring every one of them. The count is what is measuring.
          observerResult = { created: built.statement.observers.length, skipped: built.skipped };
        }

        // 6e. The twin's active configuration has just changed. Re-run the page-level reads of it
        // so the load notice, the Observers stage and Go Live's own pre-flight all see the version
        // just filed rather than the one it superseded. Fire-and-forget: the hook re-runs the two
        // effects and every consumer renders against whatever they then say. The filed id is
        // recorded first: the plan is re-saved with the re-minted composite ids only for a re-read
        // of THIS configuration (ledger row 164).
        filedConfigIdRef.current = configId ?? null;

        /*
         * The plan is launched, so it holds no unlaunched work any more: reset its `draftBasis` to
         * this launch and save that (`twin-launch-history-draft-choice`, R1.4). Only on a filing
         * that succeeded: a refused or unanswered filing returns above, leaving the draft marked as
         * unlaunched, because its work is not what runs.
         */
        draftMarkerReset = true;
        if (!(await resetDraftMarker(launchUuid))) {
          // Said in the success summary below, not only logged: the operator is about to leave.
          draftMarkerUnsaved = true;
          console.warn(
            '[go-live] filed, but the plan could not be saved as launched — the next open may ask ' +
              'once about a draft that is in fact what was launched.',
          );
        }
        liveConfiguration.retry('config');
        liveConfiguration.retry('observers');

        // 6f. Push the just-filed observers to the twin's edge device(s) as a FrameSplitConfig MQTT
        // message, so the device starts measuring them without a manual admin send. Best-effort:
        // the graph and the configuration are already live and server-side MQTT publish is itself
        // best-effort, so a failure here is captured for the success summary but never blocks or
        // fails the activation.
        try {
          framePush = await sendFrameSplitConfig({
            middlewareApi: Config.middlewareApi,
            accessToken,
            twinBid,
            twinId,
            configId,
          });
          if (framePush.failed > 0) {
            console.warn(
              `[go-live] twin is live, but the frame-split config could not be sent to ` +
                `${framePush.failed} of ${framePush.devices} edge device(s). The observers are ` +
                'filed; re-send the config from admin if the device does not pick them up.',
              { twinBid, twinId, configId },
            );
          }
        } catch (err) {
          // Narrow fields only: an AxiosError carries `.config.headers`, i.e. this caller's live
          // `Authorization: Bearer …`, and the whole object renders expandable in a console that
          // travels in screenshots. The filing client one file over documents the same hazard.
          console.warn('[go-live] failed to send frame-split config to edge device(s)', {
            twinBid,
            twinId,
            configId,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }

      /*
       * 6g. RECORD THE LAUNCH (spec twin-launch-history-record, R2). The configuration and model are
       * live, so this is a launch whatever happens next — including a BI step after it failing, and
       * including the operator having left Launch, which is why it sits BEFORE the `superseded()`
       * return, as the filing log does. Never fails the launch: a record that could not be written
       * is said in the summary, and the lock is read again from the server: the claim still holds
       * the twin (up to five minutes from its start), or — if the record landed unseen — the
       * 60-second lock runs.
       */
      const launchRecordResult = await launchRecord.record({
        accessToken,
        twinId,
        twinBid,
        launchUuid,
        configId: configId ?? null,
        configReused: filingReused !== null,
        plan: planAsFiled ?? useFloorplanStore.getState().layout,
        modelCopySuffix,
        sensorCopySuffix,
      });
      // Read the history again: a completed launch makes a migrating twin a managed one (R4, R5).
      // Whatever the result — an unknown record may have landed.
      if (launchRecordResult) setLaunchHistoryKey(k => k + 1);
      if (launchRecordResult?.status === 'recorded') {
        launchLock.applyLock(launchRecordResult.lock, launchRecordResult.launch.launchedAt);
      } else if (launchRecordResult) {
        launchLock.recheck();
      }
      // Once recorded, this launch is production: a first edit after it is based on it, and the
      // record has cleared the dropped draft it supersedes, so the banner goes (R4.5). Not before:
      // an unrecorded launch never completes, and production is still the previous one.
      if (launchRecordResult?.status === 'recorded') {
        draftChoiceRef.current.productionLaunchUuidRef.current =
          launchRecordResult.launch.launchUuid;
      }
      void draftChoiceRef.current.refreshDropped(planIdRef.current);
      // A launch that filed nothing (no observers, no configuration) is still a launch.
      if (!draftMarkerReset && !(await resetDraftMarker(launchUuid))) {
        draftMarkerUnsaved = true;
        console.warn('[go-live] launched, but the plan could not be saved as launched.');
      }

      if (superseded()) return;

      // 7. BI (`twin-launch-history-bi-push`). The model and configuration are live, so BI's
      // spatial and manufacturer hierarchies now describe what was launched. Outside the filing
      // block above, so a twin with no observers updates BI too; after the activation block, so a
      // launch that failed earlier never reaches it. On failure the person stays on Launch: the
      // banner offers a Retry of this step alone, or continuing without it, and the summary waits.
      // The launch the BI outcome is recorded against: the id the record was written under, and
      // none when the record DEFINITELY failed — there is no row to attach it to (R3.4).
      const recordedLaunchUuid =
        launchRecordResult?.status === 'recorded'
          ? (launchRecordResult.launch.launchUuid ?? launchUuid)
          : launchRecordResult?.status === 'not-recorded' && launchRecordResult.unknown
            ? launchUuid
            : undefined;
      const summaryInputs: LaunchSummaryInputs = {
        twinId,
        launchUuid: recordedLaunchUuid,
        updating: onboardingMode === 'UPDATE',
        entitiesLive,
        entitiesRemoved,
        observers: observerResult
          ? {
              created: observerResult.created,
              skipped: observerResult.skipped,
            }
          : null,
        devices: framePush ? { sent: framePush.sent, total: framePush.devices } : null,
        modelDiff,
        draftMarkerUnsaved,
        assetsLive,
        assetNameByBid,
        reused: filingReused,
        launchRecord: summaryLaunchRecord(launchRecordResult),
      };
      const bi = await biPush.run({
        twinBid,
        orgBID,
        accessToken,
        skipManufacturer: skipsManufacturer(summaryInputs.updating, assetsLive),
      });
      /*
       * 7b. RECORD WHAT BI DID (`twin-launch-history-bi-outcome`, R3). Before the `superseded()`
       * return, as the launch record is: the person leaving Launch does not un-happen the BI
       * update. A failure is recorded at once and NOT awaited — the banner must not wait on the
       * history, and a person who walks away from it leaves the launch marked failed, with its
       * reason. A finished step is awaited, so the summary can say if the history did not take it.
       */
      let biOutcomeSave: BiOutcomeSave | null = null;
      if (bi.status === 'done') {
        biOutcomeSave = await saveBiOutcome(
          summaryInputs,
          toBiOutcome(bi, assetsLive),
          accessToken,
        );
      } else if (bi.failure) {
        saveFailedInOrder(summaryInputs, bi.failure, async () => accessToken);
      }
      if (superseded()) return;
      if (bi.status !== 'done') {
        pendingSummaryRef.current = summaryInputs;
        return;
      }

      // 8. Confirm what was deployed, then into the live twin. The activating content is cleared in
      // the same batch as the summary request (`finishLaunch` reaches `showSummary` without an
      // await), so the open Modal changes content, and does not come back once the summary is
      // acknowledged (R3.3, R3.4).
      setActivationPhase(null);
      await finishLaunch(summaryInputs, toBiSummary(bi, assetsLive), superseded, biOutcomeSave);
    } catch (err) {
      // Narrow fields only — see the frame-split catch above for why the whole error must not be
      // logged. This is the outermost catch on the page's most forensic path.
      console.error('[go-live] activation failed', {
        twinBid,
        twinId,
        message: err instanceof Error ? err.message : String(err),
      });
      if (!superseded()) {
        setActivationError(err instanceof Error ? err.message : 'Activation failed.');
      }
    } finally {
      // A launch that stopped before anything went live gives its claim back, so the twin is not
      // held for nothing. One that went live keeps it: the twin IS mid-launch, and a retry with the
      // same launch id continues under it (the claim expires on its own if nobody does).
      //
      // Awaited before Go live re-enables (it never throws, and is bounded): a retry that reached the
      // claim before the release would find this run's own claim and be held by it.
      if (claimed && !modelLive) {
        await launchRecord.release({ accessToken, twinId, launchUuid: runLaunchUuid });
      }
      goLiveInFlightRef.current = false;
      setActivating(false);
      if (!superseded()) setActivationPhase(null);
      // Releases an activating content held across an accepted changes check that never re-locked.
      endActivating();
      // Whatever Go Live did not persist — an edit left behind by a failure — is the draft's again.
      // The saver reads the step live, so this asks only when a draft step is open by now.
      if (useFloorplanStore.getState().isDirty) draft.request();
    }
  };

  /*
   * Create mode (`twin-details-page-create`, R2.2–R2.3): an early return BESIDE "No twin selected",
   * not the full wizard tree, so none of the twin-scoped reads below can run without a twin. The
   * organisation is the one selected in the twin viewer; without one there is nothing to create in.
   *
   * It does take the wizard's frame (`onboarding-persistent-scene-twin-details-map`, R1.1): the
   * backdrop, the stage, the wash, and the washed map with no record, so it sits at the
   * organisation's location (else the default). This is a different tree from the wizard's, so
   * creating the twin rebuilds the stage's map once, under the route change.
   */
  if (createMode) {
    const orgId = authMetadata?.orgID;
    if (orgId && selectedOrgBid) {
      return (
        <div className={`${className ?? ''} wizard`}>
          <div
            className="backdrop"
            ref={setBackdrop}
            aria-hidden="true"
            data-onboarding-backdrop=""
          />
          {wash.mounted && (
            <div className="wash" aria-hidden="true">
              <Scrim ref={wash.ref} data-onboarding-wash="" />
            </div>
          )}
          <PlanStageBoundary entry={STEP_SCENES[SECTION.twinDetails]} tunnel={stageTunnel} />
          <PlanRendererProviders twinRecord={null} twinBid={undefined} stageTunnel={stageTunnel}>
            <OnboardingWashedPlan
              available={false}
              planScope={undefined}
              onDrawState={onWashedDrawState}
            />
          </PlanRendererProviders>
          <OnboardingCreateMode
            washed={washShown}
            organisationId={orgId}
            orgBid={selectedOrgBid}
            organisationName={authMetadata?.organisation}
            // A twin that does not exist yet is a CREATE, whatever mode a twin opened earlier in
            // this session left in the (unpersisted) session store.
            launchLabel={launchLabelFor('CREATE')}
            getToken={getCreateToken}
            onCreated={twin =>
              navigate(`/onboarding?twinId=${twin.id}&twinBid=${encodeURIComponent(twin.bid)}`, {
                replace: true,
                state: { justCreated: twin.name },
              })
            }
            onGoToTwins={() => navigate('/twin')}
          />
        </div>
      );
    }
    return (
      <div className={className}>
        <OnboardingHeader />
        <div className="no-twin">
          <h2>Choose an organisation first</h2>
          <p>Pick the organisation the twin belongs to, then press Create New Twin.</p>
          <SecondaryButton onClick={() => navigate('/twin')}>Go to your twins</SecondaryButton>
        </div>
      </div>
    );
  }

  // No twin context ⇒ point the user at the entry path instead of a wizard
  // whose uploads would have nothing to hang off.
  if (!twinId) {
    return (
      <div className={className}>
        <OnboardingHeader />
        <div className="no-twin">
          <h2>No twin selected</h2>
          <p>
            Onboarding sets up an existing twin. Pick your organisation, then press the spanner on
            the twin you want to set up.
          </p>
          <SecondaryButton onClick={() => navigate('/twin')}>Go to your twins</SecondaryButton>
        </div>
      </div>
    );
  }

  /*
   * The two twin identifiers in this URL name different twins ⇒ refuse the whole wizard.
   *
   * Deliberately AFTER the "No twin selected" return above, so a missing `?twinId=` still gets the
   * screen written for it. The two are different problems and an operator told the wrong one goes
   * looking in the wrong place.
   *
   * Refusing everything rather than disabling a step: the disagreement poisons the register list,
   * the Equipment count, the planner's sidebar, the identity prefix and what Go Live writes. There
   * is no part of this wizard that is still safe to use.
   */
  if (twinIdentityConflict) {
    return (
      <div className={className}>
        <OnboardingHeader />
        {/* Its OWN container, not `.no-twin`. That block centres its text, caps its width at
            420px and paints every `p` inside it #707070 -- host CSS that would re-skin the
            design-system `Banner` from outside, which is a fork by another route. */}
        <div className="twin-conflict" ref={refusalRef} tabIndex={-1}>
          <Banner surface="on-solid" tone="error" title="This link names two different twins">
            The equipment list would be read from the twin numbered {twinId}, but the plan would be
            saved against <strong>{twinIdentityConflict.name}</strong> ({twinIdentityConflict.bid},
            twin {twinIdentityConflict.id}). Continuing would file one customer&rsquo;s equipment
            against another customer&rsquo;s twin, and the server would refuse to save the plan.
            <p>
              Start onboarding from the spanner on the twins list rather than from a saved or edited
              address, which is how the two get out of step.
            </p>
          </Banner>
          <Button variant="primary" onClick={() => navigate('/twin')}>
            Go to your twins
          </Button>
        </div>
      </div>
    );
  }

  // Floor Plan, Camera Feeds and Observers are all full-bleed (panel + canvas
  // that own their own viewport/scroll) — they drop the content padding.
  const isFullBleedSection =
    // Equipment/Assets lays out its own two panels, gutters and scroll (`equipment-page-stage`, R1).
    section === SECTION.assets ||
    section === SECTION.plan ||
    section === SECTION.cameras ||
    section === SECTION.observers ||
    // The Composite measures list owns its viewport too: its own gutters, its own scroll
    // (`composites-page-list`, spec R3/R4).
    section === SECTION.compositeMeasures ||
    // Position twin lays its panel and map controls over the whole window (R2.2).
    section === SECTION.positionOnMap;
  /** A step whose map runs the whole page under the glass (`GLASS_SECTIONS`). */
  const glassStep =
    GLASS_SECTIONS.has(section) &&
    !landingPending &&
    // Floor Plan's loading line and start choice keep their own layout (R1.3): glass only once the
    // planner shows, as the planner says — not when the store gains a plan, which a fetched plan
    // does while its save is still in flight under the start choice.
    (section !== SECTION.plan || (planShown && !!sessionLayout));
  /**
   * Position twin's map is the page backdrop's, under the content (R2.5): the content passes the
   * pointer through to it, and the step's panel, map controls and handles take it back.
   */
  const pointerThrough = glassStep && section === SECTION.positionOnMap;
  const isLaunchSection = section === LAUNCH_SECTION;
  const landingAnnouncement = landingPending
    ? 'Opening the twin…'
    : landedOn !== null && chosenSection === landedOn
      ? `Opened on ${preLaunchTitle(landedOn) ?? 'the first step'}`
      : '';
  /** Floor Plan is where the wizard opened, and the person has not moved yet (R6). */
  const landedNoticeShown =
    section === SECTION.plan && landedOn === SECTION.plan && draftChoice.notice !== null;
  /** This step's title, or `null` on Launch — read once for the heading, the caption and the note. */
  const stepTitle = preLaunchTitle(section);
  // When this step's changes take effect, on the five editing steps; null on Twin details, Opening
  // hours, Position on map and Launch, which save straight away (`launchNote.ts`). The Observers
  // notices panel reads it too.
  const note = launchNote(stepTitle);
  // What the step is for, on a step that has one (`stepCaption.ts`).
  const caption = stepCaption(stepTitle);
  /*
   * The bar's step title and status line (`onboarding-persistent-scene-step-titles`, R1). The title
   * is named while the landing is pending, so neither heading navigation nor a script focus (a
   * draft swap) lands on an empty heading. The status line is the caption where the step has one,
   * else the launch note — a sibling of the <h3>, never inside it: browser specs find a stage by its
   * heading's name (compositesStage, untrackedByDefault), which must stay the bare title.
   */
  const headerTitle = landingPending ? (
    <span style={visuallyHidden}>Opening the twin</span>
  ) : (
    (stepTitle ?? launchLabel)
  );
  const statusLine = landingPending ? null : (caption ?? note);
  // The action row: the draft tag on a self-saving step. Not on Floor Plan while the "how would
  // you like to start?" chooser is up (there is no plan yet), and not on Twin details, which patches
  // the twin record rather than the plan: its slot draws the twin's own save tag instead
  // (`twin-details-page-address`, R6.4).
  const showActions =
    !isLaunchSection &&
    section !== SECTION.twinDetails &&
    // Equipment/Assets is not a draft step: every asset write self-commits (CSV upload persists
    // server-side; the register's add dialog POSTs on its own "Add asset"; remove
    // deletes immediately), so it stays out of `showActions` and `DRAFT_SAVE_SECTIONS`. Its slot
    // draws the REGISTER's own save tag instead (`equipment-page-stage`, R15)
    // — and that tag's Retry re-reads the register, never repeats a write (ruling 3).
    section !== SECTION.assets &&
    !(section === SECTION.plan && !sessionLayout);

  // Equipment/Assets is optional (a twin can go live with no equipment), so it never blocks: a
  // person moves on through the step list, or the start screen's Skip on an empty register.
  const isAssetsSection = section === SECTION.assets;

  /*
   * The plan search (`floor-plan-page-search`): one search over the plan, the register and both
   * customer IDs, the matches lit on the plan, Enter framing them, and the register's state at the
   * list's foot. It sits at the top of Floor Plan's list panel, not in the bar
   * (`onboarding-persistent-scene-floor-plan-glass`, R2.2), and is hidden in Manage floors, whose
   * panel has its own filter.
   */
  const planSearchElement =
    showActions && planSearchShown ? (
      <PlanEntitySearch
        entries={planSearchIndex}
        twinBid={twinBid}
        onPick={pickOnPlan}
        onLoadTypes={planSearch.loadTypes}
        typeStatus={planSearch.typeStatus}
        label="Search the plan and register"
        placeholder="Search the plan, the register or an asset ID…"
        emptyText={planSearchEmptyText}
        onActiveQueryChange={lightPlanSearch}
        onSubmit={framePlanMatches}
        sourceNote={planSearchNote}
        // `/` focuses it, drawn as a key cap (`floor-plan-page-stage`, R1). The Floor plan's only.
        shortcut="/"
        // The list panel clips its content: the results keep to the search row's width.
        listWidth="100%"
      />
    ) : undefined;

  /*
   * The step's controls in the nav bar's right slots (`onboarding-persistent-scene-step-titles`,
   * R3), in the order the content box's title row held them: the step's own search and notices
   * slots, then the save slot. Each keeps its `key`, so a slot whose
   * meaning changes remounts. Nothing at all on Launch and on Floor Plan's start chooser, and
   * nothing while the landing is pending (R3.3), so nothing there can be reached.
   */
  const barSlots = [
    // Observers' search moved into its list panel (`onboarding-persistent-scene-observers-glass`, R2.2).
    showActions && section === SECTION.compositeMeasures ? (
      <div key="search-slot" className="search-slot" ref={setStepSearchSlot} />
    ) : null,
    // The Observers step's notices chip portals here (`observer-autogen-review-layout`, L1).
    section === SECTION.observers ? (
      <div key="notices-slot" className="notices-slot" ref={setNoticesSlot} />
    ) : null,
    /*
     * KEYED, so the slot remounts when its meaning changes. Unkeyed, React reused one button node
     * from Next into whatever the next step put in the slot: a keyboard user pressing Enter on Next,
     * then again, activated a control they never focused. The slot holds the draft tag, the twin tag
     * (Twin details, Position on map, Opening hours), Equipment's register save tag, or
     * nothing. The draft tag follows `DRAFT_SAVE_SECTIONS` rather than `showActions`, so a step that
     * shows actions without saving itself never claims *Draft saved*; Equipment's tag reports the
     * register's own writes (`equipment-page-stage`, R15).
     */
    showActions && DRAFT_SAVE_SECTIONS.has(section) ? (
      /* A draft step has no Save: the tag says where its self-saving draft stands. */
      <div key="draft" className="save-slot draft-slot">
        <DraftStatusTag
          status={draft.status}
          savedAt={draft.savedAt ?? planSavedAt}
          reason={draft.reason}
          onRetry={draft.retry}
        />
      </div>
    ) : (section === SECTION.twinDetails || section === SECTION.positionOnMap) &&
      !createMode &&
      // A held save stays visible even when the twin's read has not answered. Position
      // on map reads the twin itself, so its tag never waits on the page's read
      // (Copilot review): a save it makes is reported however that read went.
      (section === SECTION.positionOnMap ||
        twinDetails.read === 'ready' ||
        twinDetailsSave.status === 'failed' ||
        twinDetailsSave.status === 'blocked') ? (
      /* Twin details saves each change to the twin straight away
     (`twin-details-page-address`, R6.4), and so does Position on map, through the
     same queue (`retire-edit-mode-position-write-path`, R5): the tag's *twin* form, as
     on Opening hours. Retry resends the held patch, which sets values, so a repeat is
     safe. */
      <div key="twin-details" className="save-slot draft-slot">
        <DraftStatusTag
          subject="twin"
          status={twinDetailsSave.status}
          savedAt={null}
          reason={twinDetailsSave.reason}
          onRetry={twinDetailsSave.retry}
        />
      </div>
    ) : section === SECTION.openingHours &&
      // A held save stays visible even when a re-read has failed meanwhile.
      (hoursEditor.read.read === 'ready' || hoursEditor.save.status === 'failed') ? (
      /* Opening hours saves to the twin straight away: the tag's *twin* form — *Saved*,
     *Not saved*, *Retry saving* — reports its queue (`twin-details-page-hours-week`,
     R1.6). Retry re-sends the held week, which the etag makes safe. */
      <div key="hours" className="save-slot draft-slot">
        <DraftStatusTag
          subject="twin"
          status={hoursEditor.save.status}
          savedAt={null}
          reason={hoursEditor.save.reason}
          onRetry={hoursEditor.save.retry}
        />
      </div>
    ) : isAssetsSection && (equipmentRowsShown || equipmentStart) ? (
      /* Equipment/Assets: the register's own save state (R15; epic question 6). On the start
     screen the tag reads *Nothing to save yet* (`equipment-page-start`, R7; frame 01). */
      <div key="equipment" className="save-slot equipment-slot">
        <DraftStatusTag
          nothingToSave={equipmentStart}
          status={registerSave.status}
          savedAt={registerSave.savedAt}
          reason={registerSave.reason}
          onRetry={registerSave.retry}
          retryLabel="Retry reading the register"
        />
      </div>
    ) : null,
  ].filter(Boolean);
  const barActions = barSlots.length > 0 ? <>{barSlots}</> : null;

  /*
   * Versions (`twin-details-page-versions`): the chip's words, the draft header, and what the draft
   * changes against production. The chip opens it once the landing has settled on a loaded plan.
   */
  const versionsLockReason =
    draftChoice.busy !== null
      ? 'A version change is running.'
      : draftChoice.mustReload !== null
        ? 'Reload to continue before changing versions.'
        : activating
          ? 'The twin is activating. Versions can change once it finishes.'
          : historyReady?.inProgress
            ? "A launch is running. Versions can't change until it finishes."
            : null;
  const launchDisabledReason = !canLaunch
    ? 'Add an observer before launching.'
    : hasDuplicateCodes
      ? (duplicateCodeHint ?? null)
      : null;
  const openVersions = () => {
    setVersionsFocus(null);
    setVersionsOpen(true);
    // Read the history afresh: a launch may have finished, or its BI outcome landed, since the last
    // read, and Versions' locks and BI tags must say what the server says now (Copilot review).
    setLaunchHistoryKey(k => k + 1);
  };

  return (
    <div
      ref={wizardRef}
      className={`${className ?? ''} wizard${glassStep ? ' is-glass' : ''}${glassStep && navIsCollapsed ? ' is-nav-collapsed' : ''}${pointerThrough ? ' is-pointer-through' : ''}${pictureHeld ? ' is-held' : ''}`}
    >
      {/*
       * The full-page backdrop (`spec-onboarding-persistent-scene-full-bleed`, R1): first in source
       * order and with no z-index, so it paints under the header, the list and the content. Empty,
       * it takes no pointer and is out of the accessibility tree; the stage owns its `aria-hidden`
       * once a map is placed in it.
       */}
      <div className="backdrop" ref={setBackdrop} aria-hidden="true" data-onboarding-backdrop="" />
      {/*
       * The wash (`spec-onboarding-persistent-scene-wash`, R3.1): the design system's frosted Scrim
       * over the washed plan, in a layer of its own that contains the Scrim's z-index, so the header,
       * the step list and the content stay above it. A sibling of the backdrop, never the map's
       * parent, so no blur is nested. No `onClick`: it takes no pointer.
       */}
      {wash.mounted && (
        <div className="wash" aria-hidden="true">
          <Scrim ref={wash.ref} data-onboarding-wash="" />
        </div>
      )}
      <OnboardingHeader
        surface="glass"
        route={route}
        versionText={chipVersionText}
        onOpenVersions={versionsAvailable ? openVersions : undefined}
        versionsOpen={versionsOpen}
        chipRef={chipRef}
        onBeforeLeave={beforeLeave}
        twinName={bidTwin?.name ?? undefined}
        stepTitle={headerTitle}
        statusLine={statusLine}
        headingRef={stepHeadingRef}
        actions={landingPending ? undefined : (barActions ?? undefined)}
      />

      {/* One toast host for the wizard; a step places its own region (the Observers stage's Undo). */}
      <ToastProvider>
        <div className="columns">
          <OnboardingNav
            items={navItems}
            current={chosenSection}
            onSelect={setSection}
            locked={activating}
            collapsed={navIsCollapsed}
            onToggleCollapsed={navCollapses ? () => setNavCollapsed(c => !c) : undefined}
          />

          <section className={`content${washShown ? ' is-washed' : ''}`}>
            <div className="content-bar">
              {/* The step's title and controls are the nav bar's (`onboarding-persistent-scene-step-titles`);
                  this row keeps only its unseen status regions. */}
              {/*
               * Where the wizard opened, said (R2, Block A): *Opening the twin…* while the landing is
               * pending, then *Opened on Floor Plan* (or Twin details) once it lands — the system moved
               * the person, after asking them to wait, without moving focus. One region for the life
               * of the page, so the change of text is what is announced; cleared once they move.
               */}
              <span role="status" aria-live="polite" style={visuallyHidden}>
                {landingAnnouncement}
              </span>
              {/* The twin just created (R6.3) — its own region, so it never races the landing's. */}
              <span role="status" aria-live="polite" style={visuallyHidden}>
                {createdAnnouncement}
              </span>
              {/* Said, not only shown: a collapsed list is a layout change a screen reader misses. */}
              <span role="status" style={visuallyHidden}>
                {navAnnouncement}
              </span>
              {/* What a draft swap did — the Twin Details banner changes under a keyboard user. */}
              <span role="status" style={visuallyHidden} key={draftChoice.announcement.n}>
                {draftChoice.announcement.text}
              </span>
            </div>

            <div
              className={`content-body ${isFullBleedSection && !landingPending ? 'planner' : ''}`}
            >
              {/*
               * Until the wizard has decided which step to open on (R2): one polite line, no step. The
               * step list beside it is usable, and a step picked now is opened and kept.
               */}
              {landingPending && <p className="landing-pending">Opening the twin…</p>}
              {/*
               * The six washed-or-scene steps each sit in a `StepFade` (`onboarding-persistent-scene-
               * panel-fades`, R2.2): on a join between two of them the old step fades out over the
               * new one. Floor Plan, Camera Feeds and Observers are not wrapped; they cut (R2.3).
               */}
              <StepFade
                step={SECTION.twinDetails}
                shown={!landingPending && section === SECTION.twinDetails}
                animate={joinFades}
                exitKey={stepJoin.n}
              >
                {() => (
                  <>
                    <DraftChoiceNotices notice={draftChoice.notice} />
                    <OnboardingTwinDetails
                      // Remounted per twin (PR review): its report cache must not suppress the new twin's
                      // first report when it happens to read the same as the last twin's.
                      key={twinBid}
                      twinBid={twinBid}
                      accessToken={accessToken}
                      route={route}
                      save={twinDetailsSave}
                      lookup={addressFlight.lookup}
                      unplacedNotice={unplacedNotice}
                      headingRef={stepHeadingRef}
                      recordPosition={twinDetailsRecordPosition}
                      onNext={() => setSection(SECTION.openingHours)}
                      onOpenPositionOnMap={() => setSection(SECTION.positionOnMap)}
                      onStateChange={reportTwinDetails}
                    />
                  </>
                )}
              </StepFade>
              {/*
               * Opening hours (`twin-details-page-hours-week`) sits outside the swap's key too: its
               * hours are the twin's, saved straight away and never part of a version. Remounted per
               * twin, so one twin's selection and remembered ranges never show on another.
               */}
              <StepFade
                step={SECTION.openingHours}
                shown={!landingPending && section === SECTION.openingHours}
                animate={joinFades}
                exitKey={stepJoin.n}
              >
                {() => (
                  <OnboardingOpeningHours
                    key={twinBid}
                    editor={hoursEditor}
                    launchHours={launchHours.state}
                    launchLabel={launchLabel}
                    twinName={(bidTwin && bidTwin.id === twinId ? bidTwin.name : null) || null}
                    organisationTwins={organisationTwins.state}
                    onRetryOrganisationTwins={organisationTwins.retry}
                    onGoToTwinDetails={() => setSection(SECTION.twinDetails)}
                  />
                )}
              </StepFade>
              {/*
               * A swap replaces the whole plan: whichever PLAN step is on screen remounts from it
               * (twin-details-page-versions, R8.4). Twin details sits outside this key on purpose: it
               * edits the twin's own record, saved separately and never part of a version, and a
               * remount would throw away its unsaved form (review, 2026-10-16).
               *
               * The keyed element also provides the plans' renderer
               * (`onboarding-3d-plan-renderer-switch`, R1): every plan pane below, read-only (the
               * Observers stage, its corner plan, the calibration pane) and editable (Floor Plan's Edit
               * floor and Build floors, Camera Feeds), draws in 3D.
               */}
              {/*
               * The onboarding stage (`spec-onboarding-persistent-scene-stage`): one map for the
               * visit, outside the draft key and every per-twin key, so neither a swap, a twin change
               * nor a save remounts it. Each step's scene is its `STEP_SCENES` entry.
               */}
              <PlanStageBoundary
                entry={landingPending ? NO_SCENE : STEP_SCENES[section]}
                tunnel={stageTunnel}
              />
              <PlanRendererProviders
                key={draftGeneration}
                twinRecord={bidTwinSettled ? bidTwin : undefined}
                twinBid={twinBid}
                stageTunnel={stageTunnel}
              >
                {/*
                 * The washed map behind the five form steps (`spec-onboarding-persistent-scene-wash`,
                 * R2; `onboarding-persistent-scene-twin-details-map`, R1): one mount for all five,
                 * keyed by twin only, so a move between two of them keeps the stage's registration and
                 * the camera never jumps. Mounted whenever the step is washed, with or without a plan;
                 * `available` says whether the plan is drawn on it (R2).
                 */}
                {entryWashed && (
                  <OnboardingWashedPlan
                    key={twinBid}
                    available={washedPlan}
                    planScope={sessionPlanTwinBid === twinBid ? sessionId : undefined}
                    onDrawState={onWashedDrawState}
                    flyRequest={flyRequest}
                    onFlyRequestTaken={setFlyTakenId}
                  />
                )}
                {/*
                 * Position on map draws the plan on the onboarding stage, so it sits inside the
                 * renderer's providers. A draft swap remounts the step and its layer with the plan,
                 * not the stage's map; a gesture in progress then is dropped unsaved, and every save
                 * already made stands.
                 */}
                <StepFade
                  step={SECTION.positionOnMap}
                  shown={!landingPending && section === SECTION.positionOnMap}
                  animate={joinFades}
                  exitKey={stepJoin.n}
                  fill
                >
                  {() => (
                    <OnboardingPositionOnMap
                      key={twinBid}
                      twinBid={twinBid}
                      route={route}
                      getAccessToken={getAccessTokenSilently}
                      save={twinDetailsSave}
                      planRead={planState}
                      planScope={sessionPlanTwinBid === twinBid ? sessionId : undefined}
                    />
                  )}
                </StepFade>
                <StepFade
                  step={SECTION.assets}
                  shown={section === SECTION.assets}
                  animate={joinFades}
                  exitKey={stepJoin.n}
                  fill
                >
                  {exiting => {
                    const shown = exiting ? equipmentHeld : equipmentOnStep;
                    return (
                      /* The identify card's host (`equipment-page-identify`): the *Identify* button in each
                   group's header, the card beside the main panel, and their writes. Only the model a
                   counted register gives offers it; a frame with no model offers nothing. */
                      <EquipmentIdentify
                        // Per twin: a toast's Undo or Try again from one twin's write never outlives a
                        // switch to another, where it would act through the new twin's binding (review).
                        key={twinId ?? 'none'}
                        twinId={twinId ?? null}
                        model={equipmentPanel === 'ready' ? identifyModel : null}
                        visibleIds={equipmentVisibleIds}
                        view={equipmentView}
                        chip={shownChip}
                        baseUrl={Config.middlewareApi}
                        getToken={() => getTokenBounded(getAccessTokenRef.current)}
                        currentTwinId={twinOnPage}
                        reloadRegister={() => reloadRegister({ afterWrite: true })}
                        beginRegisterWrite={beginRegisterWrite}
                        settleRegisterWrite={(token, outcome) =>
                          settleRegisterWrite(
                            token,
                            outcome.ok
                              ? { ok: true }
                              : { ok: false, reason: outcome.reason ?? null },
                          )
                        }
                        markNotTracked={markGroupNotTracked}
                      >
                        {identify => (
                          <EquipmentImportUndoHost
                            key={twinId ?? 'none'}
                            twinId={twinId ?? null}
                            baseUrl={Config.middlewareApi}
                            undoable={undoableImport}
                            removal={assetRemoval}
                            planKnown={coverage.planKnown}
                            pagePlanId={planId}
                            layout={registerJoinedLayout}
                            registerRows={
                              registerRowsAreThisTwins && registerRead === 'ready'
                                ? registerRows
                                : null
                            }
                            blocked={uploadInFlight}
                            beginRegisterWrite={beginRegisterWrite}
                            settleRegisterWrite={settleRegisterWrite}
                            forget={forgetUndoneRows}
                            reload={reloadAfterImportUndo}
                            reportAlert={reportWriteError}
                            onBusyChange={onImportUndoBusy}
                            onDone={onImportUndone}
                          >
                            {undo => (
                              <EquipmentStage
                                panel={equipmentPanel}
                                search={
                                  equipmentFiltered ? (
                                    /* The Equipment step's search FILTERS the register (routes board
                                   §9): it picks nothing, so it is the design system's `Search`, not
                                   `TitleBarSearch` (`equipment-page-stage`, R12). In the panel, as
                                   `ListPanel` draws it (`onboarding-assets-search-in-panel`, R2);
                                   a fading step is inert, so its `/` stands down with it. */
                                    <Search
                                      onGlass
                                      size="md"
                                      aria-label="Search the register"
                                      placeholder="Search names or identifiers"
                                      value={equipmentQuery}
                                      onChange={(value: string) => {
                                        // A search ends *From this import* (R11).
                                        setFromImport(null);
                                        setEquipmentQuery(value);
                                      }}
                                      onClear={() => {
                                        setFromImport(null);
                                        setEquipmentQuery('');
                                      }}
                                      shortcut="/"
                                    />
                                  ) : undefined
                                }
                                dock={
                                  identify.dock ??
                                  // Mounted for the whole Rows view, card open or not: a first identification's
                                  // Undo toast belongs to its identify binding, and outlives the card closing
                                  // (Copilot, PR #2177). It draws nothing while no row is open.
                                  (equipmentView === 'rows' ? (
                                    <EquipmentAssetCardHost
                                      key={twinId ?? 'none'}
                                      cardRef={assetCardRef}
                                      row={openAssetRow}
                                      rows={registerRows}
                                      layout={registerJoinedLayout}
                                      state={
                                        openAssetRow && equipmentPanel === 'ready' && identifyModel
                                          ? (identifyModel.stateOf(openAssetRow.id) ?? null)
                                          : null
                                      }
                                      planKnown={coverage.planKnown}
                                      livePlanStateOf={equipmentLivePlanStateOf}
                                      twinId={twinId ?? null}
                                      baseUrl={Config.middlewareApi}
                                      getToken={() => getTokenBounded(getAccessTokenRef.current)}
                                      currentTwinId={twinOnPage}
                                      reloadRegister={() => reloadRegister({ afterWrite: true })}
                                      beginRegisterWrite={beginRegisterWrite}
                                      settleRegisterWrite={(token, outcome) =>
                                        settleRegisterWrite(
                                          token,
                                          outcome.ok
                                            ? { ok: true }
                                            : { ok: false, reason: outcome.reason ?? null },
                                        )
                                      }
                                      writeDeps={equipmentWrites}
                                      loadModelFiles={loadModelFiles}
                                      onModelCleared={clearPlanModelForRow}
                                      removal={assetRemoval}
                                      folds={assetCardFolds}
                                      onFoldsChange={setAssetCardFolds}
                                      onClose={closeAssetCard}
                                      onRemoved={onAssetRemoved}
                                      returnFocusTo={openAssetRowEl}
                                    />
                                  ) : null)
                                }
                                barActions={identify.barActions}
                                score={identifyModel?.score ?? null}
                                brandMix={equipmentBrandMix}
                                chips={equipmentChips}
                                onToggleChip={toggleEquipmentChip}
                                view={equipmentView}
                                onViewChange={chooseEquipmentView}
                                groupsView={
                                  identifyModel ? (
                                    <EquipmentGroupsView
                                      model={identifyModel}
                                      rows={registerRows}
                                      visibleIds={equipmentVisibleIds}
                                      landingKey={equipmentLandingKey}
                                      onMarkNotTracked={markGroupNotTracked}
                                      notTracked={identify.notTracked}
                                      renderGroupActions={identify.renderGroupActions}
                                      openGroupKey={identify.openGroupKey}
                                      selectedGroupKey={identify.selectedGroupKey}
                                      onSelectGroup={identify.selectGroup}
                                      selection={{
                                        selectedKeys: equipmentSelection.selected,
                                        onSelectionChange: equipmentSelection.set,
                                      }}
                                      writes={equipmentWrites}
                                      layout={registerJoinedLayout}
                                    />
                                  ) : undefined
                                }
                                file={{
                                  /*
                                   * The file is named from the server's record of the latest upload once the read
                                   * has answered (`equipment-page-landed`, R5) — it survives a reload, and its
                                   * answer is the truth, including "no live upload". Until it answers (in flight,
                                   * or failed), this session's own upload names the file if its outcome is known —
                                   * so the file just sent is named straight away, never the one before it. Otherwise
                                   * *{n} rows* is the register's total, never passed off as a file's (stage R6).
                                   */
                                  ...(importSummary.status === 'read'
                                    ? cardImport?.fileName
                                      ? {
                                          fileName: cardImport.fileName,
                                          uploadedAt: new Date(cardImport.createdAt),
                                          rows: cardImport.counts
                                            ? (cardImport.counts.created ?? 0) +
                                              (cardImport.counts.updated ?? 0)
                                            : registerRows.length,
                                        }
                                      : {
                                          fileName: null,
                                          uploadedAt: null,
                                          rows: registerRows.length,
                                        }
                                    : importAttempt?.fileName && importAttempt.outcomeKnown
                                      ? {
                                          fileName: importAttempt.fileName,
                                          uploadedAt: importAttempt.uploadedAt ?? null,
                                          rows:
                                            importAttempt.createdCount + importAttempt.updatedCount,
                                        }
                                      : {
                                          fileName: null,
                                          uploadedAt: null,
                                          rows: registerRows.length,
                                        }),
                                  // *Undo This Import* on the file card (R6), and *Undo Again* (R7).
                                  partlyUndone: undoableImport?.partlyUndone ?? false,
                                  undoAction: undo.offered ? (
                                    <EquipmentImportUndoButton undo={undo} />
                                  ) : null,
                                  open: uploaderOpen,
                                  onToggle: () => setUploaderOpen(open => !open),
                                  controlsId: ASSET_IMPORT_BODY_ID,
                                  toggleRef: uploadToggleRef,
                                }}
                                summary={
                                  latestImport && identifyModel ? (
                                    <EquipmentLandedSummary
                                      record={latestImport}
                                      model={identifyModel}
                                      absent={{
                                        keep: request => keepAbsentRows(latestImport.id, request),
                                        removal: assetRemoval,
                                        // The row is gone from the register; the record's next read
                                        // drops it from the list (R8, R10). What the removal did is
                                        // the same notice the asset card's removal shows.
                                        onRemoved: notice => {
                                          setAssetWriteError(null);
                                          setAssetWriteNotice(notice);
                                          setImportSummaryQuietKey(key => key + 1);
                                        },
                                      }}
                                      actions={
                                        undo.offered ? (
                                          <EquipmentImportUndoButton undo={undo} />
                                        ) : undefined
                                      }
                                    />
                                  ) : undefined
                                }
                                title={
                                  fromImportShownCount !== null
                                    ? FROM_IMPORT_TITLE
                                    : equipmentChipLabel(shownChip)
                                }
                                announcement={
                                  fromImportShownCount !== null
                                    ? fromImportAnnouncement(fromImportShownCount)
                                    : equipmentAnnouncement(
                                        shownChip,
                                        equipmentVisibleIds?.size ?? registerRows.length,
                                      )
                                }
                                countText={
                                  fromImportShownCount !== null
                                    ? fromImportCountText(fromImportShownCount)
                                    : `${equipmentCountText(
                                        equipmentVisibleIds?.size ?? registerRows.length,
                                        identifyModel
                                          ? equipmentChipCount(identifyModel, shownChip)
                                          : registerRows.length,
                                        // The query the rows were filtered by, so the line and the list agree.
                                        deferredEquipmentQuery,
                                      )}${equipmentChipCaption(shownChip)}${
                                        // The Rows view's sort, said on the count line (R5) — Groups is not sorted.
                                        equipmentView === 'rows' && equipmentSort.field
                                          ? ` · sorted by ${EQUIPMENT_SORT_CAPTIONS[equipmentSort.field] ?? equipmentSort.field}`
                                          : ''
                                      }`
                                }
                                /*
                                 * NULL on the start screen — never `undefined`. `EquipmentStage` renders its
                                 * `children` (the uploader) ahead of the views only while `rows` is defined,
                                 * and inside the views' wrapper otherwise: `undefined` would move the uploader
                                 * and remount it, losing a chosen file or an upload in flight (R2).
                                 */
                                rows={
                                  shown.start ? null : (
                                    /*
                                     * The Rows view (`equipment-page-table`): the register on the design
                                     * system's list table, sortable, selectable and edited in the cell, in
                                     * place of `AssetRegisterList`, which admin keeps (NFR2). The notices
                                     * take the props the list took, unchanged (R19).
                                     */
                                    <EquipmentRowsView
                                      model={equipmentPanel === 'ready' ? identifyModel : null}
                                      rows={registerRows}
                                      layout={registerJoinedLayout}
                                      visibleIds={
                                        equipmentFiltered ? equipmentVisibleIds : undefined
                                      }
                                      read={
                                        registerRead === 'error' || registerRead === 'ready'
                                          ? registerRead
                                          : 'loading'
                                      }
                                      readError={REGISTER_READ_ERROR}
                                      onRetryRead={() => reloadRegister()}
                                      sort={equipmentSort}
                                      onSort={field => {
                                        // A sort ends *From this import* (R11).
                                        setFromImport(null);
                                        setEquipmentSort(current => nextSort(current, field));
                                      }}
                                      fromImport={fromImport}
                                      selectedKeys={equipmentSelection.selected}
                                      onSelectionChange={equipmentSelection.set}
                                      planKnown={coverage.planKnown}
                                      onRemoveAll={openRemoveAll}
                                      onOpenRow={openAssetCard}
                                      openId={openAssetRow ? openAssetId : null}
                                      writes={equipmentWrites}
                                      notices={{
                                        /*
                                         * The reconciliation's own messages, OR this page's — never both, and
                                         * the reconciliation wins when it has something to say. They are
                                         * separate state because they have separate lifetimes: the hook clears
                                         * its pair on every press, while `assetWriteError`/`assetWriteNotice`
                                         * belong to this page's own writes. Merging them would let a failure
                                         * outlive the press that superseded it.
                                         */
                                        actionError: registerActionError,
                                        actionErrorKey: assetWriteErrorSeq,
                                        actionNotice: registerActionNotice,
                                        orphanPlacementCount: coverage.orphanPlacementCount,
                                        drift: shown.drift,
                                        onAddMissingToRegister: (
                                          entities: readonly DriftEntity[],
                                        ) => addMissingTracked(entities),
                                        addingProgress,
                                      }}
                                    />
                                  )
                                }
                                selectionBar={
                                  <EquipmentRemoveSelected
                                    selectedIds={equipmentSelection.selected}
                                    rows={registerRows}
                                    layout={registerJoinedLayout}
                                    isPlaced={equipmentIsPlaced}
                                    removeAvailable={equipmentRemoveAvailable}
                                    writes={equipmentWrites}
                                    onRemoved={equipmentSelection.clear}
                                  >
                                    {openRemove => (
                                      <EquipmentSelectionBar
                                        count={equipmentSelection.selected.length}
                                        removeAvailable={equipmentRemoveAvailable}
                                        onRemove={openRemove}
                                        onClear={equipmentSelection.clear}
                                        busy={equipmentBulkBusy}
                                        actions={
                                          /* Frame 07's four actions (`equipment-page-bulk-actions`, R1). */
                                          <EquipmentBulkActions
                                            rows={equipmentSelectionShown}
                                            registerRows={registerRows}
                                            layout={registerJoinedLayout}
                                            planKnown={equipmentRemoveAvailable}
                                            planStateOf={equipmentPlanStateOf}
                                            livePlanStateOf={equipmentLivePlanStateOf}
                                            writes={equipmentWrites}
                                            identify={{
                                              twinId: twinId ?? null,
                                              baseUrl: Config.middlewareApi,
                                              getToken: () =>
                                                getTokenBounded(getAccessTokenRef.current),
                                              currentTwinId: twinOnPage,
                                              reloadRegister: () =>
                                                reloadRegister({ afterWrite: true }),
                                              beginRegisterWrite,
                                              settleRegisterWrite: (token, outcome) =>
                                                settleRegisterWrite(
                                                  token,
                                                  outcome.ok
                                                    ? { ok: true }
                                                    : { ok: false, reason: outcome.reason ?? null },
                                                ),
                                            }}
                                            loadModelFiles={loadModelFiles}
                                            onModelCleared={clearPlanModelForRow}
                                            onBusyChange={setEquipmentBulkBusy}
                                          />
                                        }
                                      />
                                    )}
                                  </EquipmentRemoveSelected>
                                }
                              >
                                <AssetCsvUploader
                                  sessionId={sessionId}
                                  twinId={twinId}
                                  /* An adoption file's BIDs must be this twin's, and its column map can open
                             in the body too (`equipment-page-adoption`, R5). */
                                  twinBid={
                                    (bidTwin && bidTwin.id === twinId ? bidTwin.bid : null) ??
                                    twinBid
                                  }
                                  bodyTarget={equipmentStartTarget}
                                  registerRows={registerNameRows}
                                  /* The empty register's start screen (`equipment-page-start`, R1). Same
                     element, same place in the tree either way — never keyed on this (R2). */
                                  start={
                                    shown.start
                                      ? {
                                          target: equipmentStartTarget,
                                          onSkip: skipOrAdvance,
                                          onAddAsset: () => {
                                            // The newest request wins: an earlier start-screen upload that
                                            // failed or wrote no rows no longer owns where focus goes
                                            // (Copilot, PR #2148).
                                            focusAfterStartUploadRef.current = false;
                                            focusAfterStartAddRef.current = true;
                                            setAddingAsset(true);
                                          },
                                          /* The register list's own write messages — a failed write — said on the screen that
                                     replaced it, so emptying the register never swallows them. */
                                          notice: registerActionNotice,
                                          // An upload that wrote nothing returns here, not to the
                                          // summary: its late-refusal line is said on this screen
                                          // (`equipment-page-reimport`, R12, R14).
                                          lateRefusal: lateRefusalLine(
                                            latestImport?.refused,
                                            'start',
                                          ),
                                          error: registerActionError,
                                          errorKey: assetWriteErrorSeq,
                                        }
                                      : undefined
                                  }
                                  /* WRAPPED, not passed by reference: these are post-write refreshes, and a
                     bare reference would also hand whatever argument the callback is invoked with
                     straight into the options parameter.

                     `onUploaded` now TAKES its argument, which it has always been given and this
                     page has always discarded. The parameter is annotated because `@repo/ui`
                     and the service layer both
                     resolve to source paths here, so an unannotated parameter is an implicit
                     `any` under `noImplicitAny`. */
                                  onUploaded={(
                                    result: BulkUploadTwinAssetsResult,
                                    upload: {
                                      mode: 'customer' | 'adopt';
                                      fileName?: string;
                                      startToken?: number;
                                      items?: BulkUploadTwinAssetsResult['items'];
                                      importId?: number;
                                    },
                                  ) => {
                                    // Before any guard: the upload has finished whichever twin it was for. Its
                                    // own token, never the latest upload's (Copilot, PR #2060).
                                    endUpload();
                                    settleRegisterWrite(upload.startToken ?? null, { ok: true });
                                    /*
                                     * The file card's controls close on a landed upload, as the uploader's own
                                     * disclosure does — leaving them open kept the file just sent chosen, one
                                     * press from sending it again (review, `equipment-page-stage`). Focus that
                                     * was inside them goes back to *Upload Updated Register* — and so does
                                     * focus on the body's column map, which goes with the file it sent
                                     * (`equipment-page-reimport`, R1). The start screen's map hands over
                                     * on its own (`focusAfterStartUploadRef`).
                                     */
                                    if (
                                      document
                                        .getElementById(ASSET_IMPORT_BODY_ID)
                                        ?.contains(document.activeElement) ||
                                      document.activeElement?.closest(
                                        '[data-upload]:not([data-start])',
                                      )
                                    ) {
                                      uploadToggleRef.current?.focus();
                                    }
                                    setUploaderOpen(false);
                                    /*
                                     * The reload is asked for FIRST, and the order is deliberate.
                                     *
                                     * `onUploaded` is called from inside the uploader's `try`, so anything that
                                     * throws here is caught there and reported as `Upload failed. Please try
                                     * again.` — for an upload that actually landed. Projecting the attempt is the
                                     * only thing in this callback that can throw, so issuing the read before it
                                     * means a projection error can no longer ALSO suppress the refresh and leave
                                     * the operator looking at a pre-upload register.
                                     *
                                     * Both setters still land in one render: `reloadRegister` returns immediately
                                     * and React 18 batches the rest of this continuation.
                                     */
                                    /*
                                     * LATEST WINS on the twin, and it has to gate the RELOAD as well as the record.
                                     *
                                     * `reloadRegister` is a `useCallback` keyed on `twinId` and passes `{ twinId }`
                                     * to the store, so an upload still in flight across a twin change holds the OLD
                                     * one — and calling it pulls the previous twin's rows AND installs
                                     * `registerContext`, the live write capability for them, against the twin now on
                                     * screen. That is the hazard the identity-conflict refusal above exists to
                                     * prevent, arriving by the one route that bypasses it: the refusal is checked
                                     * when the read is issued, and this issues it from a closure made earlier.
                                     *
                                     * Guarding only the setter left that half open — the band would be right and the
                                     * register underneath it would be another customer's.
                                     */
                                    const issuedFor = twinId;
                                    if (issuedFor !== twinIdRef.current) return;

                                    /*
                                     * *From this import* (`equipment-page-reimport`, R11): an upload that
                                     * updated or left out rows lands on what it wrote, added first. A first
                                     * upload (nothing updated, nothing left out) lands as before, and so
                                     * does one that wrote no row at all: an empty view would show nothing.
                                     */
                                    const landedImport =
                                      (result.updated ?? 0) > 0 ||
                                      (result.absentFromFile ?? []).length > 0
                                        ? fromImportOf(
                                            upload.importId ?? result.importId ?? null,
                                            upload.items ?? result.items ?? [],
                                          )
                                        : null;
                                    setFromImport(
                                      landedImport && landedImport.order.length > 0
                                        ? landedImport
                                        : null,
                                    );
                                    // Land again once the re-read below answers (`equipment-page-landed`, R7).
                                    pendingLandingRef.current = { after: registerRowsRef.current };
                                    reloadRegister({ afterWrite: true });
                                    // …and the summary reads the record this upload wrote.
                                    setImportSummaryKey(key => key + 1);
                                    /*
                                     * The file card's record, under the same guard.
                                     *
                                     * The reset effect above clears the attempt when `twinId` changes, but an
                                     * upload already in flight at that moment resolves afterwards holding the
                                     * callback from the render it was started on — so it would name the old
                                     * twin's file on the new twin's card, which is precisely the state the
                                     * effect exists to prevent.
                                     *
                                     * The same discipline the register store's sequencer and the uploader's own
                                     * check token already apply, for the same reason: a response is only
                                     * allowed to paint if it is still the answer to the question being asked.
                                     */
                                    setImportAttempt({
                                      ...toImportAttempt(result),
                                      // The file card's name and time (`equipment-page-stage`, R6, ruling 2),
                                      // only when the uploader named the file.
                                      ...(upload.fileName
                                        ? { fileName: upload.fileName, uploadedAt: new Date() }
                                        : {}),
                                    });
                                  }}
                                  /*
                                   * A FAILED upload still re-reads the register, and the file card's record stays.
                                   *
                                   * FR8's rule — the record survives a failed upload — is kept; what is corrected is
                                   * its unstated corollary, that a failure means the register needs no refresh. The
                                   * importer commits in chunks and throws after a partial commit on two paths, both
                                   * of which report how many rows had already been written, so "nothing landed" is
                                   * not true there. Without this the operator sees the previous summary above a
                                   * register that has actually moved, and their next move from a failure message is
                                   * the re-upload that duplicates every row with no customer reference.
                                   *
                                   * The attempt is NOT cleared: it is still the only record of what the last
                                   * successful upload did, and nothing about that upload changed — which is FR8
                                   * read literally, rather than the "nothing landed" gloss its Failure States cell
                                   * puts on it.
                                   */
                                  onUploadFailed={(startToken?: number) => {
                                    endUpload();
                                    settleRegisterWrite(startToken ?? null, {
                                      ok: false,
                                      reason: 'The register upload did not finish.',
                                    });
                                    // The same staleness guard as the success path above, and for the same reason:
                                    // this reload carries the `twinId` of the render the upload started on.
                                    if (twinId !== twinIdRef.current) return;
                                    reloadRegister({ afterWrite: true });
                                    // A failed upload is now the newest record: the summary of the one before it
                                    // must not go on speaking as "what landed" (R1; review, correctness).
                                    setImportSummaryKey(key => key + 1);
                                  }}
                                  /*
                                   * Whether the import controls open collapsed (Rob, 2026-09-22).
                                   *
                                   * The rule is in `registerCompletion.ts` beside `registerKnownEmpty`, not
                                   * inline here — it was inline, as `registerRead === 'ready' ? … : null`, and
                                   * that answered "not known" for a FAILED refresh, over rows the store had
                                   * deliberately kept. See that function's header for what it cost.
                                   */
                                  registerHasRows={registerHasRows(
                                    registerRead,
                                    registerRows.length,
                                  )}
                                  onUploadStart={() => {
                                    setUploadsInFlight(n => n + 1);
                                    // An upload owns where focus goes next, not an earlier hand-add.
                                    focusAfterStartAddRef.current = false;
                                    if (document.activeElement?.closest('[data-start]')) {
                                      focusAfterStartUploadRef.current = true;
                                    }
                                    // Handed back to this upload's own end, so two open uploads (the uploader
                                    // remounted while one ran) each settle their own write.
                                    return beginRegisterWrite();
                                  }}
                                  /* While the file card is drawn, its *Upload Updated Register* opens the
                     uploader (R6, ruling 2). Otherwise — an empty register, a failed read, rows the
                     model could not count — the uploader keeps its own disclosure, so it is always
                     reachable. */
                                  importOpen={equipmentFiltered ? uploaderOpen : undefined}
                                  // Not while an undo is in flight: an upsert racing its deletes re-inserts rows.
                                  disabled={importUndoing}
                                  resetToken={uploaderResetToken}
                                  resetMode={uploaderResetMode}
                                />
                                {/*
                                 * The register itself (asset-register-management §4.2) is the Rows view, mounted in
                                 * the stage above (`equipment-page-table`). It reads the REGISTER-JOINED layout, so
                                 * an adopted item counts as placed whether or not the operator has opened Floor
                                 * Plan, and reaches the same answer as the coverage index held above.
                                 */}
                                {shown.preImportWarning && (
                                  /* The customer's register has not landed yet — see the note where this is
                     derived. Rendered here, above the list, in twin-viewer's own chrome: the
                     shared `AssetRegisterList` gains no prop for it, which is what keeps this
                     feature out of `packages/ui`. */
                                  <Banner
                                    surface="on-solid"
                                    tone="warning"
                                    title="Import the customer’s register first"
                                  >
                                    {shown.preImportWarning}
                                  </Banner>
                                )}
                                {removeAllOpen && removeAllPreview && (
                                  <RemoveAllAssetsDialog
                                    counts={removeAllFrozenCounts ?? removeAllPreview.counts}
                                    planDirty={planDirty}
                                    busy={removeAllBusy}
                                    error={removeAllError}
                                    onRemoveUnplaced={() => void runRemoveUnplaced()}
                                    onRemoveAll={() => void runRemoveAll()}
                                    onCancel={closeRemoveAll}
                                    returnFocusTo={removeAllFocusFallback}
                                  />
                                )}
                                {addingAsset && (
                                  <AssetEditDialog
                                    onCancel={() => setAddingAsset(false)}
                                    onSubmit={handleSaveAsset}
                                  />
                                )}
                              </EquipmentStage>
                            )}
                          </EquipmentImportUndoHost>
                        )}
                      </EquipmentIdentify>
                    );
                  }}
                </StepFade>
                {section === SECTION.plan &&
                  // Also held until the twin read settles (`floor-plan-page-start`): the step's start
                  // and its resume tab both turn on whether the twin is live, and a step picked
                  // before the landing would otherwise open on a guess it never revisits.
                  (planLoading || !bidTwinSettled ? (
                    <div className="plan-loading">Checking for a saved plan…</div>
                  ) : (
                    <OnboardingPlanner
                      sessionId={sessionId}
                      // Set only while the plan and the register are confirmed to be one twin.
                      layoutScope={registerNameScope}
                      twinId={twinId}
                      // The twin's dGraph BID and its saved-plan id, so the chooser can offer
                      // *Start from this twin* — and withhold it where a plan already exists.
                      twinBid={twinBid}
                      existingPlanId={planId}
                      // The chooser reports a failed adoption on its own surface, so no dialog here.
                      onPersist={() => persistPlan()}
                      underlayImages={underlayImages}
                      // A Delete that takes camera regions is an observer edit: stamp the draft.
                      onObserversRemoved={handleObserverEdit}
                      onPlanChecks={handlePlanChecks}
                      // The route rule's "live or migrating", so the chooser offers *Start from this
                      // twin* only where there is a live twin; null holds the card's slot while unknown.
                      existingTwin={route === null ? null : isExistingTwinRoute(route)}
                      // Settled with no twin: the route is unknowable, which is not "new".
                      twinUnreadable={bidTwinSettled && !bidTwin && !!twinBid}
                      liveSince={productionCompletedAt}
                      // The spaces this draft added or moved keep their lasting mark
                      // (`onboarding-status-colours-plan-spaces`, R6).
                      draftChanges={draftSource.byEntity}
                      // The camera panel's picture and stream name (`unified-camera-panel`, R1).
                      liveConfiguration={liveConfiguration}
                      // The plan search and the landing visit's draft notices, in the list panel's
                      // header (`onboarding-persistent-scene-floor-plan-glass`, R2.2).
                      planSearch={planSearchElement}
                      onPlannerShownChange={setPlanShown}
                      notices={
                        landedNoticeShown ? (
                          <DraftChoiceNotices notice={draftChoice.notice} />
                        ) : undefined
                      }
                      // The identify card on the Checks tab (`floor-plan-page-identify`) writes to the
                      // register exactly as the Equipment step's card does — the same five bindings.
                      identify={{
                        getToken: () => getTokenBounded(getAccessTokenRef.current),
                        currentTwinId: twinOnPage,
                        reloadRegister: () => reloadRegister({ afterWrite: true }),
                        beginRegisterWrite,
                        settleRegisterWrite: (token, outcome) =>
                          settleRegisterWrite(
                            token,
                            outcome.ok
                              ? { ok: true }
                              : { ok: false, reason: outcome.reason ?? null },
                          ),
                      }}
                    />
                  ))}
                {section === SECTION.cameras &&
                  (planLoading ? (
                    <div className="plan-loading">Checking for a saved plan…</div>
                  ) : (
                    <OnboardingCameras
                      sessionId={sessionId}
                      // The SAME read the Observers stage uses — the twin's streams are fetched once,
                      // on this page, and handed to both stages. A second read here could disagree
                      // with the first, which is the "could not read" / "has none" contradiction the
                      // stream work exists to delete.
                      liveConfiguration={liveConfiguration}
                      // Every camera change is already in the plan store; the draft saver persists it.
                      onCameraEdit={handleCameraEdit}
                      // A Delete that takes camera regions is an observer edit: stamp the draft.
                      onObserversRemoved={handleObserverEdit}
                    />
                  ))}
                {section === SECTION.observers &&
                  (planLoading ? (
                    <div className="plan-loading">Checking for a saved plan…</div>
                  ) : (
                    <OnboardingObservers
                      sessionId={sessionId}
                      // Every edit is already in the plan store; the draft saver persists it.
                      onObserverEdit={handleObserverEdit}
                      onObserverEditPending={handleObserverEditPending}
                      registerPendingWriteFlush={registerPendingWriteFlush}
                      onNavCollapseWanted={setNavCollapsed}
                      onGoToCameraFeeds={() => setSection(SECTION.cameras)}
                      liveConfiguration={liveConfiguration}
                      // The one bar's slot for the draft-notices chip, and the step's launch note
                      // it carries (observer-autogen-review-layout, L2).
                      noticesSlot={noticesSlot}
                      launchNote={note ?? undefined}
                      // The saved plan the suggestion engine reads (observer-autogen-review-ui-preview).
                      planId={planId}
                      // What the draft added against production (onboarding-status-colours-observers).
                      draftChanges={draftSource.byEntity}
                      draftComparison={draftSource.comparison}
                    />
                  ))}
                <StepFade
                  step={SECTION.launch}
                  shown={isLaunchSection}
                  animate={joinFades}
                  exitKey={stepJoin.n}
                >
                  {() => (
                    <OnboardingLaunch
                      // The draft's origin, as the chip names it, on a launched twin only (R6).
                      draftOrigin={
                        route === 'manage'
                          ? draftOrigin({ route, draftBasis, history: historyReady })
                          : undefined
                      }
                      summary={{
                        twinDetails: {
                          located: twinDetails.located,
                          placeholder: twinDetails.placeholder,
                          address: twinDetails.address,
                          timezone: twinDetails.timezone,
                          read: twinDetails.read,
                        },
                        /*
                         * `read` travels with the counts, because this is the screen an operator reads
                         * immediately before pressing Go Live. On a failed read the counts are all zero,
                         * and "0 assets uploaded — 0 matched, 0 unknown" on a twin whose 400-row import
                         * succeeded an hour ago reads as a lost import: the operator either re-uploads
                         * the CSV (duplicating every row with no customer identifier) or launches on a
                         * summary describing a twin that does not exist.
                         */
                        assets: {
                          read: registerRead,
                          total: registerRows.length,
                          matched: matchedAssets,
                          // The rows, not the counts: each count on Launch is its list's length (R6).
                          ...launchAssets,
                        },
                        plan: {
                          drawn: planDrawn,
                          placed: placedItems.length,
                          placement: launchPlacement,
                        },
                        cameras: {
                          linked: linkedCameras,
                          unlinked: cameras.length - linkedCameras,
                          complete: cameraFeedsComplete,
                        },
                        observers: {
                          /*
                           * Both numbers come from the coverage rule, so this block and the coverage
                           * block below cannot disagree about how many entities the plan has — and both
                           * therefore need the SAME read state. Without it this block would say "0 with
                           * an observer, 160 without one yet" on a failed read, directly above a coverage
                           * block honestly saying it could not be read.
                           */
                          /*
                           * ⚠ From the CONFIGURATION read alone, never from the coverage block's state.
                           * The two differ on exactly one value and it is the one that matters: `no-plan`
                           * is TERMINAL for coverage, and mapping it onto `loading` here left this block
                           * saying "still reading" for ever — the same spin-with-no-answer trap this
                           * change fixed one block down, reintroduced one block up. A twin with no plan
                           * has no entities, so 0 and 0 is the true answer, and the Floor Plan block
                           * above already says why there are none.
                           */
                          // And the plan half, since the block now names entities — see
                          // `observersReadState` for why `absent` must still settle.
                          read: observersReadState(coverageReadState, coveragePlanState, planState),
                          created: measurementCoverage.observed,
                          withoutObserver: measurementCoverage.unobservedEntities.map(entityItem),
                        },
                        coverage: coverageBlockProps,
                        biGaps: {
                          inputs: biInputs,
                          placed: biPlacedItems.length,
                          groups: biGapGroups(
                            biPlacedItems,
                            referenceIdByAssetUid,
                            launchBiCheck.check.status === 'ready'
                              ? launchBiCheck.check
                              : launchBiCheck.check.status === 'failed'
                                ? { status: 'failed' }
                                : undefined,
                          ),
                          check: launchBiCheck.check,
                        },
                      }}
                      onGoLive={() => void handleGoLive()}
                      goLiveRef={goLiveButtonRef}
                      activating={activating}
                      error={activationError}
                      notice={activationNotice}
                      // Only reachable from the info notice's Go to twin, and nothing sets
                      // `activationNotice` today, so this is a plain open, not the landing loop.
                      onContinue={() => void landInLive()}
                      mode={onboardingMode}
                      bi={biPush.state}
                      onRetryBi={() => void handleRetryBi()}
                      onContinueWithoutBi={() => void handleContinueWithoutBi()}
                      biOutcomeSaving={biOutcomeSaving}
                      biSavingFailure={biSavingFailure}
                      lock={{
                        state: launchLock.state,
                        endsAt: launchLock.endsAt,
                        launchedAt: launchLock.launchedAt,
                        inProgress: launchLock.inProgress,
                      }}
                      onRecheckLock={launchLock.recheck}
                      hours={launchHours.state}
                      hoursRechecking={launchHours.rechecking}
                      onRecheckHours={launchHours.canRead ? launchHours.refresh : undefined}
                      biRechecking={launchBiCheck.rechecking}
                      onRecheckBi={launchBiCheck.recheck}
                      removedFloors={sessionLayout?.removedFloors}
                      renumbered={renumberedRows}
                      onPutBackFloor={record =>
                        putBackFloor({
                          record,
                          layout: useFloorplanStore.getState().layout,
                          pushUndoStep: l => useEditorStore.getState().pushSnapshot(l),
                          restore: r => useFloorplanStore.getState().restoreFloor(r),
                          persist: () => persistPlan(),
                        })
                      }
                      onRetryPlanSave={() => persistPlan()}
                    />
                  )}
                </StepFade>
                <StepFade
                  step={SECTION.compositeMeasures}
                  shown={section === SECTION.compositeMeasures}
                  animate={joinFades}
                  exitKey={stepJoin.n}
                  fill
                >
                  {exiting => (
                    <OnboardingCompositeMeasures
                      composites={asArray(sessionLayout?.composites)}
                      layout={sessionLayout}
                      twinBid={twinBid}
                      /*
                       * The TWIN's name, not the plan document's.
                       *
                       * `sessionLayout.name` is the plan's title — "Onboarding plan" on a plan nobody
                       * renamed — so the "whole twin" option read that. Found by looking at the driven-
                       * browser capture: the first row of the real gym's list said "Onboarding plan".
                       * `bidTwin` is the twin record the page already reads; absent, the option falls
                       * back to the twin BID, which is at least the twin.
                       */
                      twinName={bidTwin?.name}
                      twinId={twinId}
                      // A fading step stops writing into the title bar (R3.3): only the incoming
                      // step's controls show there.
                      searchSlot={exiting ? null : stepSearchSlot}
                      onChange={handleCompositeChange}
                      /*
                       * Read at WRITE time, not from the render that opened the dialog. Deleting a
                       * measure waits on a human, and the live-configuration match writes this same
                       * section when the statement read lands — so the array captured before the
                       * dialog can be a render stale by the time the operator answers.
                       */
                      latestComposites={latestPlanComposites}
                      liveConfiguration={liveConfiguration}
                      generation={{
                        paused: generationPaused,
                        active: generationSettled,
                        added: generatedAdded,
                        onAddedAnnounced: clearGeneratedAdded,
                      }}
                    />
                  )}
                </StepFade>
              </PlanRendererProviders>
            </div>
          </section>
        </div>
      </ToastProvider>
      <DraftReloadModal
        message={draftChoice.mustReload}
        onReload={() => window.location.reload()}
      />
      {/* Holds the wizard still while a restore runs: an edit made then would be discarded by it. */}
      <DraftRestoringModal open={draftChoice.busy === 'restore'} />
      {/* Mounted only while open, as the Load confirmation is: nothing renders for a closed one. */}
      {versionsOpen && versionsAvailable && (
        <VersionsModal
          open
          twinName={twinDisplayName}
          onClose={() => setVersionsOpen(false)}
          returnFocusTo={chipRef}
          focusOn={versionsFocus}
          read={launchHistory}
          title={draftTitle({ route: route ?? 'live-unread', draftBasis, history: historyReady })}
          subline={draftSubline({
            savedAt: historyDraft?.modifiedAt ?? null,
            savedBy: historyDraft?.modifiedBy ?? null,
            currentEmail: user?.email,
            notLaunched: draftNotLaunched,
            // Only a FRESH read of production lets the header say "same as production": after a
            // failed or pending refresh the last answer is still shown, but is not re-confirmed.
            productionKnown: launchHistory.state === 'ready' && productionLaunch !== null,
          })}
          canDiscard={canDiscard}
          // The layout as the wizard holds it now, so the stale-draft warning follows a mid-session
          // swap or launch (`remove-draft-choice-modal`, R4).
          draftLayout={layout ?? null}
          production={productionLaunch}
          showGoToLaunch={draftNotLaunched}
          launchLabel={launchLabel}
          launchDisabledReason={launchDisabledReason}
          changes={draftChanges}
          dropped={draftChoice.dropped}
          droppedReadFailed={draftChoice.droppedReadFailed}
          currentEmail={user?.email}
          lockReason={versionsLockReason}
          busy={draftChoice.busy}
          restoreError={draftChoice.restoreError}
          onDiscard={() => {
            setVersionsOpen(false);
            setVersionsFocus('discard');
            clearLoadError();
            setDiscardOpen(true);
          }}
          onGoToLaunch={() => {
            setVersionsOpen(false);
            setSection(SECTION.launch);
          }}
          onLoad={launch => {
            setVersionsOpen(false);
            setVersionsFocus({ load: launch.launchUuid });
            openLoad(launch);
          }}
          onRestore={() => {
            setVersionsOpen(false);
            void handleRestoreDropped();
          }}
          onRetryHistory={() => setLaunchHistoryKey(k => k + 1)}
          onRetryChanges={retryDraftChanges}
          onReload={() => window.location.reload()}
        />
      )}
      {discardOpen && (
        <DiscardDraftModal
          open
          productionVersion={productionLaunch ? launchVersion(productionLaunch) : null}
          changes={draftChanges}
          dropped={draftChoice.dropped}
          droppedReadFailed={draftChoice.droppedReadFailed}
          droppedChecking={draftChoice.droppedChecking}
          busy={draftChoice.busy === 'load'}
          error={draftChoice.loadError}
          onDiscard={() => void handleDiscard()}
          onCancel={() => {
            clearLoadError();
            setDiscardOpen(false);
            setVersionsOpen(true);
          }}
          onReload={() => window.location.reload()}
        />
      )}
      <LoadLaunchModal
        launch={loadTarget}
        productionVersion={productionLaunch ? launchVersion(productionLaunch) : null}
        currentEmail={user?.email}
        difference={loadDifference}
        draftChanges={loadDraftNote.current.changes}
        draftEditedAt={loadDraftNote.current.editedAt}
        onRetryDifference={retryLoadDifference}
        dropped={draftChoice.dropped}
        droppedReadFailed={draftChoice.droppedReadFailed}
        droppedChecking={draftChoice.droppedChecking}
        busy={draftChoice.busy === 'load'}
        error={draftChoice.loadError}
        onLoad={handleLoadLaunch}
        onCancel={closeLoad}
        onReload={() => window.location.reload()}
      />
    </div>
  );
};

export default styled(OnboardingPage)`
  display: flex;
  flex-direction: column;
  height: 100vh;

  .columns {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  /*
   * The wizard is layers over a full-page backdrop (\`spec-onboarding-persistent-scene-full-bleed\`,
   * R1, R4, R5): the backdrop fills the page, the header floats 8 px in at the top, and the step list
   * and the step's content stay one flex row below the header, so the content keeps today's
   * rectangle and follows the list's width transition as it always has. The backdrop takes no
   * z-index and paints first by source order; the header alone is lifted (below). The create route
   * takes this frame too, over the washed map (\`onboarding-persistent-scene-twin-details-map\`,
   * R1.1); "Choose an organisation first" and the refusal screens keep the column above.
   */
  &.wizard {
    position: relative;
    display: block;

    > .backdrop {
      position: absolute;
      inset: 0;

      &:empty {
        pointer-events: none;
      }
    }

    /*
     * Above the list and the content, which come later in source order: the glass bar is a
     * stacking context (its backdrop-filter), so without this the drop-downs a step puts in the
     * bar's slots (the Observers step's search results and draft notices) open under the content. The bar ends where the content starts, so it never covers it; the content's
     * full-screen takeover still covers the bar. While the account menu is open the layer takes
     * the popover step, as the bar itself does (NavBar.tsx, \`is-menu-open\`), so the menu also
     * clears a step's own overlay layer (Camera Feeds' calibration screen) and is not cut off by it.
     */
    > header {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: ${zIndex.sticky};

      &:has(.is-menu-open) {
        z-index: ${zIndex.popover};
      }
    }

    /*
     * The wash's layer (\`spec-onboarding-persistent-scene-wash\`, R3.1): its own stacking context, so
     * the Scrim's \`zIndex.overlay\` is contained in it and the list and content (later in source
     * order) and the header paint above. One wash at a time (R3.5): while a design-system Modal is
     * open its own Scrim stands in, so this one is drawn at nothing, with no transition. Browsers
     * without \`:has\` show both, accepted as cosmetic.
     */
    > .wash {
      position: absolute;
      inset: 0;
      z-index: 0;
      pointer-events: none;

      body:has(.tl-modal-root) & {
        opacity: 0;
        transition: none;
      }
    }

    .columns {
      position: absolute;
      /* The header's 8 px margin plus the nav bar's 52 px height (NavBar.tsx). */
      top: ${BAR_BOTTOM};
      left: 0;
      right: 0;
      bottom: 0;
    }

    /*
     * A glass step (\`GLASS_SECTIONS\`, \`onboarding-persistent-scene-camera-feeds-glass\` R1): the
     * step's content is the whole window, and the step list floats over it as glass, 8 px from the
     * left and bottom and 12 px below the bar (canvas frame 02). The content is its own stacking
     * context at z 0, so nothing the planner lifts inside it can paint over the list; the header
     * keeps its own z-index above both. The first panel's left edge follows the list's width
     * (\`--onboarding-glass-start\`), so an expanded list pushes the panels rather than covering them.
     */
    &.is-glass {
      ${GLASS_START_VAR}: ${glassStartFor(NAV_WIDTH_EXPANDED)};

      &.is-nav-collapsed {
        ${GLASS_START_VAR}: ${glassStartFor(NAV_WIDTH_COLLAPSED)};
      }

      .columns {
        top: 0;
      }

      .columns > .content {
        position: absolute;
        inset: 0;
        z-index: 0;
        background: transparent;
      }

      /*
       * Position twin (R2.5): the map sits under the content, on the backdrop, so a press anywhere
       * the step draws nothing reaches it. The columns box covers the page below the bar, so it
       * and the content both let presses through; the step list takes them back, and the step's
       * own controls (its panel, its map controls, the handles) each set \`pointer-events: auto\`.
       */
      &.is-pointer-through .columns,
      &.is-pointer-through .columns > .content {
        pointer-events: none;
      }

      &.is-pointer-through .columns > nav {
        pointer-events: auto;
      }

      .columns > nav {
        position: absolute;
        top: ${PANEL_TOP};
        left: ${GLASS_EDGE};
        bottom: ${GLASS_EDGE};
        z-index: 1;
        /* The nav bar's own glass (NavBar.tsx), so the list reads as the bar's companion. */
        background: ${glass.bgSoft};
        backdrop-filter: ${glass.blur};
        -webkit-backdrop-filter: ${glass.blur};
        box-shadow: ${shadow.glassSm};
        border-radius: ${radius.panel};
      }
    }

    /*
     * A plan map's picture held on the backdrop (\`spec-onboarding-persistent-scene-handover-glass-align\`,
     * R2.1): the content box and the plan panes are see-through, so the picture shows under the
     * arriving step while its map is still invisible. The editor's pane is \`[data-plan-pane]\`; the
     * Observers stage plan's is the read-only frame around its map layer. A browser without \`:has\`
     * keeps that pane's white, as before this change: \`:has\` is in a rule of its own, because a
     * browser that cannot read one selector in a list drops the whole rule.
     */
    &.is-held {
      .columns > .content,
      [data-plan-pane] {
        background: transparent;
      }
      :has(> [data-plan-map-layer]) {
        background: transparent;
      }
    }
  }

  .content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    background: ${semantic.surface.base};

    /* The wash shows around the panels (\`spec-onboarding-persistent-scene-wash\`, R3.4). */
    &.is-washed {
      background: transparent;
    }
  }

  /* The content box's row now holds only its unseen status regions: no height, no rule
     (onboarding-persistent-scene-step-titles, R4). */
  .content-bar {
    display: flex;
  }

  .save-slot {
    width: 160px;
  }

  /* Equipment/Assets: the register's save tag (equipment-page-stage, R15). */
  .save-slot.equipment-slot {
    width: auto;
    min-width: 160px;
    display: flex;
    justify-content: flex-end;
  }

  /* The draft tag grows to carry a failure's reason and Retry; never narrower than Save was. */
  .save-slot.draft-slot {
    width: auto;
    min-width: 160px;
  }

  .content-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem 2rem;
  }

  /* The planner-based sections own their viewport: no padding, no scroll. */
  .content-body.planner {
    padding: 0;
    overflow: hidden;
  }

  /* *Opening the twin…* while the landing is decided (R2). */
  .landing-pending {
    margin: 0;
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    color: ${semantic.text.body};
  }

  .plan-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 0.9rem;
    color: #707070;
  }

  /* The identity-conflict refusal. Layout only: max-width and centring for the column, and
     nothing that reaches inside the design-system Banner it wraps. */
  .twin-conflict {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    max-width: 640px;
    margin: 4rem auto 0;

    /* Focused programmatically when it replaces the wizard; no focus ring on a container. */
    &:focus {
      outline: none;
    }

    button {
      align-self: flex-start;
    }
  }

  .no-twin {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    max-width: 420px;
    margin: 4rem auto 0;
    text-align: center;

    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      color: #707070;
    }
  }
`;
